import React, { useEffect, useMemo, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit.js';
import { trackSignupSlackConnected, trackSignupProfileCompleted } from '../lib/analytics';
import { SignupShowcase } from '../components/SignupShowcase';
import { DarkGreenTexture } from '../components/DarkGreenTexture';
import { WaitlistModal } from '../components/WaitlistModal';
import { BillingModal } from '../components/BillingModal';
import { Check, ChevronDown, Shield, Lock, FileText, ArrowRight, ArrowLeft, Info, X, Search, Hash, MessageSquare, Headphones, Users, Grid3x3, Bell, Star, CreditCard, Calendar, ShieldCheck, Loader2, ArrowLeftRight, Plug, UserPlus, Sparkles, CheckCircle, ChevronRight, LayoutGrid, Plus, Send, PanelLeft, Folder, SquarePen, Zap, Brain, MoreHorizontal, Gauge, Settings2, PhoneCall, Paperclip, Home, GraduationCap, Activity, FolderOpen, Database, Globe, Monitor, Network, UserCircle, Copy } from 'lucide-react';
import { TRACKS, GROUP_NAMES } from '../data/workspaceTracks';
import { WorkspaceViews, WorkspaceViewId } from '../components/gigachat/WorkspaceViews';
const ALL_TOOLS = [{
  id: 'slack',
  name: 'Slack',
  icon: 'slack',
  category: 'communication'
}, {
  id: 'gmail',
  name: 'Gmail',
  icon: 'gmail',
  category: 'communication'
}, {
  id: 'outlook',
  name: 'Outlook',
  icon: 'microsoftoutlook',
  category: 'communication'
}, {
  id: 'teams',
  name: 'Microsoft Teams',
  icon: 'microsoftteams',
  category: 'communication'
}, {
  id: 'discord',
  name: 'Discord',
  icon: 'discord',
  category: 'communication'
}, {
  id: 'googledrive',
  name: 'Google Drive',
  icon: 'googledrive',
  category: 'docs'
}, {
  id: 'googlecalendar',
  name: 'Google Calendar',
  icon: 'googlecalendar',
  category: 'docs'
}, {
  id: 'notion',
  name: 'Notion',
  icon: 'notion',
  category: 'docs'
}, {
  id: 'confluence',
  name: 'Confluence',
  icon: 'confluence',
  category: 'docs'
}, {
  id: 'coda',
  name: 'Coda',
  icon: 'coda',
  category: 'docs'
}, {
  id: 'dropbox',
  name: 'Dropbox',
  icon: 'dropbox',
  category: 'docs'
}, {
  id: 'github',
  name: 'GitHub',
  icon: 'github',
  category: 'engineering'
}, {
  id: 'gitlab',
  name: 'GitLab',
  icon: 'gitlab',
  category: 'engineering'
}, {
  id: 'bitbucket',
  name: 'Bitbucket',
  icon: 'bitbucket',
  category: 'engineering'
}, {
  id: 'sentry',
  name: 'Sentry',
  icon: 'sentry',
  category: 'engineering'
}, {
  id: 'datadog',
  name: 'Datadog',
  icon: 'datadog',
  category: 'engineering'
}, {
  id: 'linear',
  name: 'Linear',
  icon: 'linear',
  category: 'project'
}, {
  id: 'jira',
  name: 'Jira',
  icon: 'jira',
  category: 'project'
}, {
  id: 'asana',
  name: 'Asana',
  icon: 'asana',
  category: 'project'
}, {
  id: 'trello',
  name: 'Trello',
  icon: 'trello',
  category: 'project'
}, {
  id: 'monday',
  name: 'Monday.com',
  icon: 'mondaydotcom',
  category: 'project'
}, {
  id: 'salesforce',
  name: 'Salesforce',
  icon: 'salesforce',
  category: 'crm'
}, {
  id: 'hubspot',
  name: 'HubSpot',
  icon: 'hubspot',
  category: 'crm'
}, {
  id: 'pipedrive',
  name: 'Pipedrive',
  icon: 'pipedrive',
  category: 'crm'
}, {
  id: 'zendesk',
  name: 'Zendesk',
  icon: 'zendesk',
  category: 'support'
}, {
  id: 'intercom',
  name: 'Intercom',
  icon: 'intercom',
  category: 'support'
}, {
  id: 'front',
  name: 'Front',
  icon: 'front',
  category: 'support'
}, {
  id: 'stripe',
  name: 'Stripe',
  icon: 'stripe',
  category: 'finance'
}, {
  id: 'quickbooks',
  name: 'QuickBooks',
  icon: 'quickbooks',
  category: 'finance'
}, {
  id: 'figma',
  name: 'Figma',
  icon: 'figma',
  category: 'design'
}, {
  id: 'miro',
  name: 'Miro',
  icon: 'miro',
  category: 'design'
}, {
  id: 'googleanalytics',
  name: 'Google Analytics',
  icon: 'googleanalytics',
  category: 'analytics'
}, {
  id: 'mixpanel',
  name: 'Mixpanel',
  icon: 'mixpanel',
  category: 'analytics'
}, {
  id: 'amplitude',
  name: 'Amplitude',
  icon: 'amplitude',
  category: 'analytics'
}, {
  id: 'mailchimp',
  name: 'Mailchimp',
  icon: 'mailchimp',
  category: 'marketing'
}, {
  id: 'googleads',
  name: 'Google Ads',
  icon: 'googleads',
  category: 'marketing'
}, {
  id: 'hootsuite',
  name: 'Hootsuite',
  icon: 'hootsuite',
  category: 'marketing'
}];
function getScopesForTool(tool: any) {
  if (tool.id === 'slack') {
    return [{
      id: 'read_channels',
      label: 'Read channel messages',
      description: "View messages in public channels you're a member of.",
      required: true
    }, {
      id: 'search_history',
      label: 'Search history',
      description: "Search across messages so Giga can cite what's been said.",
      required: true
    }, {
      id: 'read_files',
      label: 'Read files & attachments',
      description: 'Read files shared in channels and DMs.',
      required: false
    }, {
      id: 'read_dms',
      label: 'Read your direct messages',
      description: 'Index your DMs so Giga can find specific conversations.',
      required: false
    }, {
      id: 'post',
      label: 'Post on your behalf',
      description: 'Reply in channels and send DMs when you ask Giga to.',
      required: false
    }];
  }
  if (tool.category === 'docs') {
    return [{
      id: 'read_files',
      label: 'Read your files & folders',
      description: `Index docs, sheets, slides and PDFs in your ${tool.name}.`,
      required: true
    }, {
      id: 'read_shared',
      label: 'Read shared drives',
      description: 'Include drives shared with you by your team.',
      required: true
    }, {
      id: 'write_docs',
      label: 'Create & edit documents',
      description: 'Draft new docs when you ask Giga to.',
      required: false
    }];
  }
  if (tool.category === 'communication') {
    return [{
      id: 'read_msgs',
      label: 'Read messages & channels',
      description: 'Index conversations you have access to.',
      required: true
    }, {
      id: 'send_msgs',
      label: 'Send messages',
      description: 'Post updates and replies on your behalf.',
      required: true
    }];
  }
  if (tool.category === 'engineering' || tool.category === 'project') {
    return [{
      id: 'read_issues',
      label: 'Read issues & projects',
      description: 'Index tickets, PRs, and project states.',
      required: true
    }, {
      id: 'write_issues',
      label: 'Update issues',
      description: 'Change statuses and add comments.',
      required: false
    }];
  }
  return [{
    id: 'read_data',
    label: `Read ${tool.name} data`,
    description: 'Index records and objects you have access to.',
    required: true
  }, {
    id: 'write_data',
    label: `Update ${tool.name} records`,
    description: 'Create and modify records on your behalf.',
    required: false
  }];
}
function SlackIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-10407" data-mp="gapg565lptiy">
      
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-10408" data-mp="labpv5m80rn0" />
      
      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#36C5F0" data-id="element-10409" data-mp="3tplrkcm77uu9" />
      
      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-10410" data-mp="3kra1cbp8uz2c" />
      
      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-10411" data-mp="1mwtc4pfoiyu2" />
      
    </svg>;
}
function GoogleIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-id="element-10412" data-mp="3s5r3lumtywce">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.27-4.74 3.27-8.09z" fill="#4285F4" data-id="element-10413" data-mp="3o6085dmczs1j" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" fill="#34A853" data-id="element-10414" data-mp="3e5zkjfhgiprj" />
      <path d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" fill="#FBBC05" data-id="element-10415" data-mp="399itaceynva8" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.51 6.16-4.51z" fill="#EA4335" data-id="element-10416" data-mp="1ccs4u4xxm654" />
    </svg>;
}

// The three Google Workspace tools whose scopes are requested inside the same
// Google consent, so a Google signup arrives with a useful brain already
// connected instead of an empty one.
const GOOGLE_WORKSPACE_TOOLS = ['gmail', 'googledrive', 'googlecalendar'];
const BUSINESS_TYPES = ['E-commerce / DTC', 'Consumer app', 'SaaS / B2B software', 'Marketplace', 'Fintech', 'Healthcare / health-tech', 'Education / ed-tech', 'Media & entertainment', 'Gaming', 'AI / ML', 'Hardware / IoT', 'Real estate / proptech', 'Travel & hospitality', 'Food & beverage', 'Retail (brick & mortar)', 'Logistics & supply chain', 'Professional services', 'Agency / consulting', 'Nonprofit', 'Government / public sector', 'Manufacturing', 'Energy & climate', 'Crypto / web3', 'Other'];

// The connected Slack workspace. In production this comes from the Slack OAuth
// handshake (team name) so the post-connect screens address the user by their
// real workspace instead of a placeholder. Mirrors the account the user
// "connects" earlier in the flow (trackrheadquarters.slack.com).
const WORKSPACE_NAME = 'Trackr HQ';
const WORKSPACE_INITIAL = WORKSPACE_NAME.charAt(0);
function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search…',
  ariaLabel,
  searchable = true
}: {
  value: string;
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  ariaLabel?: string;
  searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.toLowerCase().includes(q));
  }, [query, options]);
  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  // Focus the search field when the panel opens (only when searchable)
  useEffect(() => {
    if (open && searchable) {
      setQuery('');
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open, searchable]);
  return <div ref={wrapperRef} className="relative" data-id="element-10417" data-mp="1825l61qqxmoc">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white text-left focus:outline-none transition-colors ${open ? 'border-[#EA580C]' : 'border-neutral-200'} ${value ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-10418" data-mp="182yv1bl04sap">
        
        <span className="truncate" data-id="element-10419" data-mp="vqwigywodtbo">{value || placeholder}</span>
        <ChevronDown size={18} className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-10420" data-mp="10jpi58daebgw" />
        
      </button>

      <AnimatePresence data-id="element-10421" data-mp="mtm4v6wdxs3l">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute z-30 mt-2 w-full rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" data-id="element-10422" data-mp="qt3co6zab1bx">
          
            {searchable && <div className="p-2 border-b border-neutral-100" data-id="element-10423" data-mp="1ah3pmih8fpx2">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 focus-within:border-[#EA580C] transition-colors" data-id="element-10424" data-mp="1afix0yehah1u">
                  <Search size={16} className="text-neutral-400 shrink-0" data-id="element-10425" data-mp="3v8m9y7gtbpw6" />
                  <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder={searchPlaceholder} className="w-full bg-transparent text-sm text-[#333333] placeholder:text-neutral-400 focus:outline-none" data-id="element-10426" data-mp="3mq15eftjortj" />
              
                </div>
              </div>}
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1" data-id="element-10427" data-mp="3rp5fd8oamtkx">
              {filtered.length === 0 ? <li className="px-4 py-3 text-sm text-neutral-400" data-id="element-10428" data-mp="33zjhqpnkkmf1">
                  No matches found
                </li> : filtered.map(option => {
            const isSelected = option === value;
            return <li key={option} role="option" aria-selected={isSelected} data-id="element-10429" data-mp="3dqe03ixp31v7">
                      <button type="button" onClick={() => {
                onChange(option);
                setOpen(false);
              }} className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5] text-[#333333] font-medium' : 'text-[#333333] hover:bg-neutral-50'}`} data-id="element-10430" data-mp="38z5qomwd7gi1">
                    
                        <span className="truncate" data-id="element-10431" data-mp="1jh0ii47dcr49">{option}</span>
                        {isSelected && <Check size={16} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-10432" data-mp="1oipsrhmrp0ra" />}
                      </button>
                    </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function MultiSelectDropdown({
  values,
  onChange,
  options,
  placeholder = 'Select options'
}: {
  values: string[];
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  return <div ref={wrapperRef} className="relative" data-id="element-10433" data-mp="ecloxw5gsyx8">
      <button type="button" onClick={() => setOpen(o => !o)} className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white text-left focus:outline-none transition-colors ${open ? 'border-[#EA580C]' : 'border-neutral-200'} ${values.length > 0 ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-10434" data-mp="9l3nl6zj4wbq">
        
        <span className="truncate" data-id="element-10435" data-mp="xaxznmc0ygux">
          {values.length === 0 ? placeholder : values.length === 1 ? values[0] : `${values.length} selected`}
        </span>
        <ChevronDown size={18} className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-10436" data-mp="o9zvn41l69cc" />
        
      </button>

      <AnimatePresence data-id="element-10437" data-mp="j9vgpigbtmo8">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute z-30 mt-2 w-full rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" data-id="element-10438" data-mp="jd14mud7yiid">
          
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1" data-id="element-10439" data-mp="3agw30suvoviq">
              {options.map(option => {
            const isSelected = values.includes(option);
            return <li key={option} role="option" aria-selected={isSelected} data-id="element-10440" data-mp="30i6u3pkymlem">
                    <button type="button" onClick={() => onChange(option)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5]' : 'hover:bg-neutral-50'}`} data-id="element-10441" data-mp="2mrbhq3b95m6a">
                    
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C]' : 'border-neutral-300 bg-white'}`} data-id="element-10442" data-mp="2cv8dmrwubdgp">
                      
                        {isSelected && <Check size={12} className="text-white" strokeWidth={3} data-id="element-10443" data-mp="28rsk44g2ht88" />}
                      </div>
                      <span className={`truncate ${isSelected ? 'text-[#333333] font-medium' : 'text-[#333333]'}`} data-id="element-10444" data-mp="1p5qr4z0fvaqa">
                      
                        {option}
                      </span>
                    </button>
                  </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function VisibilityDropdown({
  value,
  onChange
}: {
  value: 'team' | 'private';
  onChange: (val: 'team' | 'private') => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  const isPrivate = value === 'private';
  const OPTIONS: {
    id: 'team' | 'private';
    Icon: typeof Users;
    label: string;
  }[] = [{
    id: 'team',
    Icon: Users,
    label: 'Team-only'
  }, {
    id: 'private',
    Icon: Lock,
    label: 'Private (invite only)'
  }];
  return <div ref={wrapperRef} className="relative" data-id="element-10445" data-mp="63r1ibckj3df">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label="Who can use this connection?" className={`inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${open ? 'border-[#333333] bg-white' : 'border-neutral-200 bg-neutral-50 hover:border-neutral-300'} text-neutral-600`} data-id="element-10446" data-mp="652acpkvf1be">
        
        {isPrivate ? <Lock size={11} data-id="element-10447" data-mp="3jhc9xk7jo0yo" /> : <Users size={11} data-id="element-10448" data-mp="3o9lzv08nt10y" />}
        {isPrivate ? 'Private' : 'Team-only'}
        <ChevronDown size={12} className={`text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-10449" data-mp="3obqby5gpp6cb" />
        
      </button>

      <AnimatePresence data-id="element-10450" data-mp="2vt2xva04urkq">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.14,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute right-0 z-30 mt-2 w-60 rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" role="listbox" data-id="element-10451" data-mp="2vs06z22t8p4t">
          
            <div className="px-3 pt-2.5 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-10452" data-mp="25bpl72yf7o1s">
              Who should have access?
            </div>
            {OPTIONS.map(opt => {
          const active = opt.id === value;
          return <button key={opt.id} type="button" role="option" aria-selected={active} onClick={() => {
            onChange(opt.id);
            setOpen(false);
          }} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors ${active ? 'text-[#333333] font-medium' : 'text-[#333333] hover:bg-neutral-50'}`} data-id="element-10453" data-mp="1vjbk8cf62lgo">
                
                  <opt.Icon size={15} className="text-neutral-500 shrink-0" data-id="element-10454" data-mp="6gvog6odvvj2" />
                  <span className="flex-1 truncate" data-id="element-10455" data-mp="2gbqx1zwepwe">{opt.label}</span>
                  {active && <Check size={15} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-10456" data-mp="3tmmlcfaekser" />}
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function StickyRevealBar({
  children
}: {
  children: React.ReactNode;
}) {
  // A persistent fixed action bar pinned to the bottom of the screen. It stays
  // visible no matter where the user is in the scroll, with a one-time slide-up
  // entrance on mount. Self-contained so it never affects the page hook order.
  return <motion.div initial={{
    y: 90,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    type: 'spring',
    stiffness: 380,
    damping: 34,
    delay: 0.1
  }} className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 backdrop-blur-sm shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.12)]" data-id="element-10457" data-mp="35xk3p7frqzk9">
      
      <div className="max-w-4xl mx-auto px-6 py-4" data-id="element-10458" data-mp="2luo08l2h0rbp">{children}</div>
    </motion.div>;
}
// The tool picker must show the real, familiar mark for every company — not
// a letter badge or a loosely drawn icon. Each entry maps directly to the
// product's domain; logo.dev serves the canonical brand asset and the brand
// favicon is a resilient fallback if that service is temporarily unavailable.
const TOOL_LOGO_DOMAINS: Record<string, string> = {
  slack: 'slack.com',
  gmail: 'mail.google.com',
  microsoftoutlook: 'outlook.com',
  microsoftteams: 'teams.microsoft.com',
  discord: 'discord.com',
  googledrive: 'drive.google.com',
  notion: 'notion.so',
  confluence: 'atlassian.com',
  coda: 'coda.io',
  dropbox: 'dropbox.com',
  github: 'github.com',
  gitlab: 'gitlab.com',
  bitbucket: 'bitbucket.org',
  sentry: 'sentry.io',
  datadog: 'datadoghq.com',
  linear: 'linear.app',
  jira: 'atlassian.com',
  asana: 'asana.com',
  trello: 'trello.com',
  mondaydotcom: 'monday.com',
  salesforce: 'salesforce.com',
  hubspot: 'hubspot.com',
  pipedrive: 'pipedrive.com',
  zendesk: 'zendesk.com',
  intercom: 'intercom.com',
  front: 'front.com',
  stripe: 'stripe.com',
  quickbooks: 'quickbooks.intuit.com',
  figma: 'figma.com',
  miro: 'miro.com',
  googleanalytics: 'analytics.google.com',
  mixpanel: 'mixpanel.com',
  amplitude: 'amplitude.com',
  mailchimp: 'mailchimp.com',
  googleads: 'ads.google.com',
  hootsuite: 'hootsuite.com'
};
const LOGO_DEV_TOKEN = 'pk_E8dUI9PzTHmmaUwDeUCyiQ';
function ToolLogo({
  name,
  icon,
  className = 'w-12 h-12 mx-auto mb-3'
}: {
  name: string;
  icon: string;
  className?: string;
  fallbackTextSize?: string;
}) {
  const [source, setSource] = useState<'canonical' | 'favicon'>('canonical');
  const domain = TOOL_LOGO_DOMAINS[icon];
  const src = source === 'canonical' ? `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=128` : `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  return <img src={src} alt={name} onError={() => {
    if (source === 'canonical') setSource('favicon');
  }} className={`${className} object-contain`} data-id="element-10459" data-mp="2maz3bf849b2j" />;
}
function TeamsIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-10460" data-mp="2dbvj27x7qpmg">
      
      <path d="M16.5 13.5c1.93 0 3.5-1.57 3.5-3.5s-1.57-3.5-3.5-3.5-3.5 1.57-3.5 3.5 1.57 3.5 3.5 3.5z" fill="#5059C9" data-id="element-10461" data-mp="2hd72fvphuem7" />
      
      <path d="M22.5 19.5v-1.5c0-1.93-1.57-3.5-3.5-3.5h-5c-1.93 0-3.5 1.57-3.5 3.5v1.5h12z" fill="#5059C9" data-id="element-10462" data-mp="3542yx64w9xzs" />
      
      <path d="M7.5 12c1.93 0 3.5-1.57 3.5-3.5S9.43 5 7.5 5 4 6.57 4 8.5 5.57 12 7.5 12z" fill="#7B83EB" data-id="element-10463" data-mp="2w9od0kpuzp7p" />
      
      <path d="M13.5 19.5v-1.5c0-1.93-1.57-3.5-3.5-3.5h-5c-1.93 0-3.5 1.57-3.5 3.5v1.5h12z" fill="#7B83EB" data-id="element-10464" data-mp="30b1fqlxnncae" />
      
    </svg>;
}
function GigaHeader({
  hideOnMobile = false
}: {
  hideOnMobile?: boolean;
}) {
  return <div className={`border-b border-neutral-100 px-6 py-4 ${hideOnMobile ? 'hidden lg:block' : ''}`} data-id="element-10465" data-mp="3sruced268h2n">
      
      <Link to="/" aria-label="Back to Giga home" className="inline-flex items-center gap-2 group w-fit" data-id="element-10466" data-mp="254akclpa4pwm">
        
        <div className="w-8 h-8 rounded-lg bg-[#EA580C] flex items-center justify-center group-hover:scale-105 transition-transform" data-id="element-10467" data-mp="202l9p75yye92">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-10468" data-mp="1v6wf7i8t3njm" />
          
        </div>
        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-5 object-contain" data-id="element-10469" data-mp="2iwqtm48rxk5z" />
        
      </Link>
    </div>;
}
export function Signup() {
  const screenInit = useScreenInit();
  const location = useLocation();
  const navigate = useNavigate();
  // Returning users go through /app-screen-mockups/sign-in, which is its own
  // screen — this component is only the signup flow.
  const isLogin = false;
  // The feature isn't live yet — the public "Get started" CTAs send users here
  // with ?waitlist=1, which drops them on the Slack connect screen (step 2)
  // with the waitlist popup open over it.
  const waitlistParam = new URLSearchParams(location.search).get('waitlist') === '1';
  // Any screen in this flow can be opened directly with ?step=<n> (e.g.
  // ?step=22 for the Giga web chat landing), so reviewing a single screen
  // doesn't mean clicking through the whole dummy signup.
  const stepParam = Number(new URLSearchParams(location.search).get('step'));
  // A few screens also get their own real route, so they show up in the route
  // dropdown and can be opened without knowing a step number.
  const STEP_ROUTES: Record<string, number> = {
    '/app-screen-mockups/google-consent': 20,
    '/app-screen-mockups/google-permissions': 21,
    '/app-screen-mockups/giga-chat': 22
  };
  const stepFromPath = STEP_ROUTES[location.pathname];
  const stepFromUrl = stepFromPath ?? (Number.isFinite(stepParam) && stepParam > 0 ? stepParam : undefined);
  const [step, setStep] = useState(stepFromUrl ?? screenInit.step ?? (waitlistParam ? 2 : isLogin ? 10 : 1));
  const [showWaitlist, setShowWaitlist] = useState(waitlistParam);
  // Which front door the user came through. Slack signups end in Slack; Google
  // signups have no Slack to hand off to, so they land in Giga's web chat and
  // can add Slack later. Only the final destination differs — the setup steps
  // in between are shared.
  const [authProvider, setAuthProvider] = useState<'slack' | 'google'>(stepFromUrl && stepFromUrl >= 20 ? 'google' : 'slack');
  // Step 22: the web chat's nav stays out of the way. A collapsed rail holds
  // the brand mark and one toggle; the sidebar itself slides in over the page
  // only when asked for, so the composer is never competing with navigation.
  const [chatNavOpen, setChatNavOpen] = useState(false);
  const [chatNavMoreOpen, setChatNavMoreOpen] = useState(false);
  const [chatAccountOpen, setChatAccountOpen] = useState(false);
  const [chatBillingOpen, setChatBillingOpen] = useState(false);
  // Step 22 composer: what this chat is allowed to draw on. Empty by default —
  // Giga uses everything the user can see unless they narrow it.
  const [contextOpen, setContextOpen] = useState(false);
  const [contextTab, setContextTab] = useState<'All' | 'Groups' | 'Tracks' | 'Credentials' | 'Databases'>('All');
  const [contextQuery, setContextQuery] = useState('');
  const [chatContext, setChatContext] = useState<string[]>([]);
  // Which pane the chat shell is showing. 'chat' is the thread; everything
  // else is a workspace view rendered inside the same shell, so the nav never
  // navigates away.
  const [chatView, setChatView] = useState<'chat' | WorkspaceViewId>('chat');
  // Picking a destination does NOT close the nav — once opened it stays until
  // the user closes it, so moving between views doesn't cost a re-open.
  const openChatView = (v: 'chat' | WorkspaceViewId) => {
    setChatView(v);
    setChatNavMoreOpen(false);
  };
  // DEBUG: a brand-new workspace with nothing connected yet, so the context
  // picker's empty state can be reviewed without unpicking the setup steps.
  const [chatNoConnections, setChatNoConnections] = useState(false);
  const [chatNotifOpen, setChatNotifOpen] = useState(false);
  const [chatChecklistOpen, setChatChecklistOpen] = useState(false);
  const [chatChecklistStep, setChatChecklistStep] = useState<string | null>('Connect your tools');
  // Step 22: the live thread. Empty until the first prompt, at which point the
  // greeting is replaced by the conversation and the composer stays put.
  type ChatMsg = {
    id: number;
    role: 'user' | 'giga';
    text?: string;
    steps?: {
      icon: string;
      label: string;
    }[];
    answer?: string[];
    sources?: {
      icon: string;
      label: string;
    }[];
    thinking?: boolean;
  };
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const [chatCreditNoticeSeen, setChatCreditNoticeSeen] = useState(false);
  const [chatDraft, setChatDraft] = useState('');
  const chatThreadRef = useRef<HTMLDivElement>(null);
  const chatScripts: Record<string, {
    steps: {
      icon: string;
      label: string;
    }[];
    answer: string[];
    sources: {
      icon: string;
      label: string;
    }[];
  }> = {
    "What changed in my inbox while I was out?": {
      steps: [{
        icon: 'gmail',
        label: 'Read 63 unread threads in Gmail'
      }, {
        icon: 'googlecalendar',
        label: 'Cross-checked against your calendar'
      }],
      answer: ['**Three things need you today.**', '**Acme renewal** — their legal team sent redlines on Tuesday and asked for a reply by Friday. Two clauses changed: liability cap and data residency.', '**Northwind pilot** — Marcus confirmed the pilot start for 8 Sep and is waiting on your sign-off for seat count.', '**Invoice #2214** — finance flagged it as 21 days overdue and has already chased twice.', 'The remaining 60 threads are newsletters, calendar noise and CC traffic. I can archive those if you want.'],
      sources: [{
        icon: 'gmail',
        label: '63 threads'
      }, {
        icon: 'googlecalendar',
        label: '4 events'
      }]
    },
    'Summarise the deck in Drive from yesterday': {
      steps: [{
        icon: 'googledrive',
        label: 'Found “Q3 Board Review.pdf” — edited yesterday'
      }, {
        icon: 'googledrive',
        label: 'Read 24 slides'
      }],
      answer: ['**Q3 Board Review — 24 slides, edited by Priya at 18:40.**', 'The argument is that growth held while spend fell: revenue up 34% quarter over quarter, CAC down 12%, and net retention at 118%.', 'Two risks are named — hiring is a quarter behind plan, and one enterprise renewal worth 8% of ARR is unsigned.', 'Slide 19 has a gap: the ask is stated but there is no numbers slide behind it. Worth filling before Thursday.'],
      sources: [{
        icon: 'googledrive',
        label: 'Q3 Board Review.pdf'
      }]
    },
    "Who am I meeting tomorrow, and what do I need?": {
      steps: [{
        icon: 'googlecalendar',
        label: 'Read tomorrow’s 4 events'
      }, {
        icon: 'gmail',
        label: 'Pulled the last thread with each attendee'
      }],
      answer: ['**Four meetings tomorrow. Two need prep.**', '**09:30 — Acme, legal review (45m).** Bring a position on the liability cap; their redlines are still unanswered.', '**11:00 — Internal standup (15m).** Nothing needed.', '**14:00 — Northwind, pilot kickoff (60m).** Marcus wants seat count confirmed. Last thread says 25, your notes say 40.', '**16:30 — 1:1 with Priya (30m).** She flagged the slide 19 gap, so expect that.', 'I can draft the Acme reply and a one-pager for Northwind now.'],
      sources: [{
        icon: 'googlecalendar',
        label: '4 events'
      }, {
        icon: 'gmail',
        label: '6 threads'
      }]
    }
  };
  const sendChatMessage = (text: string) => {
    const clean = text.trim();
    if (!clean) return;
    // The credit welcome is a first-run message. Once they've asked something
    // it has done its job, so it never returns — even on a new chat.
    setChatCreditNoticeSeen(true);
    const script = chatScripts[clean] ?? {
      steps: [{
        icon: 'gmail',
        label: 'Searched your connected tools'
      }],
      answer: ["Here's what I found across Gmail, Drive and Calendar. Ask me to act on any of it and I'll take it from there."],
      sources: [{
        icon: 'gmail',
        label: 'Gmail'
      }, {
        icon: 'googledrive',
        label: 'Drive'
      }]
    };
    const base = Date.now();
    setChatDraft('');
    setChatMessages(prev => [...prev, {
      id: base,
      role: 'user',
      text: clean
    }, {
      id: base + 1,
      role: 'giga',
      thinking: true,
      steps: script.steps
    }]);
    // Reveal the steps, then the answer, so the thread reads as work being
    // done rather than a block of text appearing at once.
    setTimeout(() => {
      setChatMessages(prev => prev.map(m => m.id === base + 1 ? {
        ...m,
        thinking: false,
        answer: script.answer,
        sources: script.sources
      } : m));
    }, 1600);
  };
  useEffect(() => {
    if (chatMessages.length > 0) {
      chatThreadRef.current?.scrollTo({
        top: chatThreadRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [chatMessages]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  // Step 10: once the user signs out of all workspaces, the remembered
  // workspace list disappears and the screen falls back to a clean sign-in.
  const [signedOut, setSignedOut] = useState(false);
  // Step 10: only the most recently used workspace is shown by default. The
  // full list is one click away, so returning users get a single obvious way
  // back in rather than a wall of near-identical rows competing with the
  // auth buttons above them.
  const [showAllWorkspaces, setShowAllWorkspaces] = useState(false);
  // Step 10: Microsoft Teams isn't live yet, so the "Join Microsoft Teams
  // waitlist" button opens an inline email capture and confirms once submitted.
  const [teamsWaitlistOpen, setTeamsWaitlistOpen] = useState(false);
  const [teamsWaitlistEmail, setTeamsWaitlistEmail] = useState('');
  const [teamsWaitlistDone, setTeamsWaitlistDone] = useState(false);
  const submitTeamsWaitlist = (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamsWaitlistEmail.trim()) return;
    setTeamsWaitlistDone(true);
  };
  // Toggle a tool in/out of the selected set (used by the step 3 tool grid)
  const toggleTool = (id: string) => {
    setSelectedTools(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [toolSearch, setToolSearch] = useState('');
  // Step 5: "Tell us more about you" select answers
  const [foundUs, setFoundUs] = useState('');
  const [role, setRole] = useState('');
  // Step 5: what kind of business the user runs (required, searchable)
  const [businessType, setBusinessType] = useState('');
  // Step 5: which AI Teammate(s) the user wants to create first (required, multi-select)
  const [firstTeammates, setFirstTeammates] = useState<string[]>([]);
  const toggleFirstTeammate = (option: string) => setFirstTeammates(prev => prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]);
  // Step 6: Functional roles for tool recommendations (multi-select)
  const [funcRoles, setFuncRoles] = useState<string[]>([]);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  // Step 6: integrations the user requested when not found in the catalog
  const [requestedTools, setRequestedTools] = useState<string[]>([]);
  // Step 6: optional link the user can paste for a tool we don't list yet
  const [requestedToolLink, setRequestedToolLink] = useState('');
  // Step 11: selected trial plan
  const [plan, setPlan] = useState('plus');
  // Step 11: mobile-only order-summary disclosure (Stripe Checkout pattern)
  const [payDetailsOpen, setPayDetailsOpen] = useState(false);
  // Step 7: per-tool OAuth-style connection flow
  const [connectedConns, setConnectedConns] = useState<string[]>([]);
  // Whether the user has kicked off the connection sequence with the start button
  const [connectionsStarted, setConnectionsStarted] = useState(false);
  const [permModal, setPermModal] = useState<null | {
    id: string;
    name: string;
    icon: string;
    scopes: any[];
  }>(null);
  const [connectPhase, setConnectPhase] = useState<'consent' | 'handing-off' | null>(null);
  // Step 3: Slack OAuth consent — per-permission checkbox state. The two
  // REQUIRED scopes stay locked on; the rest are user-toggleable.
  const [slackOAuthScopes, setSlackOAuthScopes] = useState<Record<string, boolean>>({
    readChannels: true,
    searchHistory: true,
    readFiles: true,
    readDMs: false,
    postOnBehalf: true
  });
  // Step 7: local state for optional permission checkboxes in the modal
  const [optionalScopes, setOptionalScopes] = useState<Record<string, boolean>>({});
  // Step 7: per-tool integration visibility — 'private' (only you) or 'team'
  // (shared with the whole workspace).
  const [connVisibility, setConnVisibility] = useState<Record<string, 'private' | 'team'>>({});
  const USE_CASE_RECS: Record<string, string[]> = {
    'Marketing & Ad Campaigns': ['googleanalytics', 'googleads', 'mailchimp', 'hubspot', 'notion', 'slack'],
    'Operations & Workflow Automation': ['notion', 'slack', 'googledrive', 'asana', 'monday', 'quickbooks'],
    'Data analysis & reporting': ['googleanalytics', 'mixpanel', 'amplitude', 'notion', 'slack', 'github'],
    'Sales & CRM': ['salesforce', 'hubspot', 'pipedrive', 'slack', 'gmail', 'googledrive'],
    'Content & Creative Production': ['figma', 'miro', 'notion', 'slack', 'googledrive', 'dropbox'],
    'Engineering & Development': ['github', 'linear', 'slack', 'sentry', 'googledrive', 'notion'],
    'Executive / Leadership Assistant': ['slack', 'gmail', 'notion', 'googledrive', 'stripe', 'salesforce'],
    'Agency/Consultancy & Client Operations': ['slack', 'notion', 'googledrive', 'asana', 'trello', 'zoom'],
    'eCommerce & Shopify': ['shopify', 'stripe', 'mailchimp', 'googleanalytics', 'slack', 'zendesk'],
    Other: ['slack', 'notion', 'googledrive', 'gmail']
  };
  useEffect(() => {
    if (step === 6 && selectedTools.length === 0) {
      const activeRoles = funcRoles.length > 0 ? funcRoles : firstTeammates.length > 0 ? firstTeammates : ['Engineering & Development'];
      if (funcRoles.length === 0) {
        setFuncRoles(activeRoles);
      }
      const initialTools = new Set<string>();
      activeRoles.forEach(r => {
        USE_CASE_RECS[r]?.forEach(t => initialTools.add(t));
      });
      if (initialTools.size === 0) {
        USE_CASE_RECS['Engineering & Development'].forEach(t => initialTools.add(t));
      }
      // A Google signup already granted Gmail, Drive and Calendar during
      // consent, so those always appear (and show as connected) rather than
      // being offered again as something to set up.
      if (authProvider === 'google') {
        GOOGLE_WORKSPACE_TOOLS.forEach(t => initialTools.add(t));
      }
      setSelectedTools(Array.from(initialTools));
    }
  }, [step, firstTeammates, funcRoles.length, selectedTools.length, authProvider]);
  // Step 7: connections to make — derived from the tools the user selected.
  // Falls back to a sensible default set when nothing was selected (e.g. when
  // the screen is opened directly via screen state).
  const step7Connections = useMemo(() => {
    const ids = selectedTools.length > 0 ? selectedTools : ['slack', 'googledrive', 'notion', 'github', 'linear', 'sentry'];
    return ids.map(id => {
      const tool = ALL_TOOLS.find(t => t.id === id) || {
        id,
        name: id,
        icon: id,
        category: 'other'
      };
      return {
        ...tool,
        scopes: getScopesForTool(tool)
      };
    });
  }, [selectedTools]);
  const step7ActiveIdx = step7Connections.findIndex(c => !connectedConns.includes(c.id));
  const step7AllDone = step7Connections.length > 0 && connectedConns.length === step7Connections.length;
  // Where setup ends. A Slack signup gets handed off to Slack; a Google signup
  // has nothing to hand off to, so it lands in Giga's own web chat.
  const finalStep = authProvider === 'google' ? 22 : 8;
  // Landing/left-pane animation state. These hooks MUST live above the
  // per-step early returns below — otherwise renders that return early would
  // run fewer hooks than renders that fall through, tripping React #300.
  const LANDING_TESTIMONIAL_COUNT = 3;
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % LANDING_TESTIMONIAL_COUNT);
    }, 7000);
    return () => clearInterval(testimonialInterval);
  }, []);
  // Setup checklist progress — the rows resolve one-by-one (pending → done) so
  // the card reads like a live, sub-60-second setup actually happening.
  const SETUP_STEPS = 3;
  const [setupStep, setSetupStep] = useState(0);
  useEffect(() => {
    const start = setTimeout(() => {
      const id = setInterval(() => {
        setSetupStep(prev => {
          if (prev >= SETUP_STEPS) {
            clearInterval(id);
            return prev;
          }
          return prev + 1;
        });
      }, 750);
      (window as any).__gigaSetupInterval = id;
    }, 600);
    return () => {
      clearTimeout(start);
      clearInterval((window as any).__gigaSetupInterval);
    };
  }, []);
  // Step 22: where a Google signup lands. There is no Slack to hand off to,
  // so Giga's own web chat is the product surface. Opens on a real composer
  // with starters drawn from what was just connected, so the first question is
  // one click rather than a blank page. Slack is offered, not required.
  if (step === 22) {
    const starters = ["What changed in my inbox while I was out?", 'Summarise the deck in Drive from yesterday', "Who am I meeting tomorrow, and what do I need?"];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className={`min-h-screen bg-[#FAF8F4] font-body transition-[padding] duration-250 ease-[cubic-bezier(0.23,1,0.32,1)] ${chatNavOpen ? 'md:pl-[272px]' : ''}`} data-id="element-10470" data-mp="3alyzyrywjrlm">
        {/* Collapsed rail: just the mark and the one control that reveals the
         nav. Mirrors the reference — brand top-left, nothing else claiming
         space until the user asks for it. Hidden while the nav is pinned open,
         since the panel carries its own header. */}
        <div className={`fixed left-0 top-0 z-30 flex items-center gap-1 p-3 ${chatNavOpen ? 'md:hidden' : ''}`} data-id="element-10471" data-mp="9owmggwpak7z">
          <Link to="/" aria-label="Brightwave workspace" className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1F3A3D] text-[13px] font-bold tracking-tight text-white transition-transform hover:scale-105" data-id="element-10472" data-mp="eng8ehbanw0c">
            BW
          </Link>
          <button type="button" onClick={() => setChatNavOpen(true)} aria-label="Open navigation" aria-expanded={chatNavOpen} className="flex h-9 w-9 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-white hover:text-[#2B211A]" data-id="element-10473" data-mp="nnd1qw8mpj1q">
            <PanelLeft size={17} data-id="element-10474" data-mp="12csj5lm9sc4r" />
          </button>
        </div>

        {/* Top-right: what needs your attention, and the one human escape
         hatch. Both stay out of the composer's way. */}
        <div className="fixed right-0 top-0 z-30 flex items-center gap-1.5 p-3" data-id="element-10475" data-mp="1bfjrvdnrgtsp">
          {/* Once a thread exists it has weight and an audience — its size and
           who can see it belong here, not buried in a settings panel. */}
          {chatView === 'chat' && chatMessages.length > 0 && <span className="mr-1 hidden items-center gap-2.5 text-[12.5px] text-neutral-500 sm:flex" data-id="element-10476" data-mp="17gtcdowv5t7l">
              <span data-id="element-10477" data-mp="12k2uqx5eupk2">
                {(0.01 + chatMessages.length * 0.006).toFixed(2)} MB
              </span>
              <button type="button" className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 transition-colors hover:bg-white hover:text-[#2B211A]" data-id="element-10478" data-mp="etpa3rp5nszi">
                <Copy size={12} className="text-neutral-400" data-id="element-10479" data-mp="orwfredzuom7" />
                Private
              </button>
            </span>}
          {/* Setup progress sits with the other status affordances rather than
           floating over the composer. A ring and a count — status, not promo. */}
          <button type="button" onClick={() => setChatChecklistOpen(o => !o)} aria-expanded={chatChecklistOpen} className={`flex items-center gap-2 rounded-full border py-1.5 pl-2.5 pr-3 transition-colors ${chatChecklistOpen ? 'border-[#E2DACD] bg-white' : 'border-[#EFEAE1]/80 bg-white/70 backdrop-blur-sm hover:border-[#E2DACD] hover:bg-white'}`} data-id="element-10480" data-mp="1v7p7ow0j36jl">
            <svg viewBox="0 0 20 20" className="h-[15px] w-[15px] -rotate-90" aria-hidden="true" data-id="element-10481" data-mp="345yipjov3xm4">
              <circle cx="10" cy="10" r="8" fill="none" stroke="#EFEAE1" strokeWidth="3" data-id="element-10482" data-mp="3940act0ct597" />
              <circle cx="10" cy="10" r="8" fill="none" stroke="#2B211A" strokeWidth="3" strokeLinecap="round" strokeDasharray={2 * Math.PI * 8} strokeDashoffset={2 * Math.PI * 8 * 0.6} data-id="element-10483" data-mp="oyj77910v9z" />
            </svg>
            <span className="hidden text-[12.5px] font-medium text-neutral-500 sm:inline" data-id="element-10484" data-mp="3n2q88auzz8ym">
              Finish setup
            </span>
            <span className="text-[12.5px] text-neutral-300" data-id="element-10485" data-mp="jmj5c6kepfqw">2/5</span>
          </button>
          <div className="relative" data-id="element-10486" data-mp="jkokes94xnmt">
            <button type="button" onClick={() => setChatNotifOpen(o => !o)} aria-label="Notifications" aria-expanded={chatNotifOpen} className="relative flex h-9 w-9 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-white hover:text-[#2B211A]" data-id="element-10487" data-mp="odhorf0oytyd">
              <Bell size={17} data-id="element-10488" data-mp="oasbrzw2m9l5" />
              <span className="absolute right-2 top-2 h-[7px] w-[7px] rounded-full bg-[#EA580C] ring-2 ring-[#FAF8F4]" data-id="element-10489" data-mp="oa0snojnu4br" />
            </button>
            <AnimatePresence data-id="element-10490" data-mp="je1vpck9ndzq">
              {chatNotifOpen && <>
                  <div className="fixed inset-0 z-30" onClick={() => setChatNotifOpen(false)} data-id="element-10491" data-mp="fb5ki6x22k5t" />
                  <motion.div initial={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} transition={{
                duration: 0.18,
                ease: [0.23, 1, 0.32, 1]
              }} className="absolute right-0 top-[calc(100%+6px)] z-40 w-[300px] origin-top-right overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_24px_60px_-24px_rgba(43,33,26,0.35)]" data-id="element-10492" data-mp="aivoc95cknv3">
                    <p className="border-b border-[#F4F0E9] px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-10493" data-mp="27bkwy9w83jkd">
                      Notifications
                    </p>
                    <ul className="divide-y divide-[#F4F0E9]" data-id="element-10494" data-mp="2h5jsgq1sk53c">
                      {[{
                    title: 'Gmail, Drive and Calendar connected',
                    time: 'Just now'
                  }, {
                    title: 'Your $100 in credits is active',
                    time: 'Just now'
                  }].map(n => <li key={n.title} className="px-4 py-3" data-id="element-10495" data-mp="1y8s4w4f7v3th">
                          <p className="text-[13.5px] font-medium leading-snug text-[#2B211A]" data-id="element-10496" data-mp="1yb4vf5z1imi5">
                            {n.title}
                          </p>
                          <p className="mt-0.5 text-[12px] text-neutral-400" data-id="element-10497" data-mp="15rpjet1e5xx4">
                            {n.time}
                          </p>
                        </li>)}
                    </ul>
                  </motion.div>
                </>}
            </AnimatePresence>
          </div>
        </div>

        {/* Setup checklist, hung off the top-right control. Only the step you
         are on expands, so the panel stays short. */}
        <div className="fixed right-3 top-[52px] z-40 w-[300px]" data-id="element-10498" data-mp="394mu5x0l3jo">
          <AnimatePresence initial={false} data-id="element-10499" data-mp="i2zoaquubqku">
            {chatChecklistOpen && <motion.div key="checklist" initial={{
            opacity: 0,
            y: -8,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -8,
            scale: 0.98
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="origin-top-right overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_28px_70px_-28px_rgba(43,33,26,0.4)]" data-id="element-10500" data-mp="d5pr2p17mamg">
                <div className="border-b border-[#F4F0E9] px-4 py-3.5" data-id="element-10501" data-mp="1fi0nf7od2qxo">
                  <div className="flex items-center justify-between gap-3" data-id="element-10502" data-mp="1kgu418yy2y22">
                    <span className="text-[14px] font-semibold text-[#2B211A]" data-id="element-10503" data-mp="15jjsy0q7ru6v">
                      Finish setup
                    </span>
                    <button type="button" onClick={() => setChatChecklistOpen(false)} aria-label="Close checklist" className="text-neutral-300 transition-colors hover:text-[#2B211A]" data-id="element-10504" data-mp="1ah35mw79jywp">
                      <X size={15} data-id="element-10505" data-mp="11irjb6dfjna2" />
                    </button>
                  </div>
                  <div className="mt-2.5 flex items-center gap-2.5" data-id="element-10506" data-mp="mtlacqosiioz">
                    <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-[#F0EBE2]" data-id="element-10507" data-mp="rrwjt0b6ar6o">
                      <div className="h-full w-[40%] rounded-full bg-[#2B211A]" data-id="element-10508" data-mp="rpk834w0dd22" />
                    </div>
                    <span className="text-[12px] text-neutral-400" data-id="element-10509" data-mp="htpfpi1zg3m3">
                      2 of 5
                    </span>
                  </div>
                </div>
                <ul className="divide-y divide-[#F4F0E9]" data-id="element-10510" data-mp="30ywml5j9j7qo">
                  {[{
                label: 'Create your Giga account',
                done: true
              }, {
                label: 'Connect your tools',
                done: true,
                body: 'Gmail, Drive and Calendar are in. Add Notion, Slack or Linear so Giga can act across the rest of your stack.',
                cta: 'Connect other tools',
                view: 'integrations'
              }, {
                label: 'Ask your first question',
                done: false,
                body: 'Try one of the prompts above — it answers from the tools you just connected.'
              }, {
                label: 'Set up a Pulse',
                done: false,
                body: 'Put a recurring job on a schedule, like a Monday pipeline recap.',
                cta: 'Create a Pulse',
                view: 'pulses'
              }, {
                label: 'Build your first Workflow',
                done: false,
                body: 'Chain a few steps together so one prompt runs the whole job.',
                cta: 'Build a Workflow',
                view: 'journeys'
              }].map(item => {
                const open = chatChecklistStep === item.label;
                return <li key={item.label} data-id="element-10511" data-mp="1hzpcbvpqftbh">
                        <button type="button" onClick={() => setChatChecklistStep(open ? null : item.label)} className="flex w-full items-center gap-2.5 px-4 py-3 text-left" data-id="element-10512" data-mp="1i0qoy4mecnvb">
                          {item.done ? <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2B211A]" data-id="element-10513" data-mp="2ap5ywcqo8fzs">
                              <Check size={11} strokeWidth={3.5} className="text-white" data-id="element-10514" data-mp="20xjh1xjtvio2" />
                            </span> : <span className="h-5 w-5 shrink-0 rounded-full border-[1.5px] border-dashed border-neutral-300" data-id="element-10515" data-mp="2ycdto0qh46nk" />}
                          <span className={`flex-1 text-[13.5px] font-semibold ${item.done ? 'text-neutral-400 line-through' : 'text-[#2B211A]'}`} data-id="element-10516" data-mp="2tlnmqj2efvqr">
                            {item.label}
                          </span>
                          <ChevronDown size={14} className={`shrink-0 text-neutral-300 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-10517" data-mp="vop3tv1nhyjf" />
                        </button>
                        <AnimatePresence initial={false} data-id="element-10518" data-mp="mwn3rxvhuk9o">
                          {open && item.body && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2,
                      ease: [0.23, 1, 0.32, 1]
                    }} className="overflow-hidden" data-id="element-10519" data-mp="7zwckbvz4ev5">
                              <div className="px-4 pb-4 pl-[42px]" data-id="element-10520" data-mp="1tbwgi6jrirwq">
                                <p className="text-[12.5px] leading-relaxed text-neutral-500" data-id="element-10521" data-mp="1ycbrwskhhpc2">
                                  {item.body}
                                </p>
                                {item.cta && <button type="button" onClick={() => openChatView((item as any).view === 'journeys' ? 'workflows' : (item as any).view === 'integrations' ? 'connections' : (item as any).view)} className="mt-2.5 inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-10522" data-mp="23iz34xcb6khp">
                                    {item.cta}
                                    <ArrowRight size={13} data-id="element-10523" data-mp="2m7xa4vspuctw" />
                                  </button>}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </li>;
              })}
                </ul>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* DEBUG: preview the picker with an unconnected workspace. */}
        <button type="button" onClick={() => setChatNoConnections(v => !v)} title="Debug: empty workspace" aria-label="Debug: empty workspace" aria-pressed={chatNoConnections} className={`fixed bottom-4 left-4 z-30 flex h-7 w-7 items-center justify-center rounded-full border transition-colors ${chatNoConnections ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#EFEAE1] bg-white text-neutral-300 hover:text-[#2B211A]'}`} data-id="element-10524" data-mp="11uoi0kehaxd7">
          <Settings2 size={13} data-id="element-10525" data-mp="kjj8zbo8n0ht" />
        </button>

        {/* The sidebar itself — Tracks and Chats are the content; everything
         else is one quiet "More" disclosure at the bottom. */}
        <AnimatePresence data-id="element-10526" data-mp="tjxuab17cflb">
          {chatNavOpen && <>
              {/* Scrim only on narrow screens, where the pinned panel would
               cover the content. On desktop the layout makes room instead. */}
              <motion.div key="nav-scrim" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} onClick={() => setChatNavOpen(false)} className="fixed inset-0 z-40 bg-[#2B211A]/20 md:hidden" data-id="element-10527" data-mp="vsb3spsqynl" />
              <motion.aside key="nav-panel" initial={{
            x: -280,
            opacity: 0.6
          }} animate={{
            x: 0,
            opacity: 1
          }} exit={{
            x: -280,
            opacity: 0.6
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} className="fixed left-0 top-0 z-50 flex h-full w-[272px] flex-col border-r border-[#EFEAE1] bg-white" data-id="element-10528" data-mp="3pf2l1hvsoa19">
                <div className="flex items-center justify-between gap-2 px-3 py-3" data-id="element-10529" data-mp="31qj7onxw172b">
                  <span className="flex items-center gap-2" data-id="element-10530" data-mp="2wsq4m3rvst69">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1F3A3D] text-[13px] font-bold tracking-tight text-white" data-id="element-10531" data-mp="2wq53jgzeqh9k">
                      BW
                    </span>
                    <span className="text-[14px] font-bold text-[#2B211A]" data-id="element-10532" data-mp="2ntw1duk1p4jq">
                      Brightwave
                    </span>
                  </span>
                  <button type="button" onClick={() => setChatNavOpen(false)} aria-label="Close navigation" className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-10533" data-mp="2i6alqywsbasw">
                    <PanelLeft size={16} data-id="element-10534" data-mp="2wuf6s9jb7yeu" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-3 pb-4" data-id="element-10535" data-mp="2wwrpwq0t0eth">
                  {/* Outlined rather than a solid black bar: it is the primary
                   action, but a filled slab dominates a panel this quiet. */}
                  <button type="button" onClick={() => {
                openChatView('chat');
                setChatMessages([]);
              }} className="mb-5 flex w-full items-center gap-2.5 rounded-xl border border-[#E7E0D4] bg-white px-3 py-2.5 text-[13.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-10536" data-mp="2s35j3ehbn55l">
                    <SquarePen size={15} className="text-neutral-400" data-id="element-10537" data-mp="193g40jn2c46v" />
                    New chat
                  </button>

                  {/* The four surfaces people set up early and revisit often —
                   promoted out of the launcher so they're one click, not two. */}
                  <ul className="mb-4" data-id="element-10538" data-mp="1dx0vdsu5hzx3">
                    {[{
                  label: 'Assistants',
                  icon: UserCircle,
                  view: 'assistants'
                }, {
                  label: 'Pulses',
                  icon: Activity,
                  view: 'pulses'
                }, {
                  label: 'Connections',
                  icon: Plug,
                  view: 'connections'
                }, {
                  label: 'Groups',
                  icon: Users,
                  view: 'groups'
                }].map(item => <li key={item.label} data-id="element-10539" data-mp="25n5hpx1h0x0y">
                        <button type="button" onClick={() => openChatView(item.view as WorkspaceViewId)} className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-[7px] text-left text-[13.5px] transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A] ${chatView === item.view ? 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : 'font-medium text-neutral-600'}`} data-id="element-10540" data-mp="25mdnapjm15c9">
                          <item.icon size={15} strokeWidth={1.9} className={`shrink-0 ${chatView === item.view ? 'text-[#2B211A]' : 'text-neutral-400'}`} data-id="element-10541" data-mp="33goe1uxum0xo" />
                          {item.label}
                        </button>
                      </li>)}
                    {/* The occasional surfaces continue the same list rather
                     than living in a popover that covered the sidebar. */}
                    {chatNavMoreOpen && [{
                  label: 'Workflows',
                  icon: Network,
                  view: 'workflows'
                }, {
                  label: 'Skills',
                  icon: GraduationCap,
                  view: 'skills'
                }, {
                  label: 'Power Ups',
                  icon: Zap,
                  view: 'power-ups'
                }, {
                  label: 'Files',
                  icon: FolderOpen,
                  view: 'files'
                }, {
                  label: 'Tables',
                  icon: Database,
                  view: 'tables'
                }, {
                  label: 'Browser',
                  icon: Globe,
                  view: 'browser'
                }, {
                  label: 'Viewer',
                  icon: Monitor,
                  view: 'viewer'
                }].map(item => <li key={item.label} data-id="element-10542" data-mp="1uev6zj18desu">
                          <button type="button" onClick={() => setChatView(item.view as WorkspaceViewId)} className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-[7px] text-left text-[13.5px] transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A] ${chatView === item.view ? 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : 'font-medium text-neutral-600'}`} data-id="element-10543" data-mp="1uhr5b8gzq5ht">
                            <item.icon size={15} strokeWidth={1.9} className={`shrink-0 ${chatView === item.view ? 'text-[#2B211A]' : 'text-neutral-400'}`} data-id="element-10544" data-mp="3ajdkzbk51xzj" />
                            {item.label}
                          </button>
                        </li>)}
                    <li data-id="element-10545" data-mp="2wrfhphd3ywba">
                      <button type="button" onClick={() => setChatNavMoreOpen(o => !o)} aria-expanded={chatNavMoreOpen} className="flex w-full items-center gap-1.5 rounded-lg px-2 py-[7px] text-left text-[13px] font-medium text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-10546" data-mp="2ws8im1pdyrli">
                        {chatNavMoreOpen ? 'View less' : 'View more'}
                        <ChevronDown size={13} className={`transition-transform duration-200 ${chatNavMoreOpen ? 'rotate-180' : ''}`} data-id="element-10547" data-mp="3fhx83fagxfth" />
                      </button>
                    </li>
                  </ul>

                  {/* Six each, then a way to the full list — a sidebar that
                   scrolls for 18 rows stops being scannable. */}
                  <p className="px-2 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-10548" data-mp="1u0edzxid3l8o">
                    Tracks
                  </p>
                  <ul className="mb-1" data-id="element-10549" data-mp="23vpw8pszbal6">
                    {TRACKS.filter(t => t.status === 'active').slice(0, 6).map(t => <li key={t.name} data-id="element-10550" data-mp="28oiyn70z66u3">
                        <button type="button" onClick={() => openChatView('tracks')} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-[7px] text-left text-[13.5px] font-medium text-neutral-600 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-10551" data-mp="28pk9pbhzqtbt">
                          <Folder size={14} strokeWidth={1.9} className="shrink-0 text-neutral-300" data-id="element-10552" data-mp="2mok0aop2diam" />
                          <span className="truncate" data-id="element-10553" data-mp="2rltmzwl2jfur">{t.name}</span>
                        </button>
                      </li>)}
                  </ul>
                  <button type="button" onClick={() => openChatView('tracks')} className="mb-5 w-full rounded-lg px-2 py-1 text-left text-[12.5px] font-medium text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-10554" data-mp="x8ar0r6os5bk">
                    All tracks
                  </button>

                  <p className="px-2 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-10555" data-mp="z5srpbjs2fmy">
                    Chats
                  </p>
                  <ul className="mb-1" data-id="element-10556" data-mp="u54si2leashn">
                    {['Inbox catch-up', 'Tomorrow’s meetings', 'Q3 pipeline review', 'Renewal risk in Salesforce', 'Draft reply to Acme legal', 'Summarise the board deck'].map(c => <li key={c} data-id="element-10557" data-mp="13747yqgrhoih">
                        <button type="button" onClick={() => openChatView('chats')} className="flex w-full items-center rounded-lg px-2 py-[7px] text-left text-[13.5px] font-medium text-neutral-600 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-10558" data-mp="1hwad0v2a68ya">
                          <span className="truncate" data-id="element-10559" data-mp="1qxi4ouaakbje">{c}</span>
                        </button>
                      </li>)}
                  </ul>
                  <button type="button" onClick={() => openChatView('chats')} className="w-full rounded-lg px-2 py-1 text-left text-[12.5px] font-medium text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-10560" data-mp="25jx8crwthnc6">
                    All chats
                  </button>
                </div>

                {/* Account — usage, billing and settings belong to the person,
                 not the workspace, so they sit under the avatar rather than in
                 the feature launcher. */}
                <div className="relative border-t border-[#EFEAE1] p-3" data-id="element-10561" data-mp="1rkfsx35evmfh">
                  <button type="button" onClick={() => setChatAccountOpen(o => !o)} aria-expanded={chatAccountOpen} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-[#F6F2EA]" data-id="element-10562" data-mp="1dshoh5mnaepn">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EA580C] text-[11px] font-bold text-white" data-id="element-10563" data-mp="13ts67bqu6s8v">
                      SC
                    </span>
                    <span className="min-w-0 flex-1 text-left" data-id="element-10564" data-mp="2b7ccfkomav61">
                      <span className="block truncate text-[13px] font-semibold text-[#2B211A]" data-id="element-10565" data-mp="2b8nhdntsu8yq">
                        Sarah Chen
                      </span>
                      <span className="block truncate text-[11.5px] text-neutral-400" data-id="element-10566" data-mp="22d7q9ekfi7wu">
                        $100 credit · 0 used
                      </span>
                    </span>
                    <ChevronDown size={14} className={`shrink-0 text-neutral-400 transition-transform duration-200 ${chatAccountOpen ? 'rotate-180' : ''}`} data-id="element-10567" data-mp="1she5j7wk383z" />
                  </button>
                  <AnimatePresence data-id="element-10568" data-mp="1jf4wynkulot8">
                    {chatAccountOpen && <motion.ul initial={{
                  opacity: 0,
                  y: 6,
                  scale: 0.98
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 6,
                  scale: 0.98
                }} transition={{
                  duration: 0.18,
                  ease: [0.23, 1, 0.32, 1]
                }} className="absolute bottom-[calc(100%-4px)] left-3 right-3 z-10 origin-bottom rounded-xl border border-[#EFEAE1] bg-white p-1.5 shadow-[0_24px_60px_-24px_rgba(43,33,26,0.35)]" data-id="element-10569" data-mp="1jhr4in7qo4bg">
                        {[{
                    label: 'Usage & billing',
                    icon: CreditCard,
                    onSelect: () => {
                      setChatAccountOpen(false);
                      setChatBillingOpen(true);
                    }
                  }, {
                    label: 'Settings',
                    icon: Settings2,
                    // No dedicated settings view exists yet, so this lands on
                    // the dashboard root rather than a blank screen.
                    onSelect: () => navigate('/app-screen-mockups/dashboard')
                  }].map(item => <li key={item.label} data-id="element-10570" data-mp="qnqte3csshii">
                            <button type="button" onClick={item.onSelect} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-[13.5px] font-medium text-neutral-600 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-10571" data-mp="qqmqxqkqh46f">
                              <item.icon size={15} className="shrink-0 text-neutral-400" data-id="element-10572" data-mp="2wot5xxmh0egx" />
                              {item.label}
                            </button>
                          </li>)}
                      </motion.ul>}
                  </AnimatePresence>
                </div>
              </motion.aside>
            </>}
        </AnimatePresence>

        {/* Chat layout: greeting and starters occupy the empty thread area,
         composer parked at the bottom where it will stay once messages start
         filling the space above it. */}
        {chatView !== 'chat' ? <div className="min-h-screen" data-id="element-10573" data-mp="1lafnx9eeet8t">
            <WorkspaceViews view={chatView} data-id="element-10574" data-mp="1l94ht2xhd9ej" />
          </div> : <div className="flex h-screen flex-col" data-id="element-10575" data-mp="1gce4gnd28rwt">
          {chatMessages.length === 0 ? <div className="flex flex-1 items-center justify-center px-6 pt-20 md:pt-16" data-id="element-10576" data-mp="3rz7vk74tx7gt">
              <div className="w-full max-w-2xl" data-id="element-10577" data-mp="3rxd5yfim4r6e">
                <h1 className="text-center font-title text-3xl font-medium leading-tight tracking-tight text-[#2B211A] md:text-[38px]" data-id="element-10578" data-mp="lk4h9tq1cf9">
                  {chatCreditNoticeSeen ? 'What can I help with?' : "You're all set, Sarah."}
                </h1>
                <p className="mx-auto mt-3.5 max-w-[38ch] text-center text-[15.5px] leading-relaxed text-neutral-500" data-id="element-10579" data-mp="fht4idlfrtsq">
                  {chatCreditNoticeSeen ? 'Ask anything across your connected tools.' : <>
                      Ask anything across your connected tools. Your{' '}
                      <span className="font-semibold text-[#2B211A]" data-id="element-10580" data-mp="5lzj04pqopyt">
                        $100 in credits
                      </span>{' '}
                      is ready — no card needed.
                    </>}
                </p>

                {/* Starters as light chips rather than full-width rows — they
                 sit closer to the composer and read as prompts, not a menu. */}
                <div className="mt-7 flex flex-wrap justify-center gap-2" data-id="element-10581" data-mp="3mufe2lzomb42">
                  {starters.map(s => <button key={s} type="button" onClick={() => sendChatMessage(s)} className="rounded-full border border-[#EFEAE1] bg-white px-4 py-2 text-[13.5px] font-medium text-neutral-600 transition-colors hover:border-[#EA580C] hover:text-[#EA580C]" data-id="element-10582" data-mp="3hzi6zklyqbtt">
                      {s}
                    </button>)}
                </div>
              </div>
            </div> : <div ref={chatThreadRef} className="flex-1 overflow-y-auto px-6 pt-20 md:pt-16" data-id="element-10583" data-mp="4jk6v6vffpib">
              <div className="mx-auto flex max-w-2xl flex-col gap-7 pb-6" data-id="element-10584" data-mp="3vwey7n5ztvb8">
                {chatMessages.map(m => m.role === 'user' ? <div key={m.id} className="flex justify-end" data-id="element-10585" data-mp="3qxbxug0ykd9l">
                      <p className="max-w-[80%] rounded-2xl rounded-br-md bg-[#2B211A] px-4 py-2.5 text-[15px] leading-relaxed text-white" data-id="element-10586" data-mp="s8n0ze78zatp">
                        {m.text}
                      </p>
                    </div> : <motion.div key={m.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex gap-3" data-id="element-10587" data-mp="vqstchlyz7fy">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#1F3A3D]" data-id="element-10588" data-mp="19rdcjevsr59z">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-3.5 w-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-10589" data-mp="1emsmhvqooh14" />
                      </span>
                      <div className="min-w-0 flex-1" data-id="element-10590" data-mp="1si4i05cnybj3">
                        {/* What it did, before what it found. */}
                        {m.steps && <ul className="mb-3 space-y-1" data-id="element-10591" data-mp="22dy3ie8d1fd0">
                            {m.steps.map(s => <li key={s.label} className="flex items-center gap-2 text-[12.5px] text-neutral-400" data-id="element-10592" data-mp="3463wdxub58np">
                                <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${s.icon}.svg`} alt="" aria-hidden="true" className="h-3.5 w-3.5 object-contain" data-id="element-10593" data-mp="395q822uhmgue" />
                                {s.label}
                                {m.thinking ? <Loader2 size={11} className="animate-spin text-neutral-300" data-id="element-10594" data-mp="2z7j3yklart9t" /> : <Check size={11} strokeWidth={3} className="text-neutral-300" data-id="element-10595" data-mp="3mwbsvx3i50ux" />}
                              </li>)}
                          </ul>}
                        {m.thinking && <p className="text-[14.5px] text-neutral-400" data-id="element-10596" data-mp="3e49ue4cztuna">
                            Working…
                          </p>}
                        {m.answer && <div className="space-y-3" data-id="element-10597" data-mp="3i4k9q7modxet">
                            {m.answer.map((para, i) => <p key={i} className="text-[15px] leading-relaxed text-[#2B211A]" dangerouslySetInnerHTML={{
                    __html: para.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
                  }} data-id="element-10598" data-mp="9p0mbp8or97o" data-mpi={i} />)}
                          </div>}
                        {m.sources && <div className="mt-3.5 flex flex-wrap items-center gap-2 border-t border-[#EFEAE1] pt-3" data-id="element-10599" data-mp="1u8vz958o8e66">
                            <span className="text-[11.5px] font-medium uppercase tracking-wider text-neutral-400" data-id="element-10600" data-mp="28vp7jqjrwjov">
                              Sources
                            </span>
                            {m.sources.map(s => <span key={s.label} className="inline-flex items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1 text-[12px] font-medium text-neutral-600" data-id="element-10601" data-mp="1g8uydpsep6qp">
                                <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${s.icon}.svg`} alt="" aria-hidden="true" className="h-3 w-3 object-contain" data-id="element-10602" data-mp="1q83q0bzsu99w" />
                                {s.label}
                              </span>)}
                          </div>}
                      </div>
                    </motion.div>)}
              </div>
            </div>}

          <div className="sticky bottom-0 bg-gradient-to-t from-[#FAF8F4] via-[#FAF8F4] to-transparent px-6 pb-6 pt-4" data-id="element-10603" data-mp="33orf4qccnmax">
            <div className="mx-auto max-w-2xl" data-id="element-10604" data-mp="38nb2mv6ld65e">
              <div className="rounded-2xl border border-[#EFEAE1] bg-white p-4 shadow-[0_18px_50px_-28px_rgba(43,33,26,0.35)]" data-id="element-10605" data-mp="3do8j93byds4s">
                <label htmlFor="giga-first-question" className="sr-only" data-id="element-10606" data-mp="3im0mgti1t0oa">
                  Ask Giga a question
                </label>
                <textarea id="giga-first-question" rows={1} value={chatDraft} onChange={e => setChatDraft(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendChatMessage(chatDraft);
                }
              }} placeholder="Ask about anything in your connected tools…" className="min-h-[28px] w-full resize-none bg-transparent text-[16px] leading-relaxed text-[#2B211A] outline-none placeholder:text-neutral-400" data-id="element-10607" data-mp="2kx6z0ntel6ee" />
                <div className="mt-2.5 flex items-center justify-between gap-4" data-id="element-10608" data-mp="ocs97p11x6p8">
                  {/* Scoping the chat, not advertising the tool count. Giga
                   already uses everything the user can see; this is for
                   narrowing it to one group, track or database. */}
                  <span className="relative flex flex-wrap items-center gap-1.5" data-id="element-10609" data-mp="aczme4snbamj">
                    {/* One paperclip for both photos and files — the OS picker
                     already handles the distinction. */}
                    <button type="button" aria-label="Attach a photo or file" title="Attach a photo or file" className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-[#2B211A]" data-id="element-10610" data-mp="mo6rkzlnczt">
                      <Paperclip size={15} data-id="element-10611" data-mp="3npsz82u0yahi" />
                    </button>
                    {chatContext.map(c => <span key={c} className="inline-flex items-center gap-1 rounded-full bg-[#F4F0E9] py-1 pl-2.5 pr-1.5 text-[12.5px] font-medium text-[#2B211A]" data-id="element-10612" data-mp="3is1atisl96i2">
                        {c}
                        <button type="button" onClick={() => setChatContext(prev => prev.filter(v => v !== c))} aria-label={`Remove ${c}`} className="text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-10613" data-mp="1hrd2swlb61cx">
                          <X size={12} data-id="element-10614" data-mp="17yp76562e3fs" />
                        </button>
                      </span>)}
                    <button type="button" onClick={() => setContextOpen(o => !o)} aria-expanded={contextOpen} className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[12.5px] font-medium text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-[#2B211A]" data-id="element-10615" data-mp="1cxs22wsbyta1">
                      <Plus size={13} data-id="element-10616" data-mp="p3qpgwma3icv" />
                      Add context
                    </button>

                    <AnimatePresence data-id="element-10617" data-mp="u18zkqc183mg">
                      {contextOpen && <>
                          <div className="fixed inset-0 z-30" onClick={() => setContextOpen(false)} data-id="element-10618" data-mp="2jh8d0jmoof7w" />
                          <motion.div initial={{
                        opacity: 0,
                        y: 6,
                        scale: 0.98
                      }} animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }} exit={{
                        opacity: 0,
                        y: 6,
                        scale: 0.98
                      }} transition={{
                        duration: 0.18,
                        ease: [0.23, 1, 0.32, 1]
                      }} className="absolute bottom-[calc(100%+8px)] left-0 z-40 w-[380px] max-w-[calc(100vw-32px)] origin-bottom-left overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_24px_60px_-24px_rgba(43,33,26,0.35)]" data-id="element-10619" data-mp="2oei2u0dzisw9">
                            {/* Tabs only earn their place once there is more
                             than one kind of context to switch between. With an
                             empty workspace they are five empty rooms. */}
                            {!chatNoConnections && <div className="flex gap-1 overflow-x-auto border-b border-[#F4F0E9] px-2 py-2" data-id="element-10620" data-mp="165r0i35qvlgf">
                                {(['All', 'Groups', 'Tracks', 'Credentials', 'Databases'] as const).map(t => <button key={t} type="button" onClick={() => setContextTab(t)} className={`shrink-0 rounded-full px-2.5 py-1 text-[12.5px] font-semibold transition-colors ${contextTab === t ? 'bg-[#2B211A] text-white' : 'text-neutral-500 hover:bg-neutral-100'}`} data-id="element-10621" data-mp="1f65nr829ramk">
                                      {t}
                                    </button>)}
                              </div>}
                            {!chatNoConnections && <div className="border-b border-[#F4F0E9] p-2" data-id="element-10622" data-mp="1ytwk2ml2b4eo">
                                <input type="search" value={contextQuery} onChange={e => setContextQuery(e.target.value)} placeholder="Search context…" aria-label="Search context" className="w-full rounded-lg border border-[#EFEAE1] px-2.5 py-1.5 text-[13px] outline-none transition-colors placeholder:text-neutral-400 focus:border-neutral-400" data-id="element-10623" data-mp="22vi730rb8z2l" />
                              </div>}
                            {/* Fixed height so switching tabs never resizes the
                             panel under the pointer. */}
                            <div className={`overflow-y-auto p-2 ${chatNoConnections ? '' : 'h-[280px]'}`} data-id="element-10624" data-mp="2cywxulqxvt5o">
                              {chatNoConnections ? (
                          /* Nothing set up yet. Each tab explains its own
                          primitive and offers the one action that creates
                          it, so the empty state is never a dead end. */
                          <div className="px-2 py-4" data-id="element-10625" data-mp="30rcbdoztlrm3">
                                  <p className="text-[13.5px] font-semibold text-[#2B211A]" data-id="element-10626" data-mp="2vxhn0899sogo">
                                    Nothing to draw on yet
                                  </p>
                                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-neutral-500" data-id="element-10627" data-mp="3ofvlma2fkegt">
                                    Context is what Giga is allowed to look at
                                    when it answers — the accounts it can sign
                                    into, the tracks and groups your work lives
                                    in, the tables it can query. Connect a tool
                                    and it starts filling in here.
                                  </p>
                                  <button type="button" onClick={() => {
                              setContextOpen(false);
                              setStep(6);
                            }} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#2B211A] px-4 py-2.5 text-[13.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-10628" data-mp="286s7tgsv399">
                                    Connect your tools
                                    <ArrowRight size={14} data-id="element-10629" data-mp="l09a1856gpl3" />
                                  </button>
                                  <p className="mt-2 text-center text-[11.5px] text-neutral-400" data-id="element-10630" data-mp="30reztbvzqjh7">
                                    Takes about a minute
                                  </p>
                                </div>) : (() => {
                            const sections = [{
                              label: 'Groups',
                              items: GROUP_NAMES
                            }, {
                              label: 'Tracks',
                              items: TRACKS.filter(t => t.status === 'active').slice(0, 8).map(t => t.name)
                            }, {
                              label: 'Credentials',
                              items: ['Gmail — sarah@brightwave.io', 'Google Drive', 'Google Calendar']
                            }, {
                              label: 'Databases',
                              items: ['Pipeline (Notion)', 'Support tickets (Airtable)']
                            }].filter(s => contextTab === 'All' || s.label === contextTab).map(s => ({
                              ...s,
                              items: s.items.filter(i => i.toLowerCase().includes(contextQuery.trim().toLowerCase()))
                            })).filter(s => s.items.length > 0);
                            if (sections.length === 0) {
                              return <p className="px-2 py-6 text-center text-[13px] text-neutral-400" data-id="element-10631" data-mp="1uh1bpbq1kz0b">
                                      Nothing matches “{contextQuery}”.
                                    </p>;
                            }
                            return sections.map(s => <div key={s.label} className="mb-2 last:mb-0" data-id="element-10632" data-mp="1gh9smwko4sfg">
                                      <p className="px-2 pb-1 text-[10.5px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-10633" data-mp="1biyhmiin0bvn">
                                        {s.label}
                                      </p>
                                      {s.items.map(item => {
                                const on = chatContext.includes(item);
                                return <button key={item} type="button" onClick={() => setChatContext(prev => on ? prev.filter(v => v !== item) : [...prev, item])} className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-[13.5px] font-medium text-[#2B211A] transition-colors hover:bg-[#FFF7F0]" data-id="element-10634" data-mp="30w210ykkaixx">
                                            <span className="truncate" data-id="element-10635" data-mp="2uhebvpv2015o">{item}</span>
                                            {on && <Check size={14} strokeWidth={3} className="shrink-0 text-[#EA580C]" data-id="element-10636" data-mp="2ujr2ssishluv" />}
                                          </button>;
                              })}
                                    </div>);
                          })()}
                            </div>
                          </motion.div>
                        </>}
                    </AnimatePresence>
                  </span>
                  <button type="button" onClick={() => sendChatMessage(chatDraft)} disabled={!chatDraft.trim()} aria-label="Send" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors ${chatDraft.trim() ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-[#F0EBE2] text-neutral-400'}`} data-id="element-10637" data-mp="3if25ovpgd0o4">
                    <Send size={15} data-id="element-10638" data-mp="omh1xm42w2l5" />
                  </button>
                </div>
              </div>

              {/* Slack is optional, so it is one quiet line under the composer. */}
              <p className="mt-3 flex flex-wrap items-center justify-center gap-1.5 text-center text-[13px] text-neutral-400" data-id="element-10639" data-mp="fkre0q9enls4">
                <SlackIcon size={14} data-id="element-10640" data-mp="aloa5955vllq" />
                Your team already lives in Slack?
                <button type="button" onClick={() => {
                setAuthProvider('slack');
                setStep(2);
              }} className="font-semibold text-neutral-600 underline decoration-neutral-300 underline-offset-4 transition-colors hover:text-[#EA580C] hover:decoration-[#EA580C]" data-id="element-10641" data-mp="5oeidmuauxuq">
                  Add Slack
                </button>
              </p>
            </div>
          </div>
        </div>}

        {/* Same Usage & billing surface as the dashboard, opened on the
         starter-credit model this account is actually on. */}
        <BillingModal open={chatBillingOpen} onClose={() => setChatBillingOpen(false)} initialVariant="B" data-id="element-10642" data-mp="1tl49d9839vsh" />
      </motion.div>;
  }
  // Step 20: Google consent. Modelled on Google's own account-chooser +
  // scope screen. The Workspace scopes are requested here, in the same
  // consent, so the user arrives with Gmail, Drive and Calendar already
  // readable instead of facing an empty brain.
  if (step === 20) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-10643" data-mp="1avfhjozjavhc">
        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-10644" data-mp="1om12dj8jtfhv">
          <div className="flex-1 overflow-auto p-6 sm:p-10" data-id="element-10645" data-mp="s3d8ayp82hh4">
            <div className="mb-6 flex items-center justify-center" data-id="element-10646" data-mp="w5i1jwbrdc38">
              <GoogleIcon size={28} data-id="element-10647" data-mp="w3ndi956x3ux" />
            </div>
            <h1 className="mb-1 text-center font-title text-2xl font-bold text-[#333333]" data-id="element-10648" data-mp="d26gnu6z0qxp">
              Choose an account
            </h1>
            <p className="mb-7 text-center text-[14px] text-neutral-500" data-id="element-10649" data-mp="7zxkkoysx8rd">
              to continue to <span className="font-semibold" data-id="element-10650" data-mp="ms97pouj7ute">Giga</span>
            </p>

            <ul className="mb-6 divide-y divide-neutral-100 overflow-hidden rounded-xl border border-neutral-200" data-id="element-10651" data-mp="hz6j7v4m3bk2">
              {[{
              name: 'Sarah Chen',
              email: 'sarah.chen@brightwave.io',
              initial: 'S',
              tone: 'bg-[#EA580C]'
            }, {
              name: 'Sarah Chen',
              email: 'sarah@gmail.com',
              initial: 'S',
              tone: 'bg-[#3D2F25]'
            }].map(acct => <li key={acct.email} data-id="element-10652" data-mp="10ov40ver1blp">
                  <button type="button" onClick={() => setStep(21)} className="group flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-[#FFF7F0]" data-id="element-10653" data-mp="37gdr8a13jtn6">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${acct.tone}`} data-id="element-10654" data-mp="1i0a31rq1bfms">
                      {acct.initial}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-10655" data-mp="18aqj0iql5fqc">
                      <span className="block truncate text-[15px] font-semibold text-[#333333]" data-id="element-10656" data-mp="1884c8lbir496">
                        {acct.name}
                      </span>
                      <span className="block truncate text-[13px] text-neutral-500" data-id="element-10657" data-mp="z8h7bpjkzgbd">
                        {acct.email}
                      </span>
                    </span>
                    <ChevronRight size={16} className="shrink-0 text-neutral-300 transition-colors group-hover:text-[#EA580C]" data-id="element-10658" data-mp="pbw2b47ka57j" />
                  </button>
                </li>)}
              <li data-id="element-10659" data-mp="uca9yl593d51">
                <button type="button" onClick={() => setStep(21)} className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-neutral-50" data-id="element-10660" data-mp="2jovojg6g5xns">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-neutral-200 text-neutral-400" data-id="element-10661" data-mp="2om49foizcfsq">
                    <Plus size={16} data-id="element-10662" data-mp="2i7ylzrywl4fs" />
                  </span>
                  <span className="text-[15px] font-medium text-neutral-600" data-id="element-10663" data-mp="2d959o2vcrjh7">
                    Use another account
                  </span>
                </button>
              </li>
            </ul>

            <p className="text-center text-[12.5px] leading-relaxed text-neutral-400" data-id="element-10664" data-mp="35xsz8an4wv0k">
              To continue, Google will share your name, email address and
              profile picture with Giga.
            </p>

            <button type="button" onClick={() => setStep(1)} className="mx-auto mt-6 block text-[13px] font-semibold text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-10665" data-mp="2w7hitom9ktcs">
              Back
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 21: Google Workspace scope grant. Kept separate from the account
  // chooser so the permissions being granted are a deliberate read, not
  // something buried under an account row.
  if (step === 21) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-10666" data-mp="2fb3mmtd93n">
        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-10667" data-mp="a1wfm0qbxgy1">
          <div className="flex-1 overflow-auto p-6 sm:p-10" data-id="element-10668" data-mp="j0hwzsk2epx8">
            <div className="mb-5 flex items-center justify-center gap-4" data-id="element-10669" data-mp="iyx1o0pxf2yb">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-neutral-200 bg-white shadow-sm" data-id="element-10670" data-mp="nsjepu0058un">
                <GoogleIcon size={28} data-id="element-10671" data-mp="xq608zbt3ynl" />
              </div>
              <div className="flex gap-1" aria-hidden="true" data-id="element-10672" data-mp="xrqtmlmdxbjv">
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-10673" data-mp="12or0uzuz0kck" />
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-10674" data-mp="1o07b9fd9n0tv" />
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-10675" data-mp="1o2bqgtgm7m9g" />
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EA580C] shadow-sm" data-id="element-10676" data-mp="1j0ut37xcahxm">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain [filter:brightness(0)_invert(1)]" data-id="element-10677" data-mp="1wyt0ftd8te3m" />
              </div>
            </div>

            <h1 className="mb-2 text-center font-title text-2xl font-bold text-[#333333]" data-id="element-10678" data-mp="108tix9f4758w">
              Giga wants access to your Google Account
            </h1>
            <p className="mb-7 text-center text-[14px] text-neutral-500" data-id="element-10679" data-mp="19ajcolwgyg9u">
              sarah.chen@brightwave.io
            </p>

            <p className="mb-3 text-[13px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-10680" data-mp="ljnqc4as8d9z">
              This will allow Giga to
            </p>
            <ul className="mb-6 space-y-2.5" data-id="element-10681" data-mp="2s8y18imam5ze">
              {[{
              label: 'Read your Gmail messages and threads',
              detail: 'So it can answer from your inbox and draft replies.'
            }, {
              label: 'See and open files in Google Drive',
              detail: 'So documents and spreadsheets become part of your brain.'
            }, {
              label: 'View and manage your Google Calendar',
              detail: 'So it can prep for meetings and schedule work.'
            }].map(scope => <li key={scope.label} className="flex gap-3 rounded-xl border border-neutral-200 p-3.5" data-id="element-10682" data-mp="3fz1sdqiaod9i">
                  <Check size={16} strokeWidth={3} className="mt-0.5 shrink-0 text-[#EA580C]" data-id="element-10683" data-mp="31zkalv3f209n" />
                  <span data-id="element-10684" data-mp="36xc1qmw8lics">
                    <span className="block text-[14.5px] font-semibold text-[#333333]" data-id="element-10685" data-mp="370q6g1sdi3ne">
                      {scope.label}
                    </span>
                    <span className="mt-0.5 block text-[13px] leading-relaxed text-neutral-500" data-id="element-10686" data-mp="1vkrnn67e838z">
                      {scope.detail}
                    </span>
                  </span>
                </li>)}
            </ul>

            <div className="flex flex-col gap-3 sm:flex-row-reverse" data-id="element-10687" data-mp="28y41jl8uoj72">
              <button type="button" onClick={() => {
              // Consent granted here, so these arrive already connected.
              setConnectedConns(prev => Array.from(new Set([...prev, ...GOOGLE_WORKSPACE_TOOLS])));
              setStep(5);
            }} className="w-full rounded-xl bg-[#2B211A] px-6 py-3.5 font-bold text-white transition-colors hover:bg-black sm:w-auto" data-id="element-10688" data-mp="2dztcl0w2owv5">
                Allow
              </button>
              <button type="button" onClick={() => setStep(20)} className="w-full rounded-xl border border-neutral-300 px-6 py-3.5 font-bold text-[#2B211A] transition-colors hover:bg-neutral-50 sm:w-auto" data-id="element-10689" data-mp="1v6z850w2sn56">
                Cancel
              </button>
            </div>

            <p className="mt-5 text-[12.5px] leading-relaxed text-neutral-400" data-id="element-10690" data-mp="17ho49ajficmt">
              You can revoke access at any time from your Google Account, or
              disconnect any tool from Giga's settings.
            </p>
          </div>
        </div>
      </motion.div>;
  }
  // Step 2: Slack Consent (Workspace Selection)
  if (step === 2) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-10691" data-mp="36t6mbx11f4gn">
        
        {/* Waitlist popup — opens immediately over the Slack connect screen
                                                                                  while the feature is invite-only. */}
        <AnimatePresence data-id="element-10692" data-mp="2j3ca9hojljxg">
          {showWaitlist && <WaitlistModal referredBy={new URLSearchParams(location.search).get('ref')?.trim().toLowerCase()} onClose={() => {
          setShowWaitlist(false);
          navigate('/');
        }} data-id="element-10693" data-mp="2j2anwh22tb0f" />}
        </AnimatePresence>

        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-10694" data-mp="24izrxqs85d32">
          <div className="flex-1 p-6 sm:p-10 overflow-auto" data-id="element-10695" data-mp="1vh09cu1ka0y0">
            <h1 className="text-2xl font-title font-bold text-[#333333] text-center mb-6" data-id="element-10696" data-mp="1vil68qbclvz1">
              Sign in to Giga with Slack
            </h1>

            {/* Slack ⇄ Giga lockup */}
            <div className="flex items-center justify-center gap-4 mb-8" data-id="element-10697" data-mp="2n9hgrk9xroj5">
              <div className="w-14 h-14 rounded-2xl bg-[#2B211A] flex items-center justify-center shadow-sm" data-id="element-10698" data-mp="2s5ziscdvt8rs">
                <SlackIcon size={28} data-id="element-10699" data-mp="2eeuf8z7e7wc6" />
              </div>
              <ArrowLeftRight size={22} className="text-neutral-400" data-id="element-10700" data-mp="2ifwh7nsp2mhu" />
              <div className="w-14 h-14 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm" data-id="element-10701" data-mp="366acroahn3h6">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 object-contain [filter:brightness(0)_invert(1)]" data-id="element-10702" data-mp="3awqql9i80r42" />
                
              </div>
            </div>

            {/* Account / workspace selector */}
            <p className="text-sm font-bold text-[#2B211A] mb-2" data-id="element-10703" data-mp="1971mfrs4gb1x">
              Select a Slack account or workspace
            </p>
            <button onClick={() => setStep(3)} className="w-full flex items-center justify-between gap-3 p-4 rounded-xl border border-neutral-200 hover:border-[#EA580C] hover:bg-[#FFEDD5]/30 transition-all group mb-6 text-left" data-id="element-10704" data-mp="gj5tmy5ob3af">
              
              <div className="flex items-center gap-3 min-w-0" data-id="element-10705" data-mp="qi3d3mo8s6mk">
                <div className="w-11 h-11 rounded-lg bg-[#611f69] flex items-center justify-center text-white font-bold shrink-0" data-id="element-10706" data-mp="liicz8xc5s89">
                  F
                </div>
                <div className="min-w-0" data-id="element-10707" data-mp="21ybjjz6so23i">
                  <div className="font-bold text-[#2B211A] text-[15px] truncate" data-id="element-10708" data-mp="1wy6zjz6ogp8j">
                    Faith Alqahtani
                  </div>
                  <div className="text-xs text-neutral-500 truncate" data-id="element-10709" data-mp="6i4qu9z0u1bq">
                    alqahtani.faith@gmail.com · trackrheadquarters.slack.com
                  </div>
                </div>
              </div>
              <ChevronDown size={18} className="text-neutral-400 group-hover:text-[#EA580C] shrink-0" data-id="element-10710" data-mp="fj2vmuhaacvd" />
              
            </button>

            {/* Account-details usage */}
            <div className="rounded-2xl bg-neutral-50 border border-neutral-100 p-5 mb-6" data-id="element-10711" data-mp="pew86dv066d5">
              <p className="text-sm font-bold text-[#2B211A] mb-4" data-id="element-10712" data-mp="ucobs550fgx6">
                Your account details will be used to:
              </p>
              <ul className="space-y-4" data-id="element-10713" data-mp="136jel6insv9n">
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-10714" data-mp="135hvcerhp2gu">
                  <Plug size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-10715" data-mp="186fa0hdajedl" />
                  <span data-id="element-10716" data-mp="1d4h37v4fku2s">
                    Establish a verified connection between an existing Giga
                    account and Slack.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-10717" data-mp="1n6b2y0gyencr">
                  <UserPlus size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-10718" data-mp="1ibnjwh4pyl5k" />
                  
                  <span data-id="element-10719" data-mp="1dckf8xsnigy4">
                    Help you create a new Giga account, if you don't already
                    have one.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-10720" data-mp="25yncb760yowe">
                  <Sparkles size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-10721" data-mp="2113n5c7sgrpo" />
                  
                  <span data-id="element-10722" data-mp="1w8akctvvrtg8">
                    Unlock Giga features in Slack, like configurable
                    notifications, rich link previews, and quick actions.
                  </span>
                </li>
              </ul>
            </div>

            {/* Privacy disclosure */}
            <p className="text-xs text-neutral-500 leading-relaxed mb-0 sm:mb-6" data-id="element-10723" data-mp="2jyofit9ri8f1">
              Slack will share your name, email address, profile image, user ID,
              and team ID with Giga. View Slack's{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-10724" data-mp="mz5yt82fv6tk">
                privacy policy
              </a>
              . By agreeing to allow Slack to share your identity, you are also
              agreeing to Giga's{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-10725" data-mp="d8b9futwbr3w">
                privacy agreement
              </a>{' '}
              and{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-10726" data-mp="4b0unzhvdju8">
                terms of service
              </a>
              .
            </p>
          </div>

          <div className="p-6 border-t border-neutral-100 sm:border-none sm:p-0 sm:pb-10 sm:px-10 flex flex-col-reverse sm:flex-row items-center sm:justify-center gap-3 bg-white sticky bottom-0 z-10" data-id="element-10727" data-mp="3qgxrypsb9wtc">
            <button onClick={() => setStep(1)} className="w-full sm:w-auto px-6 py-3.5 sm:py-3 rounded-xl border border-neutral-300 text-[#2B211A] font-bold hover:bg-neutral-50 transition-colors" data-id="element-10728" data-mp="1opkuglsoar7b">
              
              Cancel
            </button>
            <button onClick={() => {
            trackSignupSlackConnected();
            setStep(3);
          }} className="w-full sm:w-auto px-6 py-3.5 sm:py-3 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold transition-colors shadow-sm" data-id="element-10729" data-mp="34alm8t77xvij">

              Accept and Continue
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 3: Slack Permissions
  if (step === 3) {
    const viewItems = [{
      icon: <MessageSquare size={18} className="text-neutral-500" data-id="element-10730" data-mp="5rohqu1zvoas" />,
      label: 'Content and info about channels & conversations'
    }, {
      icon: <LayoutGrid size={18} className="text-neutral-500" data-id="element-10731" data-mp="slcp9m0li2t" />,
      label: 'Content and info about your workspace'
    }];
    const actionItems = [{
      icon: <MessageSquare size={18} className="text-neutral-500" data-id="element-10732" data-mp="22joqg7r7bcrl" />,
      label: 'Perform actions in channels & conversations'
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-10733" data-mp="1gphpjd8va631">
        
        {/* Thin top bar */}
        <div className="h-2 w-full bg-[#2B211A]" data-id="element-10734" data-mp="1qguf1y770k5w" />

        {/* Slack header */}
        <header className="w-full border-b border-neutral-200 px-6 sm:px-10 py-5" data-id="element-10735" data-mp="1vduc5jm2eckv">
          <div className="flex items-center gap-2" data-id="element-10736" data-mp="xsgh1ig3tnuc">
            <SlackIcon size={28} data-id="element-10737" data-mp="xtrlzllad1n1" />
            <span className="text-[22px] font-bold text-[#333333] tracking-tight" data-id="element-10738" data-mp="12pooop3mm9hc">
              slack
            </span>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 w-full max-w-5xl mx-auto px-6 sm:px-10 py-8 sm:py-16 pb-32 sm:pb-16" data-id="element-10739" data-mp="1coo0uhnlon93">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16" data-id="element-10740" data-mp="2j4q7gqpqbpwv">
            {/* Left column */}
            <div data-id="element-10741" data-mp="2o3bbl183qtql">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm mb-5" data-id="element-10742" data-mp="2o2rshh6ptzaz">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 sm:w-8 sm:h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-10743" data-mp="cfo9g4acd2in" />
                
              </div>

              <h1 className="text-2xl sm:text-[32px] leading-tight font-title font-bold text-[#333333] mb-4" data-id="element-10744" data-mp="3twpei9he00gl">
                Allow the "Giga" app to access Slack
              </h1>

              <div className="flex items-center gap-2 text-[#2B211A] font-bold text-sm sm:text-[15px] mb-2" data-id="element-10745" data-mp="3kuptz775ye8w">
                <CheckCircle size={18} className="text-[#2B211A]" data-id="element-10746" data-mp="3fxg8u3qt4oqv" />
                App is approved by Slack
              </div>
              <p className="text-sm sm:text-[15px] text-neutral-500 leading-relaxed max-w-md mb-8" data-id="element-10747" data-mp="1e5tdjpzvu7ol">
                Apps are reviewed for quality before they are listed in the
                Slack Marketplace.{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-10748" data-mp="sb7qpauzy9md">
                  Learn more about app reviews.
                </a>
              </p>

              <p className="text-sm sm:text-[15px] font-bold text-[#333333] mb-2" data-id="element-10749" data-mp="waq4lmoehkfp">
                Workspace
              </p>
              <button type="button" className="w-full max-w-md flex items-center justify-between gap-3 px-4 py-3.5 rounded-lg border border-neutral-300 bg-white hover:border-neutral-400 transition-colors text-left" data-id="element-10750" data-mp="16ap998hp8qna">
                
                <div className="flex items-center gap-3 min-w-0" data-id="element-10751" data-mp="dn38s93rq24n">
                  <div className="w-7 h-7 rounded-md bg-[#4A90D9] flex items-center justify-center text-white text-[12px] font-bold shrink-0" data-id="element-10752" data-mp="il4nyiy2phau">
                    T
                  </div>
                  <span className="font-bold text-[#333333] text-[16px] truncate" data-id="element-10753" data-mp="ndo15esumaf4">
                    Tonic
                  </span>
                </div>
                <ChevronDown size={18} className="text-neutral-500 shrink-0" data-id="element-10754" data-mp="3qzn314mflx3y" />
              </button>
            </div>

            {/* Right column */}
            <div data-id="element-10755" data-mp="3eicakqgi1z3f">
              <h2 className="text-lg sm:text-[20px] font-bold text-[#333333] mb-4 sm:mb-5" data-id="element-10756" data-mp="3ehsw5jq25su5">
                Review app permissions
              </h2>

              <div className="rounded-2xl bg-[#F8F8F8] border border-neutral-200/70 p-5 sm:p-6 space-y-6 sm:space-y-7" data-id="element-10757" data-mp="39hemxycq0cuj">
                <div data-id="element-10758" data-mp="30ikqq93axohi">
                  <p className="text-sm font-bold text-[#333333] mb-3" data-id="element-10759" data-mp="30j2xhznlbl3o">
                    Information "Giga" can view
                  </p>
                  <div className="divide-y divide-neutral-200/70" data-id="element-10760" data-mp="2vq9ogzl39qls">
                    {viewItems.map((item, i) => <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 first:pt-0" data-id="element-10761" data-mp="2qt9j6qw2crvq" data-mpi={i}>
                      
                        <span className="shrink-0" data-id="element-10762" data-mp="2lru0h5hcyxgi">{item.icon}</span>
                        <span className="flex-1 text-sm sm:text-[15px] text-[#333333] leading-snug" data-id="element-10763" data-mp="2gu29qesg9hdw">
                          {item.label}
                        </span>
                        <ChevronRight size={16} className="text-neutral-400 shrink-0" data-id="element-10764" data-mp="227lly1il43h4" />
                      
                      </div>)}
                  </div>
                </div>

                <div data-id="element-10765" data-mp="1t7q8fq1k3wdx">
                  <p className="text-sm font-bold text-[#333333] mb-3" data-id="element-10766" data-mp="1t88c37qjtcvv">
                    Actions "Giga" can take
                  </p>
                  <div className="divide-y divide-neutral-200/70" data-id="element-10767" data-mp="1y8dyo9hyo973">
                    {actionItems.map((item, i) => <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 first:pt-0" data-id="element-10768" data-mp="2lxytbpnh8uw7" data-mpi={i}>
                      
                        <span className="shrink-0" data-id="element-10769" data-mp="2c6auv4si6dl8">{item.icon}</span>
                        <span className="flex-1 text-sm sm:text-[15px] text-[#333333] leading-snug" data-id="element-10770" data-mp="2c3yhz61lqtf1">
                          {item.label}
                        </span>
                        <ChevronRight size={16} className="text-neutral-400 shrink-0" data-id="element-10771" data-mp="34tydiea7qu58" />
                      
                      </div>)}
                  </div>
                </div>
              </div>

              <p className="text-xs sm:text-[13px] text-neutral-500 leading-relaxed mt-5" data-id="element-10772" data-mp="3iyxiuv245582">
                Slack will share your granted permissions with Giga. View
                Slack's{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-10773" data-mp="3soqm98s3qe18">
                  privacy policy
                </a>
                . By agreeing to allow Slack to share your granted permissions,
                you are also agreeing to Giga's{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-10774" data-mp="2w02fj8lu6d74">
                  privacy agreement
                </a>{' '}
                and{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-10775" data-mp="3a0vd627fpoeg">
                  terms of service
                </a>
                . Please review Giga's terms before installing.
              </p>

              <div className="fixed sm:static bottom-0 inset-x-0 p-4 sm:p-0 bg-white sm:bg-transparent border-t border-neutral-200 sm:border-none flex flex-col-reverse sm:flex-row items-center sm:justify-end gap-3 mt-0 sm:mt-6 z-10" data-id="element-10776" data-mp="pbfsloyt6vx9">
                <button onClick={() => setStep(2)} className="w-full sm:w-auto px-6 py-3.5 sm:py-2.5 rounded-xl sm:rounded-lg border border-neutral-300 text-[#333333] text-[15px] font-bold hover:bg-neutral-50 transition-colors" data-id="element-10777" data-mp="2uxdjbvwg4jmi">
                  
                  Cancel
                </button>
                <button onClick={() => setStep(4)} className="w-full sm:w-auto px-7 py-3.5 sm:py-2.5 rounded-xl sm:rounded-lg bg-[#007A5A] hover:bg-[#148567] text-white text-[15px] font-bold transition-colors shadow-sm" data-id="element-10778" data-mp="38xo5ilmt9a1c">
                  
                  Allow
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 4: Channel Selection
  if (step === 4) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-10779" data-mp="3rp7a2wwhgw5h">
        
        <GigaHeader data-id="element-10780" data-mp="1l0ostnv0er3p" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-10781" data-mp="1l16wh5k047ln">
          <div className="w-full max-w-md" data-id="element-10782" data-mp="1fzhk9ml1lnvz">
            <div className="mb-7 sm:mb-10" data-id="element-10783" data-mp="1g2n7sxe0whpl">
              <div className="mb-2.5" data-id="element-10784" data-mp="1by5thdqfmwm7">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-10785" data-mp="1bz7f2c52r1i6">
                  STEP 1 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-10786" data-mp="23ott2lcgjtxr">
                Which{' '}
                <span className="inline-flex items-center align-middle -mt-1" data-id="element-10787" data-mp="1zqmkids5dyxn">
                  <SlackIcon size={24} data-id="element-10788" data-mp="1uw8jnvupuzll" />
                </span>{' '}
                channels should Giga join?
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-10789" data-mp="1pwvxzefkzykl">
                We found 10 public channels in your workspace
              </p>
            </div>

            <div className="space-y-3 mb-6" data-id="element-10790" data-mp="2ilb235x6cwh5">
              <button onClick={() => setStep(5)} className="w-full py-3.5 px-6 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors" data-id="element-10791" data-mp="31j4klsyistk8">
                
                Invite Giga to public channels only
              </button>
              <button onClick={() => setStep(12)} className="w-full py-3.5 px-6 rounded-xl border-2 border-neutral-200 text-[#333333] font-medium hover:border-[#EA580C] hover:bg-[#FAF8F4]/30 transition-all" data-id="element-10792" data-mp="2sp8ajcimr7l6">
                
                Select channels
              </button>
            </div>

            <div className="flex items-center gap-2.5 text-[13px] text-neutral-500 bg-neutral-50 rounded-xl px-4 py-3" data-id="element-10793" data-mp="2nq52rkb9mn9i">
              <Info size={16} className="shrink-0" data-id="element-10794" data-mp="2du22yl3vz0pq" />
              <p className="[text-wrap:balance]" data-id="element-10795" data-mp="2duto11zuxg1r">
                You can remove Giga from any channel anytime.
              </p>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 12: Specific Channel Selection
  if (step === 12) {
    const DUMMY_CHANNELS = ['all-faith-test', 'new-channel', 'social'];
    // Auto-select all by default to match the screenshot's "3 selected" state
    // if the user hasn't interacted yet.
    const activeChannels = selectedChannels.length > 0 || !selectedChannels.includes('initialized') ? selectedChannels.includes('initialized') ? selectedChannels.filter(c => c !== 'initialized') : DUMMY_CHANNELS : [];
    const toggleChannel = (ch: string) => {
      let next = [...activeChannels];
      if (next.includes(ch)) {
        next = next.filter(c => c !== ch);
      } else {
        next.push(ch);
      }
      // Add a hidden flag so we know the user has interacted and we shouldn't auto-fill
      if (!next.includes('initialized')) next.push('initialized');
      setSelectedChannels(next);
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-10796" data-mp="uukn4fctv6jq">
        
        <GigaHeader data-id="element-10797" data-mp="514t8i7a260o" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-10798" data-mp="4zk1f0ccl16i">
          <div className="w-full max-w-md" data-id="element-10799" data-mp="91oofg8aizk6">
            <div className="mb-7 sm:mb-10" data-id="element-10800" data-mp="90dndo68c5wr">
              <div className="mb-2.5" data-id="element-10801" data-mp="dt6toghcjmay">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-10802" data-mp="du86oqhxaivb">
                  STEP 1 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-10803" data-mp="3i7wv4b1gf5ru">
                Which{' '}
                <span className="inline-flex items-center align-middle -mt-1" data-id="element-10804" data-mp="3m98isb0rpgxe">
                  <SlackIcon size={24} data-id="element-10805" data-mp="3r71qx9lmsl0b" />
                </span>{' '}
                channels should Giga join?
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-10806" data-mp="3w4tho0aji12x">
                We found 10 public channels in your workspace
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden mb-4" data-id="element-10807" data-mp="1bf42tn9u6fa8">
              {/* Search */}
              <div className="relative border-b border-neutral-200" data-id="element-10808" data-mp="2qzmuuo2ign8s">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-10809" data-mp="2r0er813xmp04" />
                
                <input type="text" placeholder={`Search ${WORKSPACE_NAME} workspace`} className="w-full pl-11 pr-4 py-4 text-[15px] text-[#2B211A] placeholder:text-neutral-400 focus:outline-none bg-transparent" data-id="element-10810" data-mp="2m6k0idpwt9rj" />
                
              </div>

              {/* List */}
              <div className="bg-[#FAF8F4]/50 p-2" data-id="element-10811" data-mp="287u3ey33ba36">
                <div className="px-3 py-2 text-sm font-medium text-[#2B211A]" data-id="element-10812" data-mp="239iv4rsg17n2">
                  {activeChannels.length} selected
                </div>
                <div className="flex flex-col gap-1 pb-2" data-id="element-10813" data-mp="1yd25tu9icn8e">
                  {DUMMY_CHANNELS.map(ch => {
                  const isSelected = activeChannels.includes(ch);
                  return <button key={ch} onClick={() => toggleChannel(ch)} className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg hover:bg-white transition-colors group" data-id="element-10814" data-mp="3spi8kl33hj8i">
                        
                        <div className="flex items-center gap-3 text-[15px] text-[#2B211A]" data-id="element-10815" data-mp="janh6pf4j8nf">
                          <Hash size={16} className="text-neutral-400" data-id="element-10816" data-mp="xz1q9i34vtcf" />
                          {ch}
                        </div>
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C] text-white' : 'border-neutral-300 text-transparent group-hover:border-neutral-400'}`} data-id="element-10817" data-mp="5b5ymrsf8rmi">
                          
                          <Check size={12} strokeWidth={3} data-id="element-10818" data-mp="f78zzvfg8m8e" />
                        </div>
                      </button>;
                })}
                </div>
              </div>
            </div>

            <button onClick={() => setStep(5)} disabled={activeChannels.length === 0} className="w-full py-4 px-6 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4 shadow-sm" data-id="element-10819" data-mp="1qqldre6rjtk8">
              
              Invite Giga to {activeChannels.length} channels
            </button>

            <div className="flex items-center justify-center gap-2 text-[13px] text-neutral-500" data-id="element-10820" data-mp="1k9a4cb9ctwfs">
              <Info size={14} data-id="element-10821" data-mp="1oavcjjay1mjn" />
              <p data-id="element-10822" data-mp="1oa3tf7yj9ha9">You can remove Giga from any channel anytime</p>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 5: Tell us about you
  if (step === 5) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-10823" data-mp="2gxzpqbk6x76i">
        
        <GigaHeader data-id="element-10824" data-mp="277dcgnzy4sqz" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-10825" data-mp="276lh9ea9gx18">
          <div className="w-full max-w-md" data-id="element-10826" data-mp="2uwpkvlng8ws8">
            <div className="mb-7 sm:mb-10" data-id="element-10827" data-mp="2zoz7arpcvepr">
              <div className="mb-2.5" data-id="element-10828" data-mp="2zpshy3rfqod6">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-10829" data-mp="2zqtu6bk6tgwh">
                  STEP 2 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-10830" data-mp="35k5ood33p20f">
                Tell us more about you
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-10831" data-mp="2vlzn5wl7hxw3">
                A couple quick questions so Giga can hit the ground running.
              </p>
            </div>

            <div className="space-y-6 mb-8" data-id="element-10832" data-mp="2lv50ws7ymyan">
              <div data-id="element-10833" data-mp="2lu3mcdrqjto6">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-10834" data-mp="2htt86dtshwy8">
                  What best describes you?
                </label>
                <SearchableSelect value={role} onChange={setRole} searchable={false} options={['I am a founder or executive and wear many hats at my company', 'I am a real estate agent or real estate broker', 'I am a consultant or work with many different clients', 'I work in sales or rev ops', 'I work on a marketing or growth team', 'I work in finance or operations', 'I am a content creator or social media influencer', 'I am a product manager or work with an engineering team', 'Other']} placeholder="Select an option" ariaLabel="What best describes you?" data-id="element-10835" data-mp="2cw03jp84xb02" />
                
              </div>

              <div data-id="element-10836" data-mp="27ojwheiw3ar2">
                <label className="block text-sm font-medium text-[#333333] mb-3" data-id="element-10837" data-mp="27p3d8rwszt3i">
                  What kind of business is it?
                </label>
                <SearchableSelect value={businessType} onChange={setBusinessType} options={BUSINESS_TYPES} placeholder="Select an option" searchPlaceholder="Search business types…" ariaLabel="What kind of business is it?" data-id="element-10838" data-mp="1f0nym6hl3csp" />
                
              </div>

              <div data-id="element-10839" data-mp="1osts5knhi2a0">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-10840" data-mp="rbv145lbcwvz">
                  What would Giga help you with? Select from use cases below:
                </label>
                <MultiSelectDropdown values={firstTeammates} onChange={toggleFirstTeammate} options={['Marketing & Ad Campaigns', 'Operations & Workflow Automation', 'Data analysis & reporting', 'Sales & CRM', 'Content & Creative Production', 'Engineering & Development', 'Executive / Leadership Assistant', 'Agency/Consultancy & Client Operations', 'eCommerce & Shopify', 'Other']} placeholder="Select use cases" data-id="element-10841" data-mp="w8d4b10zwn67" />
                
              </div>

              <div data-id="element-10842" data-mp="3gp20xtxssv6d">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-10843" data-mp="3gojwia0zfand">
                  How did you find us?{' '}
                  <span className="text-neutral-400 font-normal" data-id="element-10844" data-mp="3qoj4a4pkuwz6">
                    (optional)
                  </span>
                </label>
                <div className="relative" data-id="element-10845" data-mp="3lqhnjqfmj9qv">
                  <select value={foundUs} onChange={e => setFoundUs(e.target.value)} className={`w-full px-4 py-3 rounded-xl border border-neutral-200 appearance-none bg-white focus:outline-none focus:border-[#EA580C] transition-colors ${foundUs ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-10846" data-mp="3lnn2pxsp7mwx">
                    
                    <option value="" disabled data-id="element-10847" data-mp="bshe7kd5115q">
                      Select an option
                    </option>
                    <option data-id="element-10848" data-mp="3t9iivog9tx35">Google search</option>
                    <option data-id="element-10849" data-mp="3t8p8mdi3spg9">Slack Marketplace</option>
                    <option data-id="element-10850" data-mp="225x0w4g0vgw">Community</option>
                    <option data-id="element-10851" data-mp="20uxx9ly0bw4">Podcast</option>
                    <option data-id="element-10852" data-mp="3k8kev65u1pkk">Reddit</option>
                    <option data-id="element-10853" data-mp="3k6pqtiz9lhc9">Product Hunt</option>
                    <option data-id="element-10854" data-mp="3p740t6kff1cx">Facebook</option>
                    <option data-id="element-10855" data-mp="3p85ezjwqo3yv">YouTube</option>
                    <option data-id="element-10856" data-mp="3aa4lewjc247w">Twitter / X</option>
                    <option data-id="element-10857" data-mp="3abeknp1x9ig4">Newsletter</option>
                    <option data-id="element-10858" data-mp="3f97q2fvei8fc">Other</option>
                  </select>
                  <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" data-id="element-10859" data-mp="3f9g44b39fjs4" />
                  
                </div>
              </div>

              <div data-id="element-10860" data-mp="1dhb56fncfm7w">
                <label className="block text-sm font-medium text-[#333333] mb-3" data-id="element-10861" data-mp="1dfqitdb6n9l4">
                  Keep me posted with Giga tips and updates
                </label>
                <div className="flex items-center gap-4" data-id="element-10862" data-mp="170t6eos0kyqv">
                  <button onClick={() => setEmailOptIn(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${emailOptIn ? 'text-[#EA580C]' : 'text-neutral-500 hover:text-[#333333]'}`} data-id="element-10863" data-mp="12281wd5qbswm">
                    
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${emailOptIn ? 'border-[#EA580C] bg-[#EA580C]' : 'border-neutral-300'}`} data-id="element-10864" data-mp="1upu6noqp13l2">
                      
                      {emailOptIn && <div className="w-2 h-2 bg-white rounded-full" data-id="element-10865" data-mp="1kz98u3iug337"></div>}
                    </div>
                    <span className="text-sm font-medium" data-id="element-10866" data-mp="1gxo1exp2wh0e">Of course!</span>
                  </button>
                  <button onClick={() => setEmailOptIn(false)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${!emailOptIn ? 'text-[#EA580C]' : 'text-neutral-500 hover:text-[#333333]'}`} data-id="element-10867" data-mp="2djra6jy62m21">
                    
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${!emailOptIn ? 'border-[#EA580C] bg-[#EA580C]' : 'border-neutral-300'}`} data-id="element-10868" data-mp="6vemyoykzcx">
                      
                      {!emailOptIn && <div className="w-2 h-2 bg-white rounded-full" data-id="element-10869" data-mp="99mr91tkm165"></div>}
                    </div>
                    <span className="text-sm font-medium" data-id="element-10870" data-mp="e6mvd76v0tum">No thanks</span>
                  </button>
                </div>
              </div>
            </div>

            <button onClick={() => {
            trackSignupProfileCompleted(role, businessType);
            setStep(6);
          }} disabled={!role || !businessType || firstTeammates.length === 0} className={`w-full py-4 px-6 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${role && businessType && firstTeammates.length > 0 ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-neutral-300 text-white cursor-not-allowed'}`} data-id="element-10871" data-mp="nwfwtfdafsl5">
              
              Continue
              <ArrowRight size={18} data-id="element-10872" data-mp="7v0anlpe4lqu" />
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 6: Tool Selection
  if (step === 6) {
    const CATEGORIES: {
      key: string;
      label: string;
    }[] = [{
      key: 'communication',
      label: 'COMMUNICATION'
    }, {
      key: 'docs',
      label: 'DOCS & DRIVE'
    }, {
      key: 'engineering',
      label: 'ENGINEERING'
    }, {
      key: 'project',
      label: 'PROJECT MANAGEMENT'
    }, {
      key: 'crm',
      label: 'CRM & SALES'
    }, {
      key: 'support',
      label: 'SUPPORT'
    }, {
      key: 'finance',
      label: 'FINANCE'
    }, {
      key: 'design',
      label: 'DESIGN'
    }, {
      key: 'analytics',
      label: 'ANALYTICS'
    }, {
      key: 'marketing',
      label: 'MARKETING'
    }];
    const filteredTools = ALL_TOOLS.filter(t => t.name.toLowerCase().includes(toolSearch.toLowerCase()));
    const POPULAR_TOOL_IDS = ['slack', 'gmail', 'googledrive', 'notion', 'github', 'linear', 'jira', 'salesforce', 'hubspot', 'stripe'];
    const popularTools = POPULAR_TOOL_IDS.map(id => ALL_TOOLS.find(t => t.id === id)).filter(Boolean) as typeof ALL_TOOLS;
    const isSearching = toolSearch.trim() !== '';
    const renderToolCard = (tool: (typeof ALL_TOOLS)[number]) => {
      const isSelected = selectedTools.includes(tool.id);
      return <button key={tool.id} onClick={() => toggleTool(tool.id)} aria-pressed={isSelected} className={`group relative p-3 rounded-2xl border transition-all duration-200 ease-out active:scale-[0.97] ${isSelected ? 'border-[#F8C9A4] bg-[#FFF7ED] shadow-[0_1px_3px_rgba(234,88,12,0.08)]' : 'border-neutral-200/80 bg-white hover:border-[#F3D2BB] hover:bg-[#FFFBF7]'}`} data-id="element-10873" data-mp="3b3djpoqkduyd">
          
          <AnimatePresence data-id="element-10874" data-mp="1pqk7goo64kcj">
            {isSelected && <motion.div initial={{
            opacity: 0,
            scale: 0.5
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.5
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} className="absolute top-1.5 right-1.5 w-[18px] h-[18px] bg-[#EA580C] rounded-full flex items-center justify-center shadow-[0_1px_2px_rgba(234,88,12,0.3)]" data-id="element-10875" data-mp="1ur6vi7h6vfpz">
              
                <Check size={11} strokeWidth={2.25} className="text-white" data-id="element-10876" data-mp="bvgzmtgrbju4" />
              </motion.div>}
          </AnimatePresence>
          <ToolLogo name={tool.name} icon={tool.icon} className="w-8 h-8 mx-auto mb-1.5 transition-transform duration-200 group-hover:scale-105" fallbackTextSize="text-sm" data-id="element-10877" data-mp="gnh674rc1azo" />
          
          <div className="text-xs font-medium text-[#333333] text-center leading-tight" data-id="element-10878" data-mp="6w4gaip3guwa">
            {tool.name}
          </div>
        </button>;
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-10879" data-mp="3p4v5yd2j2iza">
        
        <GigaHeader hideOnMobile data-id="element-10880" data-mp="3ahwguor580nq" />

        <div className="flex-1 px-6 pt-6 lg:pt-12 pb-36 overflow-auto" data-id="element-10881" data-mp="35h8re7ikbrvm">
          <div className="max-w-4xl mx-auto" data-id="element-10882" data-mp="35j3ffup4s03x">
            <div className="mb-2.5" data-id="element-10883" data-mp="3kf14kt0n046n">
              <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-10884" data-mp="3kbwszakue65y">
                STEP 3 OF 4
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-10885" data-mp="3tdd4jlne8ebr">
              Where does your work actually live?
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-10886" data-mp="6zmpw5yymodx">
              GitHub, Linear, the docs you reference — pick everything. Giga
              reads across them with citations back to source.
            </p>

            <div className="relative mb-6 sm:mb-8" data-id="element-10887" data-mp="g01b75bxc3hf">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={20} data-id="element-10888" data-mp="bypszkvdqkj9" />
              
              <input type="text" placeholder="Search tools..." value={toolSearch} onChange={e => setToolSearch(e.target.value)} className="w-full pl-12 pr-4 py-3 sm:py-3.5 rounded-xl border border-neutral-200 text-[#333333] bg-white focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/15 transition-all" data-id="element-10889" data-mp="j2fond18v0zb" />
              
            </div>

            <div className="relative inline-block mb-7 sm:mb-10" data-id="element-10890" data-mp="11s4bsjyutd44">
              <button onClick={() => setRoleMenuOpen(!roleMenuOpen)} aria-expanded={roleMenuOpen} aria-label={`Suggested for ${funcRoles.join(', ')}. Change use cases`} className="group inline-flex items-center gap-2 pl-3.5 pr-3 py-2 rounded-full border border-neutral-200 bg-white text-sm shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:bg-neutral-50 hover:border-neutral-300 transition-colors" data-id="element-10891" data-mp="11tynd7o8jsvj">
                
                <Sparkles size={14} className="text-neutral-400 shrink-0" data-id="element-10892" data-mp="3djri7wdhv3zi" />
                <span className="text-neutral-500" data-id="element-10893" data-mp="3ih1dhsnke9v9">Suggested for</span>
                <span className="font-semibold text-[#333333] max-w-[200px] truncate" data-id="element-10894" data-mp="1zmf3brkx2cql">
                  {funcRoles.length === 0 ? 'Select use cases' : funcRoles.length <= 1 ? funcRoles[0] : `${funcRoles.length} use cases`}
                </span>
                <ChevronDown size={15} className={`text-neutral-400 transition-transform ${roleMenuOpen ? 'rotate-180' : ''}`} data-id="element-10895" data-mp="1qqyyygmjcf6r" />
                
              </button>

              <AnimatePresence data-id="element-10896" data-mp="2iiy1xmy3gi8w">
                {roleMenuOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setRoleMenuOpen(false)} data-id="element-10897" data-mp="2eie7ws8thuqz" />
                  
                    <motion.div initial={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} transition={{
                  duration: 0.15
                }} className="absolute left-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-neutral-200 py-2 z-50 max-h-80 overflow-y-auto" data-id="element-10898" data-mp="29ii0ej0wvl6o">
                    
                      {Object.keys(USE_CASE_RECS).map(r => {
                    const isSelected = funcRoles.includes(r);
                    return <button key={r} onClick={e => {
                      e.preventDefault();
                      setFuncRoles(prev => {
                        const next = isSelected ? prev.filter(x => x !== r) : [...prev, r];
                        const newTools = new Set<string>();
                        next.forEach(role => {
                          USE_CASE_RECS[role]?.forEach(t => newTools.add(t));
                        });
                        setSelectedTools(Array.from(newTools));
                        return next;
                      });
                    }} className={`w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5] text-[#333333] font-medium' : 'hover:bg-neutral-50 text-neutral-600'}`} data-id="element-10899" data-mp="2d0npq5nmhlid">
                          
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C]' : 'border-neutral-300 bg-white'}`} data-id="element-10900" data-mp="17vh0kms4fvl2">
                            
                              {isSelected && <Check size={12} strokeWidth={3} className="text-white" data-id="element-10901" data-mp="t51fd7zpgnmw" />}
                            </div>
                            <span className="flex-1 leading-snug" data-id="element-10902" data-mp="p4hhu3b7zi08">{r}</span>
                          </button>;
                  })}
                    </motion.div>
                  </>}
              </AnimatePresence>
            </div>

            {!isSearching && popularTools.length > 0 && <div className="mb-5 sm:mb-6" data-id="element-10903" data-mp="acp6e2ejqo3s">
                <h2 className="flex items-center gap-1.5 text-[11px] sm:text-xs font-bold text-[#EA580C] uppercase tracking-wider mb-2.5 sm:mb-3" data-id="element-10904" data-mp="abe11y5gd8ak">
                  <Sparkles size={13} className="shrink-0" data-id="element-10905" data-mp="25fvm90n1i4tj" />
                  Most popular
                </h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-id="element-10906" data-mp="2aeow8j34bdoy">
                  {popularTools.map(tool => renderToolCard(tool))}
                </div>
              </div>}

            {CATEGORIES.map(cat => {
            const items = filteredTools.filter(t => t.category === cat.key);
            if (items.length === 0) return null;
            return <div key={cat.key} className="mb-5 sm:mb-6" data-id="element-10907" data-mp="1rgm4vxn3l4nz">
                  <h2 className="text-[11px] sm:text-xs font-bold text-neutral-400 uppercase tracking-wider mb-2.5 sm:mb-3" data-id="element-10908" data-mp="1rj8dm0lq5q7s">
                    {cat.label}
                  </h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-id="element-10909" data-mp="20j55eb3ev572">
                    {items.map(tool => renderToolCard(tool))}
                  </div>
                </div>;
          })}

            {filteredTools.length === 0 && (() => {
            const term = toolSearch.trim();
            const requested = term !== '' && requestedTools.includes(term.toLowerCase());
            return <div className="text-center py-12 px-6 rounded-2xl border-2 border-dashed border-neutral-200 bg-[#FAF8F4]" data-id="element-10910" data-mp="w8twyglcg7k1">
                    {requested ? <div className="max-w-md mx-auto" data-id="element-10911" data-mp="mih29vjtey06">
                        <div className="w-12 h-12 rounded-full bg-[#FFEDD5] flex items-center justify-center mx-auto mb-4" data-id="element-10912" data-mp="mhz0kjedvrkv">
                          <Check size={22} className="text-[#EA580C]" data-id="element-10913" data-mp="ck1bxusm3srq" />
                        </div>
                        <h3 className="font-bold text-[#333333] text-lg mb-1" data-id="element-10914" data-mp="8ig4ioyuk6ox">
                          On it
                        </h3>
                        <p className="text-neutral-600 text-sm" data-id="element-10915" data-mp="3kemm7d5qdf1">
                          We&rsquo;ve got your request for{' '}
                          <span className="font-semibold" data-id="element-10916" data-mp="22vpfkl71k09a">{term}</span>
                          {requestedToolLink ? ' and the link' : ''}. If it has
                          an API or a login page, our team usually wires it up{' '}
                          <span className="font-semibold text-[#333333]" data-id="element-10917" data-mp="1bqv7vjo4hlms">
                            within 1 hour
                          </span>{' '}
                          — we&rsquo;ll email you the moment it&rsquo;s live.
                        </p>
                      </div> : <div className="max-w-xl mx-auto text-left" data-id="element-10918" data-mp="11zh4h0t1w1od">
                        <h3 className="font-bold text-[#333333] text-lg mb-1 text-center" data-id="element-10919" data-mp="o0hareqokwlc">
                          {term ? `Don't see ${term}?` : 'Looking for something specific?'}
                        </h3>
                        <p className="text-neutral-600 text-sm mb-5 text-center" data-id="element-10920" data-mp="e4o0k20fpf6q">
                          Giga connects to virtually anything. Tell us what you
                          need — drop a link and we&rsquo;ll usually have it
                          connected within 1 hour.
                        </p>
                        <label className="block text-[13px] font-semibold text-[#333333] mb-1.5" data-id="element-10921" data-mp="2dfp52wox92xe">
                          Link{' '}
                          <span className="font-normal text-neutral-400" data-id="element-10922" data-mp="3szoiapn8t2m5">
                            (optional)
                          </span>
                        </label>
                        <input type="url" value={requestedToolLink} onChange={e => setRequestedToolLink(e.target.value)} placeholder="https://yourtool.com" className="w-full mb-4 px-4 py-2.5 rounded-xl border border-neutral-200 bg-white text-sm text-[#333333] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#333333]/20 focus:border-[#333333]" data-id="element-10923" data-mp="bkrgr1a6p1ny" />
                    
                        <button onClick={() => term && setRequestedTools(prev => [...prev, term.toLowerCase()])} disabled={!term} className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold transition-colors ${term ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'}`} data-id="element-10924" data-mp="ufpxa6i8hgpa">
                      
                          {term ? `Request ${term}` : 'Type a name to request it'}
                        </button>
                        <p className="flex items-start justify-center gap-1.5 text-xs text-neutral-400 leading-relaxed text-center mt-4" data-id="element-10925" data-mp="kvujlqblfxd1">
                          <Info size={13} className="shrink-0 mt-0.5" data-id="element-10926" data-mp="c30vd3e65nn1" />
                          <span data-id="element-10927" data-mp="c0y46g1a5ogq">
                            Connect anything later from Settings — API token,
                            MCP server, or OAuth.
                          </span>
                        </p>
                      </div>}
                  </div>;
          })()}

            <StickyRevealBar data-id="element-10928" data-mp="3tb5wuokuhuco">
              <div className="flex items-center justify-between gap-3" data-id="element-10929" data-mp="3p9aoj04hygpe">
                <button onClick={() => setStep(5)} aria-label="Back" className="flex items-center justify-center w-11 h-11 rounded-full text-neutral-500 hover:text-[#333333] hover:bg-neutral-100 transition-colors shrink-0" data-id="element-10930" data-mp="3pbdmc6bw5e4o">
                  
                  <ArrowLeft size={20} data-id="element-10931" data-mp="35g2mkf8n19il" />
                </button>
                <div className="flex items-center gap-2 sm:gap-4" data-id="element-10932" data-mp="35dq5036sl162">
                  <button onClick={() => setStep(7)} className="px-2 sm:px-4 py-3 text-sm font-medium text-neutral-500 hover:text-[#333333] transition-colors whitespace-nowrap" data-id="element-10933" data-mp="3ad2r2lpua47l">
                    
                    Later
                  </button>
                  <button onClick={() => setStep(7)} aria-label={selectedTools.length > 0 ? `Continue with ${selectedTools.length} tools` : 'Continue'} className="px-5 sm:px-7 py-3 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors flex items-center gap-2 whitespace-nowrap" data-id="element-10934" data-mp="2mgycd35d19pc">
                    
                    Continue
                    {selectedTools.length > 0 && <span className="hidden sm:inline text-sm" data-id="element-10935" data-mp="30e3j75cwecln">
                        with {selectedTools.length}{' '}
                        {selectedTools.length === 1 ? 'tool' : 'tools'}
                      </span>}
                    <ArrowRight size={18} strokeWidth={2} data-id="element-10936" data-mp="2bsu6ysv2xh44" />
                  </button>
                </div>
              </div>
            </StickyRevealBar>
          </div>
        </div>
      </motion.div>;
  }
  // Step 7: Connections — per-tool OAuth permission flow
  if (step === 7) {
    const connections = step7Connections;
    const total = connections.length;
    const doneCount = connectedConns.length;
    const activeIdx = step7ActiveIdx;
    const allDone = step7AllDone;
    const allow = (conn: any) => {
      const next = connectedConns.includes(conn.id) ? connectedConns : [...connectedConns, conn.id];
      setConnectedConns(next);
      // Lock in the chosen visibility (default to shared with team if untouched)
      setConnVisibility(prev => ({
        ...prev,
        [conn.id]: prev[conn.id] ?? 'team'
      }));
      // Reset optional permission toggles for the next tool's fresh card
      setOptionalScopes({});
      if (next.length === total) {
        // All done — close the consent screen and advance.
        setConnectPhase(null);
        setPermModal(null);
        setTimeout(() => setStep(finalStep), 600);
      } else {
        // Visibility was set up front on the list, so once the user kicks off
        // the sequence we chain straight to the next tool's consent screen.
        const nextConn = connections.find(c => !next.includes(c.id));
        if (nextConn) {
          setPermModal(nextConn);
          setConnectPhase('consent');
        } else {
          setConnectPhase(null);
          setPermModal(null);
        }
      }
    };
    const handleAllowClick = (conn: any) => {
      setConnectPhase('handing-off');
      setTimeout(() => {
        allow(conn);
      }, 2000);
    };
    const startConnecting = () => {
      const firstConn = connections.find(c => !connectedConns.includes(c.id));
      if (!firstConn) return;
      setConnectionsStarted(true);
      setPermModal(firstConn);
      setConnectPhase('consent');
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-10937" data-mp="2zkkhdr8e9dsr">
        
        <GigaHeader hideOnMobile data-id="element-10938" data-mp="2pty80er9tjii" />

        <div className="flex-1 px-4 sm:px-6 py-6 sm:py-12 pb-40 overflow-auto" data-id="element-10939" data-mp="2pv9a8a52ij7i">
          <div className="max-w-4xl mx-auto" data-id="element-10940" data-mp="2lt5v9bj6wwhh">
            <div className="mb-2.5" data-id="element-10941" data-mp="2luh07eodgaa6">
              <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-10942" data-mp="3iimzdm2iyah2">
                STEP 4 OF 4
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-10943" data-mp="3dhpmnp0aa8my">
              Let's plug Giga into your stack.
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-10944" data-mp="34ic9omcyb1xh">
              Approve each one to connect. You can adjust permissions anytime in
              Settings.
            </p>

            <div className="bg-neutral-50 rounded-2xl p-4 sm:p-6 mb-6" data-id="element-10945" data-mp="1t2njzn8bbosz">
              <div className="flex items-center justify-between mb-4 sm:mb-5" data-id="element-10946" data-mp="8mz8du9pke9i">
                <h2 className="text-lg sm:text-xl md:text-2xl font-title font-bold text-[#333333] italic" data-id="element-10947" data-mp="dkhl7vqujdms">
                  {total} connections to make
                </h2>
                <span className="text-xs sm:text-sm font-medium text-neutral-500" data-id="element-10948" data-mp="mdb21xlxyl2r">
                  {doneCount}/{total} done
                </span>
              </div>

              {/* progress bar */}
              <div className="h-1.5 w-full rounded-full bg-neutral-200 mb-5 sm:mb-6 overflow-hidden" data-id="element-10949" data-mp="wbiga8ouip12">
                <motion.div className="h-full rounded-full bg-[#EA580C]" animate={{
                width: `${doneCount / total * 100}%`
              }} transition={{
                type: 'spring',
                stiffness: 120,
                damping: 20
              }} data-id="element-10950" data-mp="11av2qsbt1u34" />
                
              </div>

              <div className="space-y-2" data-id="element-10951" data-mp="3730jmie14kz5">
                {connections.map(conn => {
                const connected = connectedConns.includes(conn.id);
                return <div key={conn.id} className={`flex items-center gap-3 p-3 sm:p-3.5 rounded-xl bg-white border transition-all ${connected ? 'border-[#EA580C]' : 'border-neutral-200'}`} data-id="element-10952" data-mp="eawjmepplkq2">
                      
                      {/* Tool info */}
                      <ToolLogo name={conn.name} icon={conn.icon} className="w-9 h-9 shrink-0" fallbackTextSize="text-sm" data-id="element-10953" data-mp="j73e9bcnn9sp" />
                      
                      <div className="min-w-0 flex-1" data-id="element-10954" data-mp="x5kygz3cq45v">
                        <div className="font-bold text-sm sm:text-base text-[#333333] truncate" data-id="element-10955" data-mp="s81ls3maxzg1">
                          {conn.name}
                        </div>
                        <div className="text-xs text-neutral-500" data-id="element-10956" data-mp="11ywb5guuhf5p">
                          {conn.scopes.length} permission
                          {conn.scopes.length > 1 ? 's' : ''}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="shrink-0" data-id="element-10957" data-mp="1axhq7213yc1q">
                        {connected ? <span className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full bg-[#FFF0E6] text-xs font-semibold text-[#EA580C]" data-id="element-10958" data-mp="1ulnlwm2wfnid">
                            <span className="w-4 h-4 rounded-full bg-[#EA580C] flex items-center justify-center" data-id="element-10959" data-mp="1lkz5jkq19b2b">
                              <Check size={11} strokeWidth={3} className="text-white" data-id="element-10960" data-mp="1pl9khmvszbtb" />
                            
                            </span>
                            Connected
                          </span> : <span className="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border border-neutral-200 bg-white text-xs font-medium text-neutral-500" data-id="element-10961" data-mp="1glmiayv921z7">
                            <span className="w-2 h-2 rounded-full border-[1.5px] border-neutral-300" data-id="element-10962" data-mp="16v9zpc6zwivp" />
                            Pending
                          </span>}
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed bottom action bar — keeps the primary action reachable as the
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                         tool list scrolls. Hidden while a consent screen is open. */}
        {!permModal && <StickyRevealBar data-id="element-10963" data-mp="bmhl65vsq8z9">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3" data-id="element-10964" data-mp="bmpvpr4g527a">
              {allDone ? <button onClick={() => setStep(finalStep)} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#2B211A] text-white font-bold hover:bg-black transition-colors shrink-0" data-id="element-10965" data-mp="6qj1gvlexf56">
              
                  Continue
                  <ArrowRight size={18} data-id="element-10966" data-mp="2rdizfiedjwfd" />
                </button> : <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 shrink-0" data-id="element-10967" data-mp="2wbkjo6nje1sf">
                  <button onClick={() => setStep(finalStep)} className="w-full sm:w-auto px-4 py-3 rounded-xl text-sm font-semibold text-neutral-500 hover:text-[#333333] hover:bg-neutral-100 transition-colors" data-id="element-10968" data-mp="1zltoe57f8alk">
                
                    {doneCount > 0 ? 'Finish the rest later' : "I'll connect later"}
                  </button>
                  <button onClick={startConnecting} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#2B211A] text-white font-bold hover:bg-black transition-colors" data-id="element-10969" data-mp="2de9ksn5thadi">
                
                    {doneCount > 0 ? 'Resume connecting' : 'Start connecting'}
                    <ArrowRight size={18} data-id="element-10970" data-mp="z5geg6as4gb" />
                  </button>
                </div>}
            </div>
          </StickyRevealBar>}

        {/* OAuth permission modal / full-screen flow — sits OUTSIDE the page
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                         wrapper concerns; full-screen cover. */}
        <AnimatePresence data-id="element-10971" data-mp="30eajo2j27jlj">
          {permModal && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex flex-col items-center bg-white md:bg-[#F5F5F5] overflow-y-auto" data-id="element-10972" data-mp="2st6mmn7wdeop">
            
              {connectPhase === 'consent' && <div className="w-full h-full flex flex-col items-center md:pt-12 md:pb-24 md:px-6" data-id="element-10973" data-mp="2e4topkp91lrk">
                  {/* Fake URL Bar - Full width header on mobile, floating pill on desktop */}
                  <div className="flex items-center justify-between w-full md:max-w-md bg-neutral-100 md:bg-white md:rounded-full px-4 py-3 md:py-2 md:shadow-sm border-b md:border border-neutral-200 mb-0 md:mb-8 shrink-0" data-id="element-10974" data-mp="36s69pqfrhlir">
                    <div className="flex items-center gap-2 text-neutral-500" data-id="element-10975" data-mp="2wtqlpwc16sdh">
                      <Lock size={14} className="text-[#EA580C]" data-id="element-10976" data-mp="1qepx6bpc5cgx" />
                      <span className="font-mono text-xs truncate max-w-[200px] sm:max-w-none" data-id="element-10977" data-mp="1lfczsx0u8n03">
                        {permModal.id}.com{' '}
                        <span className="text-neutral-400" data-id="element-10978" data-mp="dgz3tu0h7trt">
                          /oauth/authorize
                        </span>
                      </span>
                    </div>
                    <span className="font-mono text-xs text-neutral-400 shrink-0" data-id="element-10979" data-mp="3ijd3s5d2miu">
                      {doneCount + 1} of {total}
                    </span>
                  </div>

                  {/* Consent Card - Full width on mobile, rounded card on desktop */}
                  <div className="w-full max-w-md bg-white md:rounded-3xl md:shadow-xl md:border border-neutral-200 flex flex-col min-h-screen md:min-h-0" data-id="element-10980" data-mp="3uo1dn5209u0h">
                    <div className="flex-1 overflow-auto" data-id="element-10981" data-mp="3gy7muh6xwzo4">
                      <div className="p-6 md:p-8 pb-6 flex flex-col items-center" data-id="element-10982" data-mp="3lv7sws3si2f8">
                        <div className="flex items-center justify-center gap-4 mb-6" data-id="element-10983" data-mp="36yqiq47c6tu9">
                          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl border border-neutral-200 flex items-center justify-center shadow-sm bg-white" data-id="element-10984" data-mp="3bxjwzyug6ovh">
                            <ToolLogo name={permModal.name} icon={permModal.icon} className="w-7 h-7 md:w-8 md:h-8" data-id="element-10985" data-mp="2sc9x9xky1s0n" />
                        
                          </div>
                          <ArrowRight size={16} className="text-neutral-300" data-id="element-10986" data-mp="32a7j6efbzcq3" />
                          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm" data-id="element-10987" data-mp="2xb4dcrrj12h2">
                            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 md:w-8 md:h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-10988" data-mp="3b9b31kmbnw5n" />
                        
                          </div>
                        </div>

                        <h3 className="text-xl md:text-2xl font-title font-bold text-[#333333] text-center mb-3 leading-tight" data-id="element-10989" data-mp="3uy3epft5yyla">
                          Gigabrain is requesting access to your{' '}
                          {permModal.name} account
                        </h3>

                        <p className="text-[13px] md:text-[14px] text-neutral-500 text-center mb-6 max-w-sm leading-relaxed" data-id="element-10990" data-mp="1sby2esym4nf8">
                          Choose what Giga can do. You can change this anytime
                          in Settings → Connections, and revoke from{' '}
                          {permModal.name} directly.
                        </p>

                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-neutral-200 bg-neutral-50" data-id="element-10991" data-mp="14fjxblp6fjqr">
                          <div className="w-5 h-5 rounded-full bg-[#2B211A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-10992" data-mp="1ec6h075ro4r7">
                            L
                          </div>
                          <span className="text-sm text-neutral-700" data-id="element-10993" data-mp="2prlpo87c8l25">
                            Signed in as{' '}
                            <span className="font-semibold" data-id="element-10994" data-mp="2upwwea2c6fg5">l@acme.co</span>
                          </span>
                        </div>
                      </div>

                      <div className="px-6 md:px-8 py-6 bg-white border-t border-neutral-100" data-id="element-10995" data-mp="j4w2zg37d9s0">
                        <div className="flex items-center justify-between mb-4" data-id="element-10996" data-mp="e6cgnekp5vz4">
                          <span className="text-sm font-semibold text-[#333333]" data-id="element-10997" data-mp="9bfcakyd4ask">
                            This will allow Giga to:
                          </span>
                          <span className="text-xs font-mono text-neutral-400" data-id="element-10998" data-mp="3vlj86qisw430">
                            {permModal.scopes.filter((s: any) => s.required || optionalScopes[s.id]).length}
                            /{permModal.scopes.length} permissions
                          </span>
                        </div>

                        <div className="space-y-0 divide-y divide-neutral-100 border-b border-neutral-100 mb-6" data-id="element-10999" data-mp="3hm9srt2f5e03">
                          {permModal.scopes.map((scope: any) => <label key={scope.id} className={`flex items-start gap-4 py-4 ${scope.required ? 'cursor-default' : 'cursor-pointer'}`} data-id="element-11000" data-mp="1ftlorhhyamj9">
                        
                              <div className="mt-0.5" data-id="element-11001" data-mp="3a7pr3cbo74bp">
                                <div className={`w-5 h-5 rounded flex items-center justify-center ${scope.required || !!optionalScopes[scope.id] ? 'bg-[#2B211A] border-[#2B211A]' : 'border-neutral-300 border'} ${scope.required ? 'opacity-100' : ''}`} data-id="element-11002" data-mp="3f1c27026r05e">
                            
                                  {(scope.required || !!optionalScopes[scope.id]) && <Check size={12} className="text-white" strokeWidth={3} data-id="element-11003" data-mp="3sxz29bpfq6fj" />}
                                </div>
                              </div>
                              <div data-id="element-11004" data-mp="3j0uh9f7bsh0b">
                                <div className="flex items-center gap-2 mb-0.5" data-id="element-11005" data-mp="3j3ypcnnwvww9">
                                  <span className="text-[15px] font-bold text-[#333333]" data-id="element-11006" data-mp="3nz49pi1o13i8">
                                    {scope.label}
                                  </span>
                                  {scope.required && <span className="px-1.5 py-0.5 rounded bg-neutral-100 text-[10px] font-bold text-neutral-500 tracking-wide uppercase" data-id="element-11007" data-mp="2cn3htu9i8yy9">
                                      Required
                                    </span>}
                                </div>
                                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-11008" data-mp="13nk7yf6huvo1">
                                  {scope.description}
                                </p>
                              </div>
                            </label>)}
                        </div>

                        <div className="p-4 rounded-xl border border-neutral-200 bg-neutral-50 flex gap-3" data-id="element-11009" data-mp="6zdvx778t0zq">
                          <ShieldCheck className="w-5 h-5 text-[#EA580C] shrink-0 mt-0.5" data-id="element-11010" data-mp="kr13w4ajrxyc" />
                          <p className="text-[13px] text-neutral-600 leading-relaxed" data-id="element-11011" data-mp="fz0w5po8k0r7">
                            Giga stores access tokens encrypted at rest and
                            isolated per workspace. Your {permModal.name} data
                            is never shared with other Gigabrain customers or
                            used to train shared models.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 md:p-6 bg-white border-t border-neutral-100 flex flex-col md:flex-row items-center justify-between sticky bottom-0 z-10" data-id="element-11012" data-mp="m32eqkbb6dam">
                      <p className="hidden md:block text-[11px] text-neutral-500 max-w-[180px] leading-relaxed" data-id="element-11013" data-mp="r0an5t6nn2yo">
                        By continuing, you agree to Gigabrain's{' '}
                        <a href="#" className="text-neutral-700 hover:underline font-medium" data-id="element-11014" data-mp="10z08jvxrf5jo">
                      
                          Terms
                        </a>{' '}
                        and{' '}
                        <a href="#" className="text-neutral-700 hover:underline font-medium" data-id="element-11015" data-mp="19uzeve5ebgvw">
                      
                          Privacy Notice
                        </a>
                        .
                      </p>
                      <div className="flex flex-col-reverse md:flex-row md:items-center gap-3 w-full md:w-auto" data-id="element-11016" data-mp="1tvsot6ic1duz">
                        <button onClick={() => {
                    setPermModal(null);
                    setConnectPhase(null);
                    setConnectionsStarted(false);
                  }} className="w-full md:w-auto px-5 py-3.5 md:py-2.5 rounded-xl text-sm font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors" data-id="element-11017" data-mp="1ov4w8geggoyn">
                      
                          Cancel
                        </button>
                        <button onClick={() => handleAllowClick(permModal)} className="w-full md:w-auto px-6 py-3.5 md:py-2.5 rounded-xl bg-[#2B211A] text-white text-sm font-semibold hover:bg-[#EA580C] transition-colors shadow-sm whitespace-nowrap" data-id="element-11018" data-mp="27nfshreltons">
                      
                          Allow access
                        </button>
                      </div>
                    </div>
                  </div>

                  <p className="hidden md:block mt-6 font-mono text-xs text-neutral-400 text-center max-w-sm leading-relaxed" data-id="element-11019" data-mp="2qjp84j55mn9w">
                    You'll be returned to Gigabrain automatically. This stays in
                    the same tab.
                  </p>
                </div>}

              {connectPhase === 'handing-off' && <div className="flex-1 flex flex-col items-center justify-center w-full" data-id="element-11020" data-mp="13kyl24cu05w">
                  <div className="flex items-center gap-6 mb-6" data-id="element-11021" data-mp="f3lyae8vv1js">
                    <div className="w-12 h-12 rounded-xl border border-neutral-200 bg-white flex items-center justify-center shadow-sm" data-id="element-11022" data-mp="f22fafvueceb">
                      <ToolLogo name={permModal.name} icon={permModal.icon} className="w-6 h-6" data-id="element-11023" data-mp="otf757hn52kc" />
                  
                    </div>
                    <Loader2 size={24} className="text-neutral-400 animate-spin" data-id="element-11024" data-mp="jv40t6qk1a6v" />
                
                    <div className="w-12 h-12 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shadow-sm" data-id="element-11025" data-mp="xuwmgnn8507z">
                      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-11026" data-mp="1ch2ek2pgc7el" />
                  
                    </div>
                  </div>
                  <p className="font-mono text-sm text-neutral-500" data-id="element-11027" data-mp="tq2iyingbzcc">
                    Handing off to {permModal.name}...
                  </p>
                </div>}
            </motion.div>}
        </AnimatePresence>
      </motion.div>;
  }
  // Step 8: Now go to Slack
  if (step === 8) {
    const navItems = [{
      label: 'Threads',
      icon: MessageSquare
    }, {
      label: 'Huddles',
      icon: Headphones
    }, {
      label: 'Recap',
      icon: ArrowRight
    }, {
      label: 'Directories',
      icon: Users
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-11028" data-mp="fpi2vu8x8hmj">
        
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 md:px-10 py-6" data-id="element-11029" data-mp="2co7iau021lx9">
          <Link to="/" aria-label="Back to Giga home" className="flex items-center gap-2 group w-fit" data-id="element-11030" data-mp="28n5fk36xirqj">
            
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain group-hover:scale-105 transition-transform" data-id="element-11031" data-mp="23pn4o79cq2fw" />
            
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-6 object-contain" data-id="element-11032" data-mp="2rdn3zvkqb163" />
            
          </Link>
        </div>

        <div className="flex-1 flex items-center" data-id="element-11033" data-mp="1fwwuo3l0dt89">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 w-full grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-10" data-id="element-11034" data-mp="1azn7yvp07vo4">
            {/* Left — call to action */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-11035" data-mp="15v2nxea2izos">
              
              <h1 className="text-5xl md:text-6xl font-title font-bold text-[#333333] mb-5 tracking-tight" data-id="element-11036" data-mp="1flntjlo5t4h7">
                Giga's waiting in Slack.
              </h1>
              <p className="text-lg text-neutral-600 mb-8 max-w-md leading-relaxed" data-id="element-11037" data-mp="1ol14kirxm141">
                Open Slack and find Giga under{' '}
                <span className="font-semibold text-[#333333]" data-id="element-11038" data-mp="1tl6r5kjcgxf9">"Apps"</span>, or
                just <span className="font-semibold text-[#333333]" data-id="element-11039" data-mp="1yioy55dsx2km">@Giga</span>{' '}
                them in any channel in {WORKSPACE_NAME}.
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-11040" data-mp="45phd8xoq2br">
                <button onClick={() => setStep(9)} className="inline-flex items-center justify-center gap-2.5 bg-[#2B211A] text-white px-7 py-4 rounded-xl font-bold text-base hover:bg-black transition-colors shadow-lg shadow-black/20" data-id="element-11041" data-mp="45z84yqk0dii">
                  
                  <SlackIcon size={20} data-id="element-11042" data-mp="23o0v58hubrek" />
                  Message Giga
                </button>
                <Link to="/" className="inline-flex items-center gap-2 text-neutral-500 font-medium hover:text-[#333333] transition-colors" data-id="element-11043" data-mp="1ynwijtk1zgtm">
                  
                  <ArrowLeft size={18} data-id="element-11044" data-mp="1awqy113640cz" />
                  Back to home
                </Link>
              </div>
            </motion.div>

            {/* Right — Slack window mock */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="hidden lg:block rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white" data-id="element-11045" data-mp="1kpy30ktzxecj">
              
              {/* macOS chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-[#3F0E40]" data-id="element-11046" data-mp="33nb4tftfyt4j">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-11047" data-mp="33pfigph576i0" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-11048" data-mp="2px7o14dbzrmh" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-11049" data-mp="2uuhorg667ppm" />
                <div className="flex-1 mx-4" data-id="element-11050" data-mp="2usmyrng1l7eo">
                  <div className="flex items-center gap-2 rounded-md bg-black/20 px-3 py-1.5 text-white/60 text-xs" data-id="element-11051" data-mp="3iln47u878fze">
                    <Search size={12} data-id="element-11052" data-mp="3mlngb87tsf3a" />
                    Search
                  </div>
                </div>
              </div>

              <div className="flex h-[420px]" data-id="element-11053" data-mp="38os3d4oge3hy">
                {/* Sidebar */}
                <div className="w-40 shrink-0 bg-[#3F0E40] text-white/80 py-3 flex flex-col" data-id="element-11054" data-mp="3djo02i4mxf1e">
                  <div className="px-4 pb-3 mb-1 border-b border-white/10 flex items-center gap-1.5 font-bold text-white text-sm" data-id="element-11055" data-mp="1x5xray2km2lf">
                    {WORKSPACE_NAME}
                    <ChevronDown size={14} data-id="element-11056" data-mp="1i362rxaj8h0z" />
                  </div>
                  <div className="px-2 space-y-0.5 text-[13px]" data-id="element-11057" data-mp="1e2vycp2e1yo8">
                    {navItems.map(n => <div key={n.label} className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-11058" data-mp="1e502970aexoi">
                      
                        <n.icon size={15} className="opacity-70" data-id="element-11059" data-mp="14c18lapxogy1" />
                        {n.label}
                      </div>)}
                    <div className="h-px bg-white/10 my-2 mx-2" data-id="element-11060" data-mp="zbn23x3zdf24" />
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-11061" data-mp="udvcj9qt6513">
                      <Hash size={15} className="opacity-70" data-id="element-11062" data-mp="qdbdu1ql6tcu" />
                      Channels
                    </div>
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-11063" data-mp="lgb8xu5h3wnb">
                      <MessageSquare size={15} className="opacity-70" data-id="element-11064" data-mp="2fs98t5hw7g6j" />
                      Direct messages
                    </div>
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-11065" data-mp="2napiuitrrrnh">
                      <Grid3x3 size={15} className="opacity-70" data-id="element-11066" data-mp="2s8r1x3r73qyy" />
                      Apps
                    </div>
                    {/* Active Giga DM */}
                    <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-[#1164A3] text-white mt-0.5" data-id="element-11067" data-mp="320ofgh5oev0u">
                      <span className="flex items-center gap-2.5" data-id="element-11068" data-mp="24e7khikf0ti9">
                        <span className="w-4 h-4 rounded bg-[#F97316] flex items-center justify-center" data-id="element-11069" data-mp="29axz01gknh1k">
                          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-2.5 h-2.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11070" data-mp="2e9r9rm4h4ty1" />
                          
                        </span>
                        Giga
                      </span>
                      <span className="w-4 h-4 rounded-full bg-[#E01E5A] text-white text-[10px] font-bold flex items-center justify-center" data-id="element-11071" data-mp="jos3t9oogoqf">
                        1
                      </span>
                    </div>
                  </div>
                </div>

                {/* Main column */}
                <div className="flex-1 min-w-0 bg-white flex flex-col" data-id="element-11072" data-mp="ols9vklj1rhj">
                  {/* DM header + tabs */}
                  <div className="px-4 pt-3 border-b border-neutral-200" data-id="element-11073" data-mp="12cnak9lvh2a1">
                    <div className="flex items-center gap-2 mb-3" data-id="element-11074" data-mp="xfwuvndzc8p5">
                      <span className="w-6 h-6 rounded bg-[#F97316] flex items-center justify-center" data-id="element-11075" data-mp="1cce4a92lzd92">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11076" data-mp="17dkr6hr8ho9f" />
                        
                      </span>
                      <span className="font-bold text-[#333333] text-sm" data-id="element-11077" data-mp="1onon7ggweokg">
                        Giga
                      </span>
                      <ChevronDown size={14} className="text-neutral-400" data-id="element-11078" data-mp="1tkx6xlhp30nt" />
                    </div>
                    <div className="flex items-center gap-5 text-sm" data-id="element-11079" data-mp="1ewkbcpmirjtu">
                      <span className="pb-2 text-neutral-500" data-id="element-11080" data-mp="1juvlz2kn1yd4">Home</span>
                      <span className="pb-2 -mb-px border-b-2 border-[#333333] font-semibold text-[#333333]" data-id="element-11081" data-mp="15vmgoxxz0onx">
                        Messages
                      </span>
                      <span className="pb-2 text-neutral-500" data-id="element-11082" data-mp="19vehgoh4hqiq">About</span>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-auto px-4 py-4" data-id="element-11083" data-mp="10xue99zp9k5h">
                    <div className="text-center text-[11px] text-neutral-400 mb-4" data-id="element-11084" data-mp="2qbhe12bmm7jo">
                      This is the very beginning of your direct message history
                      with <span className="text-[#1164A3]" data-id="element-11085" data-mp="2v8pvf0oya7jv">@Giga</span>
                    </div>
                    <div className="flex items-start gap-2.5" data-id="element-11086" data-mp="2nrm7mn2wdri">
                      <span className="w-9 h-9 rounded-lg bg-[#F97316] flex items-center justify-center shrink-0" data-id="element-11087" data-mp="7hclrj4blfst">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11088" data-mp="3pweryemhhjqf" />
                        
                      </span>
                      <div className="min-w-0" data-id="element-11089" data-mp="qd2l0ykvby30">
                        <div className="flex items-center gap-1.5 mb-0.5" data-id="element-11090" data-mp="cem98t85fhe0">
                          <span className="font-bold text-sm text-[#333333]" data-id="element-11091" data-mp="he8tvnqb3zwu">
                            Giga
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-200 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-11092" data-mp="2x4lbcm2iiw8p">
                            App
                          </span>
                          <span className="text-[11px] text-neutral-400 hover:underline cursor-pointer" data-id="element-11093" data-mp="2pmdphws7lci9">
                            9:16 AM
                          </span>
                        </div>
                        <p className="text-sm text-[#333333] leading-snug mb-2" data-id="element-11094" data-mp="2ynbjdm8xo3n4">
                          Hi 👋, great to meet you. I'm Giga, your new AI
                          coworker. Here are two ways to work with me:
                        </p>
                        <p className="text-sm text-[#333333] leading-snug mb-1.5" data-id="element-11095" data-mp="25zz9ocnvfqb8">
                          💬 <span className="font-bold" data-id="element-11096" data-mp="2ko2ehiq6gvyg">DM me here</span> —
                          just message me like a coworker. Research, analysis,
                          reports, automation.
                        </p>
                        <p className="text-sm text-[#333333] leading-snug" data-id="element-11097" data-mp="1s4wu0xt82ml8">
                          <span className="text-[#1164A3] font-medium" data-id="element-11098" data-mp="1n73ma0c9tkiu">
                            @Giga
                          </span>{' '}
                          <span className="font-bold" data-id="element-11099" data-mp="3s993rx743wm3">in any channel</span> —
                          mention me in context and I'll jump in with the full
                          thread as background.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right — Slack mock (Mobile) */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="lg:hidden rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white flex flex-col w-full max-w-md mx-auto" data-id="element-11100" data-mp="399v89atqidth">
              
              {/* Mobile Slack Header */}
              <div className="bg-[#3F0E40] text-white px-4 py-3 flex items-center gap-3" data-id="element-11101" data-mp="xmnfnc8orkns">
                <div className="w-9 h-9 rounded bg-white/20 flex items-center justify-center shrink-0" data-id="element-11102" data-mp="17qvfw5ynricp">
                  <span className="font-bold text-base" data-id="element-11103" data-mp="12s0oifmmklgz">
                    {WORKSPACE_INITIAL}
                  </span>
                </div>
                <div className="flex-1 min-w-0" data-id="element-11104" data-mp="ty5t3zbb29ej">
                  <div className="text-xs font-medium text-white/70 truncate" data-id="element-11105" data-mp="tv9v6azgjkq3">
                    {WORKSPACE_NAME}
                  </div>
                  <div className="font-bold text-[15px] truncate leading-tight" data-id="element-11106" data-mp="jynbvqms51q6">
                    Giga
                  </div>
                </div>
                <Search size={20} className="text-white/80" data-id="element-11107" data-mp="f0ddl75rdl0c" />
              </div>

              {/* Mobile Messages Area */}
              <div className="flex-1 bg-white p-5" data-id="element-11108" data-mp="29e4la14j1ut5">
                <div className="text-center text-[11px] text-neutral-400 mb-5" data-id="element-11109" data-mp="29ctgbxzcih0g">
                  This is the very beginning of your direct message history with{' '}
                  <span className="text-[#1164A3]" data-id="element-11110" data-mp="2cf7uk7whpije">@Giga</span>
                </div>
                <div className="flex items-start gap-3" data-id="element-11111" data-mp="2hekeajs2e9hr">
                  <span className="w-10 h-10 rounded-lg bg-[#F97316] flex items-center justify-center shrink-0" data-id="element-11112" data-mp="2he179y3yd5ii">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11113" data-mp="23h4550idw1mb" />
                    
                  </span>
                  <div className="min-w-0 pt-0.5" data-id="element-11114" data-mp="1jr273de1xffv">
                    <div className="flex items-center gap-1.5 mb-1" data-id="element-11115" data-mp="1jsd8j6k0yb3t">
                      <span className="font-bold text-[15px] text-[#333333]" data-id="element-11116" data-mp="1oplvdkg4b3be">
                        Giga
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-200 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-11117" data-mp="1aqvxw3pdz1mi">
                        App
                      </span>
                      <span className="text-xs text-neutral-400" data-id="element-11118" data-mp="w5rjz1zh0x1u">9:16 AM</span>
                    </div>
                    <p className="text-[15px] text-[#333333] leading-snug mb-2.5" data-id="element-11119" data-mp="r6x0ryxld6h7">
                      Hi 👋, great to meet you. I'm Giga, your new AI coworker.
                      Here are two ways to work with me:
                    </p>
                    <p className="text-[15px] text-[#333333] leading-snug mb-2" data-id="element-11120" data-mp="1f1rcqf09w4mh">
                      💬 <span className="font-bold" data-id="element-11121" data-mp="1a3y83qembiob">DM me here</span> — just
                      message me like a coworker. Research, analysis, reports,
                      automation.
                    </p>
                    <p className="text-[15px] text-[#333333] leading-snug" data-id="element-11122" data-mp="1jszohhp2s22t">
                      <span className="text-[#1164A3] font-medium" data-id="element-11123" data-mp="1xsr7jwug88no">@Giga</span>{' '}
                      <span className="font-bold" data-id="element-11124" data-mp="1xqo68gnuit3n">in any channel</span> —
                      mention me in context and I'll jump in with the full
                      thread as background.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>;
  }
  if (step === 9) {
    const navItems = [{
      label: 'Threads',
      icon: MessageSquare
    }, {
      label: 'Huddles',
      icon: Headphones
    }, {
      label: 'Recap',
      icon: ArrowRight
    }, {
      label: 'Directories',
      icon: Users
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="h-screen w-full bg-white font-body flex flex-col overflow-hidden" data-id="element-11125" data-mp="1e5kszdbcc2kk">
        
        {/* macOS chrome & Slack header */}
        <div className="flex items-center gap-4 px-4 py-2 bg-[#3F0E40] border-b border-white/10 shrink-0" data-id="element-11126" data-mp="1n5r5avvaywi5">
          <div className="flex items-center gap-2 w-16" data-id="element-11127" data-mp="3kxvcy630uodw">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-11128" data-mp="3oxvqloiaqvjw" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-11129" data-mp="3oyf7r304hfwv" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-11130" data-mp="3hfo3kvw9b5rv" />
          </div>
          <div className="flex-1 flex justify-center" data-id="element-11131" data-mp="3cneax83rbrlw">
            <div className="flex items-center gap-2 rounded-md bg-white/20 hover:bg-white/30 transition-colors px-4 py-1.5 text-white/80 text-sm w-full max-w-2xl cursor-text" data-id="element-11132" data-mp="3cltete1so0lx">
              <Search size={14} data-id="element-11133" data-mp="33ossre276eln" />
              Search {WORKSPACE_NAME}
            </div>
          </div>
          <div className="w-16 flex justify-end" data-id="element-11134" data-mp="47ku0s5ike4b">
            <div className="w-7 h-7 rounded bg-white/20" data-id="element-11135" data-mp="3vdvxsw1wrszc" />
          </div>
        </div>

        <div className="flex flex-1 min-h-0" data-id="element-11136" data-mp="3rcsl2if5qd2c">
          {/* Sidebar */}
          <div className="w-64 shrink-0 bg-[#3F0E40] text-white/80 py-4 flex flex-col overflow-y-auto" data-id="element-11137" data-mp="3rewwrmjasgd6">
            <div className="px-4 pb-4 mb-2 border-b border-white/10 flex items-center justify-between font-bold text-white text-lg" data-id="element-11138" data-mp="3mftta6iyui7b">
              <span className="flex items-center gap-1.5" data-id="element-11139" data-mp="2ay0zaocio420">
                {WORKSPACE_NAME}
                <ChevronDown size={16} data-id="element-11140" data-mp="evny0408itav" />
              </span>
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" data-id="element-11141" data-mp="exihddvuydcu">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-11142" data-mp="iuwnuxs5m715">
                  
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-11143" data-mp="9wurwwna24kq"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-11144" data-mp="xn89tuc5en1l"></path>
                </svg>
              </div>
            </div>
            <div className="px-3 space-y-0.5 text-[15px]" data-id="element-11145" data-mp="nw3uuumh326r">
              {navItems.map(n => <div key={n.label} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer" data-id="element-11146" data-mp="ntztajc1qf9n">
                
                  <n.icon size={18} className="opacity-70" data-id="element-11147" data-mp="1gjzpzuwe8m1f" />
                  {n.label}
                </div>)}
              <div className="h-px bg-white/10 my-4 mx-3" data-id="element-11148" data-mp="1ld2lw9on87ks" />

              <div className="flex items-center justify-between px-3 py-1.5 group cursor-pointer" data-id="element-11149" data-mp="361ddt6tyigak">
                <span className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity" data-id="element-11150" data-mp="313by8vvqoz3u">
                  <ChevronDown size={14} data-id="element-11151" data-mp="3eugo569b2d0z" />
                  Channels
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-4" data-id="element-11152" data-mp="3atogghg30g6t">
                <Hash size={16} className="opacity-70" data-id="element-11153" data-mp="2n556zylapxd5" />
                general
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-4" data-id="element-11154" data-mp="2i60zdce0s7ox">
                <Hash size={16} className="opacity-70" data-id="element-11155" data-mp="2r44k46h6l0fo" />
                engineering
              </div>

              <div className="h-px bg-white/10 my-4 mx-3" data-id="element-11156" data-mp="bcyrluyzgsfv" />

              <div className="flex items-center justify-between px-3 py-1.5 group cursor-pointer" data-id="element-11157" data-mp="bfkxlq686zvz">
                <span className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity" data-id="element-11158" data-mp="6ghxmk53rjuv">
                  <ChevronDown size={14} data-id="element-11159" data-mp="kyn94zdsg2co" />
                  Direct messages
                </span>
              </div>

              {/* Active Giga DM */}
              <div className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-[#1164A3] text-white mt-1 cursor-pointer" data-id="element-11160" data-mp="c1355iojjryd">
                <span className="flex items-center gap-3" data-id="element-11161" data-mp="73jqwiruff6f">
                  <span className="relative w-5 h-5 rounded bg-[#F97316] flex items-center justify-center" data-id="element-11162" data-mp="14nnwij50o41x">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11163" data-mp="zqe7v5pgbwf5" />
                    
                    <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#2BAC76] border-2 border-[#1164A3]" data-id="element-11164" data-mp="1nh1ihyqr505e" />
                  </span>
                  Giga
                </span>
              </div>

              {[{
              name: 'Priya Patel',
              initial: 'P',
              color: 'bg-[#4A9E8E]',
              online: true
            }, {
              name: 'Marcus Lee',
              initial: 'M',
              color: 'bg-[#9B6DD6]',
              online: true
            }, {
              name: 'Jordan Kim',
              initial: 'J',
              color: 'bg-[#E0727F]',
              online: false
            }, {
              name: 'Elena Rodriguez',
              initial: 'E',
              color: 'bg-[#D9A441]',
              online: false
            }, {
              name: 'Sarah (you)',
              initial: 'S',
              color: 'bg-[#4F84D6]',
              online: true
            }].map(person => <div key={person.name} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-1" data-id="element-11165" data-mp="2sn9raahc4kw1">
                
                  <span className={`relative w-5 h-5 rounded ${person.color} flex items-center justify-center text-xs font-bold text-white shrink-0`} data-id="element-11166" data-mp="2wlz5u6e408h9">
                  
                    {person.initial}
                    {person.online ? <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#2BAC76] border-2 border-[#3F0E40]" data-id="element-11167" data-mp="16cf28z7vatms" /> : <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#3F0E40] border-2 border-white/50" data-id="element-11168" data-mp="11k41ju5az3t6" />}
                  </span>
                  {person.name}
                </div>)}
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="flex-1 min-w-0 bg-white flex flex-col" data-id="element-11169" data-mp="1pahj2qq9hk9i">
            {/* Header */}
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between shrink-0" data-id="element-11170" data-mp="1l9fhw4csay4w">
              <div className="flex items-center gap-3" data-id="element-11171" data-mp="3r1kx7pzd1gyt">
                <span className="w-8 h-8 rounded bg-[#F97316] flex items-center justify-center" data-id="element-11172" data-mp="3r4737l6lroex">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11173" data-mp="3n2lumc13pwaj" />
                  
                </span>
                <div className="flex flex-col" data-id="element-11174" data-mp="334ycw7s3bhbe">
                  <div className="flex items-center gap-2" data-id="element-11175" data-mp="384u579rfdkb4">
                    <span className="font-bold text-[#333333] text-[15px]" data-id="element-11176" data-mp="2t8l8aev0bj1v">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-11177" data-mp="2y7y59sfle6i6">
                      App
                    </span>
                    <span className="w-2 h-2 rounded-full bg-[#2BAC76]" data-id="element-11178" data-mp="2p7sy9jeb9a4j" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-neutral-500" data-id="element-11179" data-mp="iepg4e1ql74o">
                <span className="font-semibold text-sm pb-1 border-b-2 border-[#333333] text-[#333333] cursor-pointer" data-id="element-11180" data-mp="nf4u97tueinp">
                  Messages
                </span>
                <span className="font-semibold text-sm pb-1 border-b-2 border-transparent hover:text-[#333333] cursor-pointer" data-id="element-11181" data-mp="2ejf23jb5v6ck">
                  About
                </span>
              </div>
            </div>

            {/* Messages Scroll Area */}
            <div className="flex-1 overflow-y-auto p-5 flex flex-col" data-id="element-11182" data-mp="25lctahl6r9eq">
              {/* Intro Block */}
              <div className="mb-8 mt-4" data-id="element-11183" data-mp="20lqdc0dz3f28">
                <div className="flex items-center gap-4 mb-4" data-id="element-11184" data-mp="1vs5qn4hty7ic">
                  <div className="w-16 h-16 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-sm" data-id="element-11185" data-mp="1qu3ylu2fexuq">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-10 h-10 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11186" data-mp="1lvj7ck55joif" />
                    
                  </div>
                  <div data-id="element-11187" data-mp="3ogmvq1xl9ipc">
                    <div className="flex items-center gap-2 mb-1" data-id="element-11188" data-mp="3oh6chfbi611s">
                      <span className="font-bold text-2xl text-[#333333]" data-id="element-11189" data-mp="3tfzspfi6c65n">
                        Giga
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-11190" data-mp="1di7xxe3z9dz">
                        App
                      </span>
                      <span className="w-2.5 h-2.5 rounded-full bg-[#2BAC76]" data-id="element-11191" data-mp="35pdmzuvn6xyt" />
                    </div>
                  </div>
                </div>
                <p className="text-[15px] text-[#333333] mb-2" data-id="element-11192" data-mp="3eqtvfx2wcq0e">
                  This is the very beginning of your direct message history with{' '}
                  <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded" data-id="element-11193" data-mp="3jkg298mdpzoa">
                    @Giga
                  </span>
                </p>
                <div className="flex items-center gap-1.5 text-[#1164A3] text-[15px] hover:underline cursor-pointer w-fit" data-id="element-11194" data-mp="2r193ioormiez">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-11195" data-mp="3ubdg8a5rx13o">
                    
                    <circle cx="12" cy="12" r="10" data-id="element-11196" data-mp="6yud1gms3dqz"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" data-id="element-11197" data-mp="2y0lmda4ngtc"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-11198" data-mp="30wl461midjw"></line>
                  </svg>
                  How does Giga work?
                </div>
              </div>

              {/* Date Divider */}
              <div className="relative flex items-center justify-center mb-6" data-id="element-11199" data-mp="3kf0m5b6bgl8e">
                <div className="absolute inset-0 flex items-center" data-id="element-11200" data-mp="3kcxllx7jf9pf">
                  <div className="w-full border-t border-neutral-200" data-id="element-11201" data-mp="3ff5w19ud7zoe"></div>
                </div>
                <div className="relative bg-white px-4" data-id="element-11202" data-mp="zni0niv02f7p">
                  <span className="text-[13px] font-bold text-[#333333] border border-neutral-200 rounded-full px-4 py-1 shadow-sm" data-id="element-11203" data-mp="upyn6l64m6gt">
                    Today <ChevronDown size={12} className="inline ml-1" data-id="element-11204" data-mp="135xlqhm5ix3c" />
                  </span>
                </div>
              </div>

              {/* Giga Onboarding Message */}
              <div className="flex items-start gap-3 hover:bg-neutral-50 -mx-5 px-5 py-2 transition-colors" data-id="element-11205" data-mp="t9ch3xe1no01">
                <div className="w-10 h-10 rounded-xl bg-[#F97316] flex items-center justify-center shrink-0 mt-0.5" data-id="element-11206" data-mp="y8p2edp9on0i">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11207" data-mp="1lxqgaggvcqd6" />
                  
                </div>
                <div className="min-w-0 flex-1" data-id="element-11208" data-mp="1h4njlzgsp0sr">
                  <div className="flex items-center gap-2 mb-1" data-id="element-11209" data-mp="24vklejevmhqa">
                    <span className="font-bold text-[15px] text-[#333333]" data-id="element-11210" data-mp="24tzt70g1bavl">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-11211" data-mp="9l32r8ix8oyj">
                      App
                    </span>
                    <span className="text-xs text-neutral-500 hover:underline cursor-pointer" data-id="element-11212" data-mp="3vzcmh4bwp8sz">
                      3:27 PM
                    </span>
                  </div>

                  <div className="text-[15px] text-[#333333] leading-relaxed space-y-3" data-id="element-11213" data-mp="sj5kr3d1xlmu">
                    <p data-id="element-11214" data-mp="nkb344qtm34b">
                      Hi 👋, great to meet you. I am Giga, your new AI coworker.
                      Here are three ways to work with me:
                    </p>

                    <div className="space-y-1.5" data-id="element-11215" data-mp="jkk81ou9h0tz">
                      <p data-id="element-11216" data-mp="eil5gh6ci7y3">
                        💬 <span className="font-bold" data-id="element-11217" data-mp="elqrtonlavq4">DM me here</span> — just
                        message me like a coworker. Research, analysis, reports,
                        automation — anything.
                      </p>
                      <p data-id="element-11218" data-mp="1es2xe3daut29">
                        📣{' '}
                        <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded font-medium cursor-pointer" data-id="element-11219" data-mp="1etnsbu3j0e0n">
                          @Giga
                        </span>{' '}
                        <span className="font-bold" data-id="element-11220" data-mp="1nrpo9v8ekgh2">in any channel</span> —
                        mention me in context and I'll jump in with the full
                        thread as background.
                      </p>
                      <p data-id="element-11221" data-mp="1xi2k4jlo3w2i">
                        🔌{' '}
                        <span className="font-bold" data-id="element-11222" data-mp="22gm5ahsft3tt">
                          I connect to 3000+ tools
                        </span>{' '}
                        — no tools are connected yet. Need GitHub, Stripe,
                        HubSpot, Google Ads, or anything else? Just tell me and
                        I'll figure out access.
                      </p>
                      <p data-id="element-11223" data-mp="h42nbhi4cmjh">
                        🔍{' '}
                        <span className="font-bold" data-id="element-11224" data-mp="h4knuqbtdmx7">
                          I'm now reading through the workspace history
                        </span>{' '}
                        — I'll propose ideas for how I can help over the next
                        hours.
                      </p>
                    </div>

                    <p data-id="element-11225" data-mp="3ey070k1iems2">
                      Try one of these now — just copy, paste, and hit send:
                    </p>

                    <div className="space-y-4" data-id="element-11226" data-mp="2m7hy32e3tr6q">
                      <div data-id="element-11227" data-mp="2m8t293bgp0yd">
                        <p className="italic text-neutral-600 mb-1" data-id="element-11228" data-mp="2m9ky8f8z10p6">
                          1. In a channel:
                        </p>
                        <div className="border-l-4 border-neutral-200 pl-3 py-0.5" data-id="element-11229" data-mp="2w562kk1iid23">
                          <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded font-medium" data-id="element-11230" data-mp="307iy6fmjoul5">
                            @Giga
                          </span>{' '}
                          summarize what's been discussed in this channel this
                          week
                        </div>
                      </div>

                      <div data-id="element-11231" data-mp="1zyb6d9ucnx0e">
                        <p className="italic text-neutral-600 mb-1" data-id="element-11232" data-mp="200nmdhgjrxat">
                          2. Or right here:
                        </p>
                        <div className="border-l-4 border-neutral-200 pl-3 py-0.5" data-id="element-11233" data-mp="1r5pnzuumrsc3">
                          Research the top 5 trends in our industry and put
                          together a brief report
                        </div>
                      </div>
                    </div>

                    <p data-id="element-11234" data-mp="1c8qj4er5ydlb">
                      Or just tell me what you need — I'll take it from there 🚀
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Message Composer */}
            <div className="px-5 pb-6 pt-2 shrink-0" data-id="element-11235" data-mp="13ael3rnywdiu">
              <div className="border border-neutral-400 rounded-xl overflow-hidden focus-within:border-neutral-500 focus-within:ring-1 focus-within:ring-neutral-500 transition-all shadow-sm" data-id="element-11236" data-mp="32lxv89l0bru3">
                {/* Toolbar */}
                <div className="bg-neutral-50 border-b border-neutral-200 px-2 py-1.5 flex items-center gap-1" data-id="element-11237" data-mp="3gqeqapteto60">
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-11238" data-mp="3lj7rx4tl0gdv">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-11239" data-mp="37s318s86yyfo">
                      
                      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" data-id="element-11240" data-mp="4b4bc1i2kfta"></path>
                      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" data-id="element-11241" data-mp="8boatbq47vil"></path>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-11242" data-mp="3qghp56n7pwc9">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-11243" data-mp="3vet39tkjit0a">
                      
                      <line x1="19" y1="4" x2="10" y2="4" data-id="element-11244" data-mp="2k1yzyrih7aru"></line>
                      <line x1="14" y1="20" x2="5" y2="20" data-id="element-11245" data-mp="2ch68d24r6qyd"></line>
                      <line x1="15" y1="4" x2="9" y2="20" data-id="element-11246" data-mp="2cjiyw3oku9n1"></line>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-11247" data-mp="27ko7weggheru">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-11248" data-mp="2lfqkfkft62dm">
                      
                      <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" data-id="element-11249" data-mp="1srckv092mxzy"></path>
                      <line x1="4" y1="21" x2="20" y2="21" data-id="element-11250" data-mp="1oteuvv7h87vi"></line>
                    </svg>
                  </button>
                  <div className="w-px h-4 bg-neutral-300 mx-1" data-id="element-11251" data-mp="22kjpujzwgbzi" />
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-11252" data-mp="1xrh1qrdw5uqp">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-11253" data-mp="1a260z9wjk0ck">
                      
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" data-id="element-11254" data-mp="1ir3efp3ubxsl"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" data-id="element-11255" data-mp="1np4vy5lmbp1y"></path>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-11256" data-mp="1smocxd9pafxl">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-11257" data-mp="1xgsm836fv01u">
                      
                      <line x1="8" y1="6" x2="21" y2="6" data-id="element-11258" data-mp="26el2b9jzibqa"></line>
                      <line x1="8" y1="12" x2="21" y2="12" data-id="element-11259" data-mp="2bezb4ttetppd"></line>
                      <line x1="8" y1="18" x2="21" y2="18" data-id="element-11260" data-mp="2bdei58mquetm"></line>
                      <line x1="3" y1="6" x2="3.01" y2="6" data-id="element-11261" data-mp="2gdtt5tjjza8f"></line>
                      <line x1="3" y1="12" x2="3.01" y2="12" data-id="element-11262" data-mp="h1zowhuixx94"></line>
                      <line x1="3" y1="18" x2="3.01" y2="18" data-id="element-11263" data-mp="m0az4toqe9rv"></line>
                    </svg>
                  </button>
                </div>

                {/* Input Area */}
                <div className="p-3 min-h-[80px] bg-white" data-id="element-11264" data-mp="2zbvm81tju0jj">
                  <p className="text-neutral-400 text-[15px]" data-id="element-11265" data-mp="2ujm252f47up6">Message Giga</p>
                </div>

                {/* Bottom Actions */}
                <div className="bg-white px-2 pb-2 flex items-center justify-between" data-id="element-11266" data-mp="2qiingj8t04pj">
                  <div className="flex items-center gap-1" data-id="element-11267" data-mp="2lkqq39pe3qdy">
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-11268" data-mp="3i9q2n0x0beby">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-11269" data-mp="3e7lbc8u16tsh">
                        
                        <line x1="12" y1="5" x2="12" y2="19" data-id="element-11270" data-mp="34a6ui84742zn"></line>
                        <line x1="5" y1="12" x2="19" y2="12" data-id="element-11271" data-mp="34c1glpr7e15b"></line>
                      </svg>
                    </button>
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-11272" data-mp="1suryb6jt9f5l">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-11273" data-mp="1ubxewpz7rjqd">
                        
                        <circle cx="12" cy="12" r="10" data-id="element-11274" data-mp="1lazfym3mu2en"></circle>
                        <path d="M8 14s1.5 2 4 2 4-2 4-2" data-id="element-11275" data-mp="1lapsp69z29cn"></path>
                        <line x1="9" y1="9" x2="9.01" y2="9" data-id="element-11276" data-mp="1q910zckmcbsr"></line>
                        <line x1="15" y1="9" x2="15.01" y2="9" data-id="element-11277" data-mp="1bl64rm7ejpax"></line>
                      </svg>
                    </button>
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-11278" data-mp="1gc62gtocibeh">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-11279" data-mp="12las1cy77m8s">
                        
                        <circle cx="12" cy="12" r="4" data-id="element-11280" data-mp="slsbz7leskm0"></circle>
                        <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" data-id="element-11281" data-mp="soo7yqdp4z7t"></path>
                      </svg>
                    </button>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-11282" data-mp="e0ei7b45xp9d">
                    <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded" data-id="element-11283" data-mp="911tektqe845">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-11284" data-mp="nxj0gzuw10kw">
                        
                        <line x1="22" y1="2" x2="11" y2="13" data-id="element-11285" data-mp="wy7is6rbdn3l"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2" data-id="element-11286" data-mp="wwe9skt8aqsf"></polygon>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 10: Welcome back (signed out / sign in to another workspace)
  if (step === 10) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="relative min-h-screen bg-[#FAF8F4] font-body flex flex-col items-center px-6 overflow-hidden" data-id="element-11287" data-mp="1fq0ck3e7jccd">
        
        {/* Brand cream surface with a soft warm bloom so it stays consistent
                                                                  with the rest of the auth flow (the "Try Giga free" screen). The
                                                                  orange primary CTA reads boldly against the cream. */}
        <div aria-hidden="true" className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[760px] h-[760px] rounded-full bg-[#EA580C]/[0.07] blur-[160px]" data-id="element-11288" data-mp="1zbtoacne9jjn" />
        
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#EA580C]/[0.05] blur-[150px]" data-id="element-11289" data-mp="28crwz88s2f8k" />
        

        {/* Return home */}
        <Link to="/" className="absolute z-20 top-6 left-6 flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-11290" data-mp="1gllrsde79rcf">
          
          <ArrowLeft size={16} data-id="element-11291" data-mp="1kkukrg1f1l6x" />
          Back to home
        </Link>

        <div className="relative z-10 w-full max-w-md flex-1 flex flex-col items-center justify-center text-center" data-id="element-11292" data-mp="1d24rqz2ww2ts">
          {/* Logo */}
          <div className="w-16 h-16 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-lg shadow-[#F97316]/20 mb-8" data-id="element-11293" data-mp="142q5kbts2ef3">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-9 h-9 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11294" data-mp="z4ga00455bsy" />
            
          </div>

          <h1 className="text-4xl md:text-5xl font-title font-bold text-[#2B211A] tracking-tight mb-4" data-id="element-11295" data-mp="kehek53z0eh6">
            {signedOut ? 'Sign in to Giga' : 'Welcome back'}
          </h1>

          <p className="text-sm text-neutral-500 mb-10" data-id="element-11296" data-mp="gbcc22jw4csj">
            {signedOut ? "You've been signed out of all workspaces." : 'Pick up right where you left off.'}
          </p>

          <div className="w-full space-y-3" data-id="element-11297" data-mp="6frgam5f08r8">
            <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl bg-[#EA580C] hover:bg-[#d24e09] shadow-lg shadow-[#EA580C]/20 transition-all" data-id="element-11298" data-mp="3bi1asak7gi65">
              
              <SlackIcon size={20} data-id="element-11299" data-mp="3lfywaqaok0v2" />
              <span className="font-semibold text-white" data-id="element-11300" data-mp="3ghx3verd6p6x">
                Continue with Slack
              </span>
            </button>
            <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border border-[#EAE4D9] bg-white transition-all hover:border-[#D4D0C8] hover:shadow-lg hover:shadow-black/5" data-id="element-11301" data-mp="2st4clvlot5in">
              <GoogleIcon size={20} data-id="element-11302" data-mp="2xrfmg6bzfg0v" />
              <span className="font-semibold text-[#2B211A]" data-id="element-11303" data-mp="2xqe37ektbn82">
                Continue with Google
              </span>
            </button>
            {teamsWaitlistDone ? <div className="w-full flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl bg-[#EA580C]/[0.08] border border-[#EA580C]/20 text-[#2B211A]" data-id="element-11304" data-mp="252s1kbv5asnu">
                <Check size={18} className="text-[#EA580C]" strokeWidth={2.5} data-id="element-11305" data-mp="kid3756awh6k" />
                <span className="text-sm font-medium" data-id="element-11306" data-mp="pcr0xe8fr0ee">
                  You're on the Microsoft Teams waitlist — we'll email you when
                  it's ready.
                </span>
              </div> : teamsWaitlistOpen ? <form onSubmit={submitTeamsWaitlist} className="w-full flex flex-col sm:flex-row gap-2" data-id="element-11307" data-mp="fja8oeny5tcu">
              
                <label htmlFor="teams-waitlist-email" className="sr-only" data-id="element-11308" data-mp="18a1zewm8tpss">
                  Work email for the Microsoft Teams waitlist
                </label>
                <input id="teams-waitlist-email" type="email" required autoFocus value={teamsWaitlistEmail} onChange={e => setTeamsWaitlistEmail(e.target.value)} placeholder="you@company.com" className="flex-1 py-3.5 px-4 rounded-xl bg-white border border-neutral-200 text-[#2B211A] placeholder:text-neutral-400 shadow-sm focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30 transition-all" data-id="element-11309" data-mp="u9r7dq976561" />
              
                <button type="submit" className="shrink-0 flex items-center justify-center gap-2 py-3.5 px-5 rounded-xl bg-[#EA580C] hover:bg-[#d24e09] text-white font-semibold shadow-lg shadow-[#EA580C]/20 transition-all" data-id="element-11310" data-mp="1kk9slzey54pu">
                
                  Notify me
                  <ArrowRight size={18} data-id="element-11311" data-mp="1brppalnpmbyo" />
                </button>
              </form> : <button onClick={() => setTeamsWaitlistOpen(true)} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl bg-white border border-neutral-200 text-[#2B211A] shadow-sm hover:bg-neutral-50 hover:border-neutral-300 transition-colors" data-id="element-11312" data-mp="16twiprihvfxv">
              
                <TeamsIcon size={20} data-id="element-11313" data-mp="stvkykxj0omm" />
                <span className="font-medium" data-id="element-11314" data-mp="suwwsrmd9f5e">
                  Join Microsoft Teams waitlist
                </span>
              </button>}
          </div>

          {/* Your Workspaces Section — hidden once the user signs out of all
                                                              workspaces, leaving a clean sign-in with just the providers above. */}
          {!signedOut && <div className="w-full mt-10" data-id="element-11315" data-mp="dx5j2mm1yrlt">
              <div className="flex items-center gap-4 mb-6" data-id="element-11316" data-mp="ip5ec1r6gwc2">
                <div className="h-px bg-neutral-200 flex-1" data-id="element-11317" data-mp="ipoylp4avwt9" />
                <span className="text-[11px] font-bold tracking-wider text-neutral-400 uppercase" data-id="element-11318" data-mp="mra4urmbxcuh">
                  Your Workspaces
                </span>
                <div className="h-px bg-neutral-200 flex-1" data-id="element-11319" data-mp="3m7ypy90ebb3l" />
              </div>

              <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-between p-4 rounded-xl border border-neutral-200 hover:border-[#EA580C] hover:bg-white transition-all group text-left bg-white shadow-sm" data-id="element-11320" data-mp="3r5ibzv3c4s63">
              
                <div className="flex items-center gap-4" data-id="element-11321" data-mp="1afbb1tw1ch6q">
                  <div className="w-10 h-10 rounded-lg bg-[#2B211A] border border-black/10 text-white flex items-center justify-center font-bold text-sm" data-id="element-11322" data-mp="1fffqdgl7j5vd">
                    FT
                  </div>
                  <div className="flex flex-col" data-id="element-11323" data-mp="18z5wovfrejez">
                    <span className="font-semibold text-[#2B211A]" data-id="element-11324" data-mp="190qoidaovo95">
                      Faith Test
                    </span>
                    <span className="text-sm text-neutral-500" data-id="element-11325" data-mp="1mwm0s0al213k">
                      alqahtani.faith03@gmail.com
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-11326" data-mp="1wva927crn1u2">
                  <Check size={20} className="text-[#EA580C]" strokeWidth={2.5} data-id="element-11327" data-mp="1rytil6i3gbdt" />
                
                  <ArrowRight size={20} className="text-neutral-400 group-hover:text-[#EA580C] transition-colors" data-id="element-11328" data-mp="26n7k9e3rxtss" />
                
                </div>
              </button>

              <button onClick={() => setSignedOut(true)} className="mt-6 text-sm text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-11329" data-mp="2fncq3jtbkk4u">
              
                Sign out of all workspaces
              </button>
            </div>}

          <p className="mt-10 text-sm text-neutral-500" data-id="element-11330" data-mp="2ubmkxdhpmka5">
            No account?{' '}
            <button onClick={() => {
            // Send the user to the free-trial start ("Try Giga free")
            // screen — step 1 is the default signup entry.
            setSignedOut(false);
            setStep(1);
            navigate('/app-screen-mockups/signup');
          }} className="font-bold text-[#2B211A] underline hover:text-[#EA580C] transition-colors" data-id="element-11331" data-mp="2z8cz1v9yf5sx">
              
              Sign up
            </button>
          </p>
        </div>

        <p className="relative z-10 max-w-md text-center text-xs text-neutral-400 pb-8" data-id="element-11332" data-mp="21lnr74sklyz5">
          By logging in, you agree to the Giga{' '}
          <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-11333" data-mp="26ls7outhatpd">
            
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-11334" data-mp="1xk2ebjg1dkoy">
            
            Terms of Service
          </a>
          .
        </p>
      </motion.div>;
  }
  // Step 11: Trial Paywall
  if (step === 11) {
    const plans = [{
      id: 'plus',
      name: 'Plus',
      desc: 'The ultimate AI work assistant.',
      price: '$49.99/mo'
    }, {
      id: 'pro',
      name: 'Pro',
      desc: 'More usage for power users.',
      price: '$99.99/mo',
      popular: true
    }];
    const selectedPrice = plans.find(p => p.id === plan)?.price.split('/')[0] || '$49.99';
    const futureDate = new Date(Date.now() + 7 * 864e5).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    const selectedPlan = plans.find(p => p.id === plan) || plans[0];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="relative min-h-screen w-full bg-white font-body" data-id="element-11335" data-mp="83gd4su3edty">
        
        {/* Real Stripe Checkout anatomy: full-height split, dark merchant
         summary rail on the left, white payment pane on the right. */}
        <div className="mx-auto grid min-h-screen w-full grid-cols-1 lg:grid-cols-2" data-id="element-11336" data-mp="18ces7cq970qy">
          {/* ── Left: dark order summary ─────────────────────────────────── */}
          <aside className="flex flex-col bg-[#1B130D] px-5 py-5 text-white sm:px-10 lg:py-16 lg:pl-[max(2.5rem,calc((100vw-1180px)/2+2.5rem))] lg:pr-16" data-id="element-11337" data-mp="1hb9z6r14gevb">
            <div className="flex flex-1 flex-col" data-id="element-11338" data-mp="1m4mkar6le3jb">
              {/* back + merchant (logo in a white circle, exactly like Stripe) */}
              <div className="flex items-center gap-3" data-id="element-11339" data-mp="1w06cb2i1ci2s">
                <button type="button"
              // In the signup flow the paywall is reached from the tool
              // connections screen (step 7), not the sign-in screen
              // (step 10) — back should return to connections.
              onClick={() => setStep(7)} aria-label="Back" className="text-white/50 transition-colors hover:text-white" data-id="element-11340" data-mp="1w2sjv24xexl0">
                  
                  <ArrowLeft size={18} data-id="element-11341" data-mp="anv3pk4msnyw" />
                </button>
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white" data-id="element-11342" data-mp="3nvp7xanfkso5">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-4 w-4 object-contain" data-id="element-11343" data-mp="3iy77y9re5nsa" />
                  
                </span>
                <span className="text-[15px] font-medium text-white" data-id="element-11344" data-mp="34xcwfi91r6pe">Giga</span>
              </div>

              {/* headline price */}
              <div className="mt-6 lg:mt-12" data-id="element-11345" data-mp="2zzl2kioudaik">
                <p className="text-[14px] text-white/60 lg:text-[15px]" data-id="element-11346" data-mp="300w9ushhx0ef">
                  Subscribe to Giga {selectedPlan.name}
                </p>
                <div className="mt-1 flex items-start gap-2" data-id="element-11347" data-mp="38y6i04z0o9f4">
                  <span className="text-[32px] font-bold leading-none tracking-tight lg:text-[44px]" data-id="element-11348" data-mp="t7tscjcnq6s6">
                    {selectedPrice}
                  </span>
                  <span className="mt-0.5 text-[13px] leading-tight text-white/60 lg:mt-1 lg:text-[15px]" data-id="element-11349" data-mp="o8qzrydv5t13">
                    per
                    <br data-id="element-11350" data-mp="10rmllyudj3xw" />
                    month
                  </span>
                </div>
                <p className="mt-2 text-[13px] text-white/50 lg:mt-3 lg:text-[14px]" data-id="element-11351" data-mp="mrtnvjkfjngf">
                  7-day free trial · US$0.00 due today
                </p>

                {/* plan segmented control */}
                <div className="mt-5 inline-flex items-center rounded-lg border border-white/15 p-0.5 text-[13px]" data-id="element-11352" data-mp="1fgr5dkgthg84">
                  {plans.map(p => <button key={p.id} onClick={() => setPlan(p.id)} className={`flex items-center gap-1.5 rounded-[6px] px-3 py-1.5 font-medium transition-colors ${plan === p.id ? 'bg-white text-[#1B130D]' : 'text-white/60 hover:text-white'}`} data-id="element-11353" data-mp="1kekbkdi4ea8e">
                    
                      {p.name}
                      {p.popular && <span className={`rounded px-1 text-[10px] font-bold uppercase ${plan === p.id ? 'bg-[#EA580C] text-white' : 'text-[#EA580C]'}`} data-id="element-11354" data-mp="1ydscbatxlqep">
                      
                          Popular
                        </span>}
                    </button>)}
                </div>
              </div>

              {/* mobile-only details disclosure */}
              <button type="button" onClick={() => setPayDetailsOpen(o => !o)} aria-expanded={payDetailsOpen} className="mt-4 flex items-center gap-1.5 self-start text-[13px] font-medium text-white/60 transition-colors hover:text-white lg:hidden" data-id="element-11355" data-mp="211zt49636iri">
                
                {payDetailsOpen ? 'Hide details' : 'View details'}
                <ChevronDown size={15} className={`transition-transform ${payDetailsOpen ? 'rotate-180' : ''}`} data-id="element-11356" data-mp="14cs7l15gw9nl" />
                
              </button>

              {/* breakdown */}
              <div className={`${payDetailsOpen ? 'block' : 'hidden'} lg:block`} data-id="element-11357" data-mp="1dauc3qoetkfm">
                <div className="mt-6 flex items-start justify-between gap-4 lg:mt-12" data-id="element-11358" data-mp="pljay835do0y">
                  <div data-id="element-11359" data-mp="ll95qxn6j1n5">
                    <p className="text-[15px] font-medium text-white" data-id="element-11360" data-mp="ljod5dkfdsrx">
                      Giga {selectedPlan.name}
                    </p>
                    <p className="mt-0.5 text-[13px] text-white/50" data-id="element-11361" data-mp="bey46nqltoe7">
                      Billed monthly
                    </p>
                  </div>
                  <p className="text-[15px] font-medium text-white" data-id="element-11362" data-mp="1jo07ugnb6xs">
                    {selectedPrice}
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-6" data-id="element-11363" data-mp="6fd3a7rvj5yu">
                  <span className="text-[15px] font-medium text-white" data-id="element-11364" data-mp="u5fqoakufnr4">
                    Subtotal
                  </span>
                  <span className="text-[15px] font-medium text-white" data-id="element-11365" data-mp="k9m9gdw6j630">
                    {selectedPrice}
                  </span>
                </div>

                <div className="mt-5" data-id="element-11366" data-mp="paaazti1b5bh">
                  <button type="button" className="rounded-lg bg-white/[0.07] px-4 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-white/10" data-id="element-11367" data-mp="34ytus21lurrh">
                    
                    Add promotion code
                  </button>
                </div>

                <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-5" data-id="element-11368" data-mp="1ghiiny3x4ip2">
                  <span className="text-[15px] font-medium text-white" data-id="element-11369" data-mp="16pwbqdym55s4">
                    Total due today
                  </span>
                  <span className="text-[15px] font-medium text-white" data-id="element-11370" data-mp="11rl0byso6n7s">
                    US$0.00
                  </span>
                </div>
              </div>

              {/* footer pinned bottom — desktop only */}
              <div className="mt-auto hidden items-center gap-4 pt-14 text-[12px] text-white/40 lg:flex" data-id="element-11371" data-mp="sojvs2jgd468">
                <span className="flex items-center gap-1.5" data-id="element-11372" data-mp="nrjyoh4kzbr9">
                  Powered by{' '}
                  <span className="font-bold text-white/70" data-id="element-11373" data-mp="iti8zdcnge6t">stripe</span>
                </span>
                <span className="h-3 w-px bg-white/20" data-id="element-11374" data-mp="2i4jas09r5nts" />
                <button type="button" className="hover:text-white/70" data-id="element-11375" data-mp="2d6841yer7tfs">
                  Terms
                </button>
                <button type="button" className="hover:text-white/70" data-id="element-11376" data-mp="2kofz9eay6piw">
                  Privacy
                </button>
              </div>
            </div>
          </aside>

          {/* ── Right: white payment form ─────────────────────────────────── */}
          <div className="px-5 py-8 sm:px-10 lg:py-16 lg:pl-16 lg:pr-[max(2.5rem,calc((100vw-1180px)/2+2.5rem))]" data-id="element-11377" data-mp="2zedsw9rnc7fh">
            <div className="mx-auto w-full max-w-md" data-id="element-11378" data-mp="21qv9m50czmx8">
              {/* Express checkout — wallets above the card form */}
              <div className="grid grid-cols-2 gap-3" data-id="element-11379" data-mp="26qres7kslked">
                <button type="button" onClick={() => setStep(finalStep)} aria-label="Pay with Apple Pay" className="flex h-11 items-center justify-center gap-1 rounded-lg bg-black text-white transition-opacity hover:opacity-85" data-id="element-11380" data-mp="26rj81bqx361h">
                  
                  <svg viewBox="0 0 384 512" className="h-[17px] w-[17px] fill-current" aria-hidden="true" data-id="element-11381" data-mp="3n2ws7zspx9vf">
                    
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" data-id="element-11382" data-mp="3s55l6w5lcbxj" />
                  </svg>
                  <span className="text-[16px] font-medium" data-id="element-11383" data-mp="cg1efjvtr9su">Pay</span>
                </button>
                <button type="button" onClick={() => setStep(finalStep)} aria-label="Pay with Google Pay" className="flex h-11 items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white text-[#3c4043] transition-colors hover:bg-neutral-50" data-id="element-11384" data-mp="8fr6w2sdwbbv">
                  
                  <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/google.svg" alt="" className="h-[18px] w-[18px]" data-id="element-11385" data-mp="w6mwct9daufy" />
                  
                  <span className="text-[15px] font-medium" data-id="element-11386" data-mp="1ask7o5ccqc63">Pay</span>
                </button>
              </div>

              <div className="my-6 flex items-center gap-4 text-[14px] text-[#6B7280]" data-id="element-11387" data-mp="1eunnt79yu4xp">
                <span className="h-px flex-1 bg-neutral-200" data-id="element-11388" data-mp="10umlrohysrgn" />
                Or pay with card
                <span className="h-px flex-1 bg-neutral-200" data-id="element-11389" data-mp="15th5qtro4m2c" />
              </div>

              <div className="space-y-4" data-id="element-11390" data-mp="15vab85qjp08r">
                <div data-id="element-11391" data-mp="r4b3ewxjafq8">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-11392" data-mp="r303573arq3v">
                    Email
                  </label>
                  <input type="email" placeholder="faith@company.com" className="mt-1.5 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-[15px] text-[#30313D] outline-none transition-shadow placeholder:text-neutral-400 focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30" data-id="element-11393" data-mp="vx4dm0brug9p" />
                  
                </div>

                <div data-id="element-11394" data-mp="d51pk3wsvxpf">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-11395" data-mp="d3gxqm1vesv9">
                    Card information
                  </label>
                  <div className="mt-1.5 overflow-hidden rounded-lg border border-neutral-300 focus-within:border-[#EA580C] focus-within:ring-2 focus-within:ring-[#EA580C]/30" data-id="element-11396" data-mp="3leum05nmjpii">
                    <div className="flex items-center border-b border-neutral-200" data-id="element-11397" data-mp="3gdnco25u2i9q">
                      <input placeholder="1234 1234 1234 1234" inputMode="numeric" className="w-full bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-11398" data-mp="3bi7y18004ic9" />
                      
                      <div className="flex items-center gap-1 pr-3" aria-hidden="true" data-id="element-11399" data-mp="7y3nbe8a9jzy">
                        
                        {['visa', 'mastercard', 'amex', 'discover'].map(brand => <img key={brand} src={`https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons/flat/${brand}.svg`} alt="" className="h-4 w-auto rounded-[2px]" data-id="element-11400" data-mp="3u6mpfwe8dydl" />)}
                      </div>
                    </div>
                    <div className="flex" data-id="element-11401" data-mp="y9yp1hshmr4u">
                      <input placeholder="MM / YY" className="w-1/2 bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-11402" data-mp="yazzpl5ldbm1" />
                      
                      <input placeholder="CVC" inputMode="numeric" className="w-1/2 border-l border-neutral-200 bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-11403" data-mp="187v0kapc7o3h" />
                      
                    </div>
                  </div>
                </div>

                <div data-id="element-11404" data-mp="1lyhwxw7ml1we">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-11405" data-mp="1lxyduc68o7gs">
                    Name on card
                  </label>
                  <input placeholder="Faith Anderson" className="mt-1.5 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-[15px] text-[#30313D] outline-none transition-shadow placeholder:text-neutral-400 focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30" data-id="element-11406" data-mp="1vxgvglyofh5o" />
                  
                </div>

                <div data-id="element-11407" data-mp="23ggdooaej2ng">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-11408" data-mp="23fx5hzajzsmm">
                    Country or region
                  </label>
                  <div className="mt-1.5 overflow-hidden rounded-lg border border-neutral-300 focus-within:border-[#EA580C] focus-within:ring-2 focus-within:ring-[#EA580C]/30" data-id="element-11409" data-mp="2d71kv0455fhz">
                    <div className="relative border-b border-neutral-200" data-id="element-11410" data-mp="28a1fkrf48grx">
                      <select defaultValue="United States" className="w-full appearance-none bg-transparent px-3 py-2.5 pr-9 text-[15px] text-[#30313D] outline-none" data-id="element-11411" data-mp="1kj5iberw4tda">
                        
                        <option data-id="element-11412" data-mp="1uhbgpmehnhde">United States</option>
                        <option data-id="element-11413" data-mp="1phgq7jymktsv">Canada</option>
                        <option data-id="element-11414" data-mp="1pgns17dqfcmc">United Kingdom</option>
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-11415" data-mp="35unno65jog3s" />
                      
                    </div>
                    <input placeholder="ZIP" className="w-full bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-11416" data-mp="38flkvi0a9s7g" />
                    
                  </div>
                </div>
              </div>

              <button onClick={() => setStep(finalStep)} className="mt-6 w-full rounded-lg bg-[#EA580C] py-3 text-[15px] font-semibold text-white transition-colors hover:bg-[#d24e08]" data-id="element-11417" data-mp="2zh9nmx4wvw61">
                
                Subscribe
              </button>

              <p className="mt-4 text-center text-[12px] leading-relaxed text-[#8792A2]" data-id="element-11418" data-mp="uzjnab55c4">
                By confirming your subscription, you allow Giga to charge your
                card for this payment and future payments in accordance with
                their terms. Your 7-day trial is free — you won't be charged
                until {futureDate}, and you can cancel anytime.
              </p>

              {/* mobile-only Stripe footer */}
              <div className="mt-8 flex items-center justify-center gap-4 text-[12px] text-neutral-400 lg:hidden" data-id="element-11419" data-mp="isxjb7i8x1ql">
                <span className="flex items-center gap-1.5" data-id="element-11420" data-mp="1o9etfbs60gkk">
                  Powered by{' '}
                  <span className="font-bold text-neutral-500" data-id="element-11421" data-mp="1o7258gvtd9z2">stripe</span>
                </span>
                <span className="h-3 w-px bg-neutral-200" data-id="element-11422" data-mp="1t2hfkyum4nqq" />
                <button type="button" className="hover:text-neutral-600" data-id="element-11423" data-mp="1xxws9ngvwdlk">
                  Terms
                </button>
                <button type="button" className="hover:text-neutral-600" data-id="element-11424" data-mp="11b1uj98pf92m">
                  Privacy
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 1: Landing
  const LANDING_TESTIMONIALS = [{
    quote: "Nothing short of revolutionary. The best attempt I've ever had as a CEO to turn every employee into an AI-first operator.",
    name: 'Tony Ennis',
    title: 'CEO, Brightwave',
    image: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/image_14.png"
  }, {
    quote: "Giga has completely transformed how our engineering team accesses context. It's like having a senior engineer who knows everything.",
    name: 'Sarah Chen',
    title: 'VP Engineering, Northwind',
    image: 'https://i.pravatar.cc/150?u=sarah'
  }, {
    quote: 'The seamless integration with Slack means my team actually uses it. No new tabs, just instant answers where we already work.',
    name: 'Marcus Lee',
    title: 'Product Lead, Acme Corp',
    image: 'https://i.pravatar.cc/150?u=marcus'
  }];
  return <div className="min-h-screen flex font-body bg-white" data-id="element-11425" data-mp="3igqr0q4k4fy4">
      {/* Teams isn't live — the hover state on that button opens this. */}
      <AnimatePresence data-id="element-11426" data-mp="3djqlqhfj7h82">
        {showWaitlist && <WaitlistModal referredBy={new URLSearchParams(location.search).get('ref')?.trim().toLowerCase()} onClose={() => setShowWaitlist(false)} data-id="element-11427" data-mp="1gpx3ngobuufk" />}
      </AnimatePresence>

      {/* Left Pane - Product Scenery */}
      <div className="hidden lg:flex w-1/2 bg-[#2B211A] border-r border-neutral-800 relative overflow-hidden flex-col p-12" data-id="element-11428" data-mp="366izdrri5936">
        {/* Shared dark-green grid + subtle glow */}
        <DarkGreenTexture glow="top-left" data-id="element-11429" data-mp="2w8v3uzm2nxjy" />

        <Link to="/" aria-label="Giga home" className="relative z-10 flex items-center gap-2 mb-20 md:mb-24" data-id="element-11430" data-mp="2w9wi1cydx05w">
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" data-id="element-11431" data-mp="3187p5fxaowkf" />
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11432" data-mp="3fx46k3l8rlxp" />
          
        </Link>

        {/* Product story — auto-cycling feature showcase (Integrations,
         Smart Memory, Autonomous work) */}
        <div className="relative z-10 flex flex-col items-center w-full" data-id="element-11433" data-mp="28gxt7buczbkf">
          <SignupShowcase compactCounter counterPosition="bottom" headlinePosition="bottom" data-id="element-11434" data-mp="20xwvj768oc4z" />
          
          {false && <motion.div initial="hidden" animate="show" variants={{
          hidden: {},
          show: {
            transition: {
              staggerChildren: 0.12,
              delayChildren: 0.1
            }
          }
        }} className="w-full max-w-sm flex flex-col items-center gap-6" data-id="element-11435" data-mp="1w2rasbokp3ih">
            
              {/* Speed chip */}
              <motion.div variants={{
            hidden: {
              opacity: 0,
              y: -8
            },
            show: {
              opacity: 1,
              y: 0
            }
          }} className="inline-flex items-center gap-2 rounded-full bg-[#F97316] px-4 py-1.5 text-[#2B211A] text-xs font-bold shadow-lg" data-id="element-11436" data-mp="1daykwq4svyez">
              
                <motion.span animate={{
              scale: [1, 1.3, 1],
              opacity: [1, 0.6, 1]
            }} transition={{
              duration: 1.4,
              repeat: Infinity
            }} className="w-2 h-2 rounded-full bg-[#2B211A]" data-id="element-11437" data-mp="zgfm0oo2fd1f" />
              
                Live in under 60 seconds
              </motion.div>

              {/* Setup checklist — ticks off fast */}
              <motion.div variants={{
            hidden: {
              opacity: 0,
              y: 12
            },
            show: {
              opacity: 1,
              y: 0
            }
          }} className="w-full bg-white border border-neutral-200 rounded-2xl p-5 shadow-xl shadow-black/5 space-y-3" data-id="element-11438" data-mp="6r5swd54vj6a">
              
                {[{
              label: 'Connected to Slack',
              activeLabel: 'Connecting to Slack…',
              icon: 'slack'
            }, {
              label: 'Synced your tools',
              activeLabel: 'Syncing your tools…',
              tools: true
            }, {
              label: 'Giga is live',
              activeLabel: 'Booting Giga…',
              giga: true
            }].map((item, i) => {
              const isDone = setupStep > i;
              const isActive = setupStep === i;
              return <motion.div key={item.label} variants={{
                hidden: {
                  opacity: 0,
                  x: -10
                },
                show: {
                  opacity: 1,
                  x: 0
                }
              }} className={`relative flex items-center justify-between gap-3 rounded-xl px-2 py-1 transition-colors ${item.giga && isDone ? 'bg-[#F97316]/10' : ''}`} data-id="element-11439" data-mp="2jy41mdieukrc" data-mpi={i}>
                    
                      {/* Lime glow payoff on the final 'Giga is live' row */}
                      {item.giga && isDone && <motion.span aria-hidden="true" initial={{
                  opacity: 0
                }} animate={{
                  opacity: [0, 1, 0.5]
                }} transition={{
                  duration: 0.8
                }} className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-[#F97316]/50" data-id="element-11440" data-mp="2tprzaw5k8y19" />}
                      <div className="relative flex items-center gap-3" data-id="element-11441" data-mp="1jsjtlf5uyv84">
                        <div className="w-6 h-6 shrink-0 flex items-center justify-center" data-id="element-11442" data-mp="1or4ttel41ewk">
                          <AnimatePresence mode="wait" data-id="element-11443" data-mp="19tkrygjifzum">
                            {isDone ? <motion.div key="done" initial={{
                        scale: 0
                      }} animate={{
                        scale: 1
                      }} transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 18
                      }} className="w-6 h-6 rounded-full bg-[#F97316] flex items-center justify-center" data-id="element-11444" data-mp="19vf538ojino5">
                            
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2B211A" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-11445" data-mp="w2z8oqq59nw7">
                              
                                  <path d="M20 6L9 17l-5-5" data-id="element-11446" data-mp="m2oup3waccfz" />
                                </svg>
                              </motion.div> : isActive ? <motion.div key="active" className="w-5 h-5 rounded-full border-2 border-neutral-200 border-t-[#EA580C] animate-spin" data-id="element-11447" data-mp="3fprxx5mlmyf" /> : <motion.div key="pending" className="w-5 h-5 rounded-full border-2 border-neutral-200" data-id="element-11448" data-mp="3uo3noqmqh50t" />}
                          </AnimatePresence>
                        </div>
                        <span className={`font-medium text-sm transition-colors ${isDone ? 'text-neutral-700' : isActive ? 'text-neutral-500' : 'text-neutral-300'}`} data-id="element-11449" data-mp="7j5o6sds9lal">
                        
                          {isActive ? item.activeLabel : item.label}
                        </span>
                      </div>

                      {/* Right-side badges fade in once the row completes */}
                      <AnimatePresence data-id="element-11450" data-mp="r74wzxoseidd">
                        {isDone && item.icon === 'slack' && <motion.div initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shadow-sm border border-neutral-100" data-id="element-11451" data-mp="155bazta7znm4">
                        
                            <SlackIcon size={16} data-id="element-11452" data-mp="2giccfe58kmu2" />
                          </motion.div>}

                        {isDone && item.tools && <div className="flex -space-x-1.5" data-id="element-11453" data-mp="2lgvx7b83fsku">
                            {['notion', 'linear', 'google-drive', 'figma'].map((slug, j) => <motion.div key={slug} initial={{
                      opacity: 0,
                      scale: 0.8
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} transition={{
                      delay: j * 0.06
                    }} className="w-7 h-7 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center overflow-hidden" data-id="element-11454" data-mp="1nvrgs1hq9okp" data-mpi={j}>
                            
                                  <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt={slug} className="w-4 h-4 object-contain" data-id="element-11455" data-mp="1a1h087fvx0pq" />
                            
                                </motion.div>)}
                          </div>}
                      </AnimatePresence>
                    </motion.div>;
            })}
              </motion.div>
            </motion.div>}
        </div>

        {/* Social proof — anchored to the bottom of the brand pane so the
         form column stays focused on the CTAs and workspace list. */}
        <div className="relative z-10 mt-auto flex items-center gap-4 pt-10" data-id="element-11456" data-mp="ety9ppl412fv">
          <div className="flex -space-x-2.5" data-id="element-11457" data-mp="ertvoethyn1v">
            {["https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_26.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_25.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_24.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_23.png"].map((src, i) => <img key={i} src={src} alt="" aria-hidden="true" className="w-8 h-8 rounded-full object-cover border-2 border-[#2B211A] shadow-sm relative" style={{
            zIndex: 4 - i
          }} data-id="element-11458" data-mp="27ur01vsl16h8" data-mpi={i} />)}
          </div>
          <div className="flex flex-col gap-0.5" data-id="element-11459" data-mp="1xz763exkwhv4">
            <div className="flex items-center gap-0.5" aria-hidden="true" data-id="element-11460" data-mp="1t6ns4guzbkps">
              {[0, 1, 2, 3, 4].map(i => <Star key={i} className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B]" data-id="element-11461" data-mp="1o7kzxx03l8z8" />)}
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-white/60" data-id="element-11462" data-mp="1k7118ozvlxaz">
              Loved by early adopters
            </span>
          </div>
        </div>
      </div>

      {/* Right Pane - Signup Form */}
      <div className="w-full lg:w-1/2 max-lg:min-h-screen flex flex-col items-center justify-start pt-0 lg:p-12 relative bg-[#FAF8F4] overflow-hidden" data-id="element-11463" data-mp="1a9mlyspovek9">
        {/* Mobile-only brand texture — a square-dot halftone field that fades in
         at the top and bottom edges and clears through the middle so the form
         stays clean. Hidden on desktop, where the left pane carries the brand. */}
        <div aria-hidden="true" className="lg:hidden dot-matrix z-0" style={{
        WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 28%, transparent 72%, black 100%)',
        maskImage: 'linear-gradient(to bottom, black 0%, transparent 28%, transparent 72%, black 100%)'
      }} data-id="element-11464" data-mp="3or00r0ov0l3" />
        

        {/* Mobile-only brand lockup — pinned to the very top-left of the screen.
         On desktop (lg+) the left pane already shows the Giga logo. */}
        <Link to="/" aria-label="Giga home" className="flex lg:hidden items-center gap-2 absolute top-6 left-6 z-20" data-id="element-11465" data-mp="2kf7kpy2fwmhp">
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-8 h-8 object-contain" data-id="element-11466" data-mp="2oeyeman9jiqg" />
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-5 object-contain" data-id="element-11467" data-mp="3mq9e84swccfi" />
          
        </Link>

        {/* Small top spacer on desktop — the pane now carries the workspaces
         list too, so the content block starts higher to fit evenly. */}
        <div className="hidden lg:block h-4 mb-4" aria-hidden="true" data-id="element-11468" data-mp="33vcb6wdol197" />

        <div className="relative z-10 w-full max-w-[400px] flex flex-col flex-1 lg:flex-none justify-center lg:justify-start px-6 md:px-8 lg:px-0 pt-16 lg:pt-0 pb-5 lg:pb-0" data-id="element-11469" data-mp="3htseseg8ybn4">
          {/* The eyebrow and the separate offer badge are gone — three stacked
           labels above the headline was the source of the crowding. The offer
           now lives in the headline itself, where it gets real weight, with
           the amount in orange so it still reads at a glance. */}
          <h1 className="font-title font-medium text-2xl md:text-4xl tracking-tight text-[#2B211A] mb-3 text-center lg:text-left" data-id="element-11470" data-mp="15k2cj1selgzy">
            {isLogin ? <>
                Your entire work stack,
                <br data-id="element-11471" data-mp="vktdvvmlfe79" />
                one chat away.
              </> : <>
                Try Giga free, with
                <br data-id="element-11472" data-mp="1jci9bkupoisf" />
                <span className="text-[#EA580C]" data-id="element-11473" data-mp="1jbz1iwyrzas4">$100 in credits</span> included.
              </>}
          </h1>

          {/* Short product line rather than the old three-clause sentence.
           The headline now carries the offer, so this only has to say what
           Giga is — and the task feed beside it already demonstrates the
           "connect, ask, act" story better than a sentence can. */}
          <p className="text-[15px] md:text-base text-neutral-500 leading-relaxed mb-6 text-center lg:text-left" data-id="element-11474" data-mp="227ylbkhln80m">
            Every tool your company runs on, one brain.
          </p>

          {/* Mobile-only notification illustration. The avatar/star social
           proof that used to sit here was removed — on a screen whose only job
           is "pick an auth method", it was a third competing block above the
           fold. Desktop still carries it in the left brand pane. */}
          <div className="lg:hidden w-full flex justify-center mb-5" data-id="element-11475" data-mp="4h0gjog5k1v3">
            <SignupShowcase variant="light" counterPosition="bottom" compactCounter data-id="element-11476" data-mp="121wg6v79yger" />
          </div>

          <div className="flex flex-col gap-3" data-id="element-11477" data-mp="x4wcgqxwdpqt">
            <motion.button whileHover={{
            scale: 1.01,
            y: -1
          }} whileTap={{
            scale: 0.99
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} onClick={() => {
            setAuthProvider('slack');
            if (isLogin) {
              navigate('/app-screen-mockups/dashboard');
            } else {
              setStep(2);
            }
          }} className="w-full flex items-center justify-center gap-3 px-4 py-3 md:py-3.5 rounded-xl bg-[#2B211A] text-white font-bold transition-shadow hover:shadow-lg hover:shadow-black/5" data-id="element-11478" data-mp="x67f2nflwrgc">
              
              <SlackIcon size={20} data-id="element-11479" data-mp="1xb02t740i5ol" />
              Continue with Slack
            </motion.button>
            {/* Google is a full second front door, not a fallback — plenty of
             teams have no Slack at all. It sits directly under Slack (equal
             weight, lighter fill) so neither reads as the only way in. */}
            <motion.button whileHover={{
            scale: 1.01,
            y: -1
          }} whileTap={{
            scale: 0.99
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} onClick={() => {
            setAuthProvider('google');
            if (isLogin) {
              navigate('/app-screen-mockups/dashboard');
            } else {
              setStep(20);
            }
          }} className="w-full flex items-center justify-center gap-3 px-4 py-3 md:py-3.5 rounded-xl border border-[#EAE4D9] bg-white font-bold text-[#2B211A] transition-all hover:border-[#D4D0C8] hover:shadow-lg hover:shadow-black/5" data-id="element-11480" data-mp="2b9zrufvfsmla">
              <GoogleIcon size={20} data-id="element-11481" data-mp="2f2ith9ok311e" />
              Continue with Google
            </motion.button>
            {/* Teams isn't live. Rather than a dead disabled row, hovering
             turns it into the one thing you can actually do: join the list. */}
            <button type="button" onClick={() => setShowWaitlist(true)} className="group relative flex w-full items-center justify-center gap-3 rounded-xl border border-[#EFEAE1] bg-white/60 px-4 py-3 font-bold text-neutral-400 transition-all hover:border-[#D4D0C8] hover:bg-white hover:text-[#2B211A] md:py-3.5" data-id="element-11482" data-mp="2j32syjwlqgqp">
              <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg" alt="" aria-hidden="true" className="h-5 w-5 opacity-40 transition-opacity group-hover:opacity-100" data-id="element-11483" data-mp="13lsk5hou5t3k" />
              <span className="group-hover:hidden" data-id="element-11484" data-mp="3aa1ejc0ka73v">Continue with Teams</span>
              <span className="hidden group-hover:inline" data-id="element-11485" data-mp="3f9ny036zgjl4">Join the waitlist</span>
              <span className="absolute right-4 rounded-full bg-[#F0EBE2] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-neutral-500 transition-colors group-hover:bg-[#FFEDD5] group-hover:text-[#B4470F]" data-id="element-11486" data-mp="3fbgwuwbctzik">
                Soon
              </span>
            </button>
          </div>

          {/* Book a demo — a real third option for buyers who want a walkthrough
           before connecting a workspace, but outlined and set apart by an "or"
           rule so the two auth buttons stay the primary path. */}
          {!isLogin && <>
              <div className="mt-4 flex items-center gap-4" aria-hidden="true" data-id="element-11487" data-mp="2mm1okeb2qkqc">
                <div className="h-px flex-1 bg-neutral-200" data-id="element-11488" data-mp="fu96oysql7cu" />
                <span className="text-[13px] text-neutral-400" data-id="element-11489" data-mp="fsprlbith2cn">or</span>
                <div className="h-px flex-1 bg-neutral-200" data-id="element-11490" data-mp="kt3pi0qpevwy" />
              </div>
              <a href="https://calendly.com/tonyennis/ai-adoption-for-your-business-clone" target="_blank" rel="noopener noreferrer" className="mt-4 flex w-full items-center justify-center rounded-xl border border-[#EAE4D9] bg-white px-4 py-3 font-bold text-[#2B211A] transition-all hover:border-[#D4D0C8] hover:bg-neutral-50" data-id="element-11491" data-mp="33r3o9hcllghq">
                Book a demo
              </a>
              <p className="mt-4 text-center text-[13px] text-neutral-400" data-id="element-11492" data-mp="2l26s17xn4dwv">
                No credit card required
              </p>
            </>}

          {/* Your Workspaces — returning users with remembered sessions can
           jump straight into a workspace instead of re-authing. Only the most
           recent one shows by default: three near-identical rows made the
           screen feel busy, and the other two are one click away. */}
          {!signedOut && <div className="w-full mt-5 md:mt-6" data-id="element-11493" data-mp="a79mgvzvt3es">
              <div className="flex items-center gap-4 mb-3" data-id="element-11494" data-mp="5bkimggtx0co">
                <div className="h-px bg-neutral-200 flex-1" data-id="element-11495" data-mp="cu1v8vk04v9v" />
                <span className="text-[11px] font-bold tracking-wider text-neutral-400 uppercase" data-id="element-11496" data-mp="ctimo5g8rj8i">
                  Continue as
                </span>
                <div className="h-px bg-neutral-200 flex-1" data-id="element-11497" data-mp="mjbowfuhum03" />
              </div>

              <div className="rounded-2xl border border-neutral-200 bg-white divide-y divide-neutral-100 overflow-hidden" data-id="element-11498" data-mp="riycnj7y7kn5">
                {[{
              initials: 'T',
              name: 'Tonic',
              email: 'faith@tonichq.com'
            }, ...(showAllWorkspaces ? [{
              initials: 'B',
              name: 'Brightwave',
              email: 'sarah.chen@brightwave.io'
            }, {
              initials: 'N',
              name: 'Northwind Labs',
              email: 'marcus.lee@northwindlabs.com'
            }] : [])].map(ws => <button key={ws.name} onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center gap-3.5 px-4 py-3 hover:bg-[#FFFCF8] transition-colors group text-left" data-id="element-11499" data-mp="1f7r0esef2m6o">
                    <div className="w-9 h-9 shrink-0 rounded-lg bg-[#2B211A] text-white flex items-center justify-center font-bold text-[13px]" data-id="element-11500" data-mp="2acfguon35mdm">
                      {ws.initials}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col" data-id="element-11501" data-mp="20lu848dp71gz">
                      <span className="text-sm font-semibold text-[#2B211A] truncate" data-id="element-11502" data-mp="25kfd0l3wa9br">
                        {ws.name}
                      </span>
                      <span className="text-[13px] text-neutral-500 truncate" data-id="element-11503" data-mp="1rnsck8cqh113">
                        {ws.email}
                      </span>
                    </div>
                    <div className="flex w-6 items-center justify-end shrink-0" data-id="element-11504" data-mp="1hojilfhvbmeq">
                      <ArrowRight size={16} className="text-neutral-300 group-hover:text-[#EA580C] group-hover:translate-x-0.5 transition-all" data-id="element-11505" data-mp="1mne3cmzebl1h" />
                    </div>
                  </button>)}
              </div>

              <div className="mt-3 flex items-center justify-center gap-2 text-[13px] text-neutral-400" data-id="element-11506" data-mp="3gz2iap5mnnlv">
                {!showAllWorkspaces && <>
                    <button onClick={() => setShowAllWorkspaces(true)} className="transition-colors hover:text-[#2B211A]" data-id="element-11507" data-mp="10e7aus7thmu3">
                      Use another workspace
                    </button>
                    <span aria-hidden="true" data-id="element-11508" data-mp="1ac3i38xmvxmx">·</span>
                  </>}
                <button onClick={() => setSignedOut(true)} className="transition-colors hover:text-[#2B211A]" data-id="element-11509" data-mp="1aa0jw1mbuy74">
                  Sign out
                </button>
              </div>
            </div>}

          {/* Returning users — quiet sign-in link, but ONLY when there are no
           remembered workspaces on this device (the Your Workspaces list IS
           the signed-in path, so showing both would be redundant). Hidden on
           /login, which already is the sign-in. */}
          {!isLogin && signedOut && <p className="mt-8 text-center text-sm text-neutral-500" data-id="element-11510" data-mp="3phsl0sa2s7l">
              Already have an account?{' '}
              <Link to="/app-screen-mockups/sign-in" className="font-bold text-[#2B211A] underline underline-offset-2 hover:text-[#EA580C] transition-colors" data-id="element-11511" data-mp="1yr32ld90b3k6">
              
                Sign in
              </Link>
            </p>}

          <p className="text-center text-[11px] text-neutral-400 mt-3 pt-0 mb-0 leading-relaxed max-w-[320px] mx-auto" data-id="element-11512" data-mp="27qgc25x4rs4w">
            By continuing, you agree to Giga's{' '}
            <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-11513" data-mp="2hq7a2hkwgp9q">
              
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-11514" data-mp="2mmflpkzw20c5">
              
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>;
}